// src/components/icons/VipIcon.js
import React from 'react';
import styled from 'styled-components';
import { SiInstagram, SiX, SiTiktok, SiYoutube, SiFacebook, SiSnapchat, SiTwitch, SiThreads, SiPinterest, SiSpotify, SiDiscord } from '@icons-pack/react-simple-icons';

// App Icons Collection
const AppIcons = {
  // Home - Crystalline house with depth
  Home: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <defs>
        <linearGradient id="homeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
        </linearGradient>
      </defs>
      <path d="M3 12l9-9 9 9" />
      <path d="M5 10v10a1 1 0 0 0 1 1h3v-6a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v6h3a1 1 0 0 0 1-1V10" />
      <polygon points="12,3 19,10 19,21 5,21 5,10" fill="url(#homeGrad)" opacity="0.1" />
      <circle cx="12" cy="16" r="1" fill="currentColor" opacity="0.6" />
      <path d="M8 8l8 0" strokeWidth="1" opacity="0.3" />
    </svg>
  ),

// Compass/Discover Icon - Navigation for discovery
CompassDiscoverIcon : ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <defs>
      <linearGradient id="compassGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
      </linearGradient>
    </defs>
    
    {/* Compass circle */}
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="10" fill="url(#compassGrad)" opacity="0.1" />
    
    {/* Compass needle */}
    <path d="M16 8l-2 6-6 2 2-6z" />
    <path d="M16 8l-2 6-6 2 2-6z" fill="currentColor" opacity="0.2" />
    
    {/* Direction markers */}
    <line x1="12" y1="2" x2="12" y2="4" strokeWidth="1" opacity="0.8" />
    <line x1="12" y1="20" x2="12" y2="22" strokeWidth="1" opacity="0.8" />
    <line x1="2" y1="12" x2="4" y2="12" strokeWidth="1" opacity="0.8" />
    <line x1="20" y1="12" x2="22" y2="12" strokeWidth="1" opacity="0.8" />
    
    {/* Discovery waves */}
    <circle cx="12" cy="12" r="6" stroke="currentColor" strokeWidth="0.5" opacity="0.3">
      <animate attributeName="r" values="6;8;6" dur="3s" repeatCount="indefinite" />
      <animate attributeName="opacity" values="0.3;0.1;0.3" dur="3s" repeatCount="indefinite" />
    </circle>
    
    {/* Cardinal point dots */}
    <circle cx="12" cy="2" r="0.5" fill="currentColor" opacity="0.8" />
    <circle cx="22" cy="12" r="0.5" fill="currentColor" opacity="0.8" />
    <circle cx="12" cy="22" r="0.5" fill="currentColor" opacity="0.8" />
    <circle cx="2" cy="12" r="0.5" fill="currentColor" opacity="0.8" />
    
    {/* Exploration sparkles */}
    <circle cx="8" cy="8" r="0.3" fill="currentColor" opacity="0.6" />
    <circle cx="16" cy="8" r="0.3" fill="currentColor" opacity="0.6" />
    <circle cx="8" cy="16" r="0.3" fill="currentColor" opacity="0.6" />
    <circle cx="16" cy="16" r="0.3" fill="currentColor" opacity="0.6" />
  </svg>
),

  // Notifications - Dynamic bell with energy waves
  Notifications: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      <circle cx="18" cy="6" r="3" fill="currentColor" opacity="0.8" />
      <circle cx="18" cy="6" r="1.5" fill="white" />
      <path d="M21 3c-1 1-1 3-1 3" strokeWidth="1" opacity="0.4" />
      <path d="M15 3c1 1 1 3 1 3" strokeWidth="1" opacity="0.4" />
      <circle cx="8" cy="12" r="0.5" fill="currentColor" opacity="0.5" />
      <circle cx="16" cy="12" r="0.5" fill="currentColor" opacity="0.5" />
    </svg>
  ),

  // Dashboard - Geometric grid with depth
  Dashboard: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <rect x="3" y="3" width="7" height="7" fill="currentColor" opacity="0.2" />
      <rect x="14" y="3" width="7" height="7" fill="currentColor" opacity="0.15" />
      <rect x="3" y="14" width="7" height="7" fill="currentColor" opacity="0.15" />
      <rect x="14" y="14" width="7" height="7" fill="currentColor" opacity="0.1" />
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
      <circle cx="6.5" cy="6.5" r="1" fill="currentColor" opacity="0.6" />
      <circle cx="17.5" cy="6.5" r="1" fill="currentColor" opacity="0.6" />
      <path d="M5 17h4" strokeWidth="1" opacity="0.5" />
      <path d="M16 17h3" strokeWidth="1" opacity="0.5" />
    </svg>
  ),

  // Messages - Neural chat bubble
  Messages: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" fill="currentColor" opacity="0.1" />
      <circle cx="8" cy="10" r="1" fill="currentColor" opacity="0.8" />
      <circle cx="12" cy="10" r="1" fill="currentColor" opacity="0.6" />
      <circle cx="16" cy="10" r="1" fill="currentColor" opacity="0.8" />
      <path d="M8 10l4 3 4-3" strokeWidth="0.5" opacity="0.3" />
      <path d="M7 14h10" strokeWidth="1" opacity="0.4" />
    </svg>
  ),

  Mail: ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    {...props}
  >
    {/* Background glow */}
    <rect x="3" y="5" width="18" height="14" rx="2" opacity="0.15"/>
    
    {/* Mail envelope body */}
    <path fillRule="evenodd" clipRule="evenodd" d="M3.879 6.879A3 3 0 0 1 6 6h12a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V9a3 3 0 0 1 .879-2.121zM6 8a1 1 0 0 0-1 1v.221l7 4.667 7-4.667V9a1 1 0 0 0-1-1H6zm13 3.446l-6.445 4.297a1 1 0 0 1-1.11 0L5 11.446V15a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-3.554z"/>
    
    {/* Energy particles around envelope */}
    <circle cx="5" cy="8" r="0.8" opacity="0.6"/>
    <circle cx="7" cy="6" r="1" opacity="0.5"/>
    <circle cx="12" cy="5" r="0.7" opacity="0.4"/>
    <circle cx="17" cy="6" r="1" opacity="0.5"/>
    <circle cx="19" cy="8" r="0.8" opacity="0.6"/>
    <circle cx="19" cy="16" r="0.8" opacity="0.5"/>
    <circle cx="17" cy="18" r="0.7" opacity="0.4"/>
    <circle cx="7" cy="18" r="0.7" opacity="0.4"/>
    <circle cx="5" cy="16" r="0.8" opacity="0.5"/>
    
    {/* Flow lines */}
    <path d="M6 6.5l0.5-0.5" strokeWidth="0.5" stroke="currentColor" opacity="0.3" fill="none"/>
    <path d="M18 6.5l-0.5-0.5" strokeWidth="0.5" stroke="currentColor" opacity="0.3" fill="none"/>
    <path d="M6 17.5l0.5 0.5" strokeWidth="0.5" stroke="currentColor" opacity="0.3" fill="none"/>
    <path d="M18 17.5l-0.5 0.5" strokeWidth="0.5" stroke="currentColor" opacity="0.3" fill="none"/>
    
    {/* Sparkles for energy */}
    <path d="M9 4l0.2-0.6 0.2 0.6 0.6 0.2-0.6 0.2-0.2 0.6-0.2-0.6-0.6-0.2z" opacity="0.4"/>
    <path d="M15 4l0.2-0.6 0.2 0.6 0.6 0.2-0.6 0.2-0.2 0.6-0.2-0.6-0.6-0.2z" opacity="0.4"/>
    <path d="M4 12l0.3-0.8 0.3 0.8 0.8 0.3-0.8 0.3-0.3 0.8-0.3-0.8-0.8-0.3z" opacity="0.35"/>
    <path d="M20 12l-0.3-0.8-0.3 0.8-0.8 0.3 0.8 0.3 0.3 0.8 0.3-0.8 0.8-0.3z" opacity="0.35"/>
  </svg>
),

  // Bookmarks - Crystalline bookmark
  Bookmarks: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" fill="currentColor" opacity="0.15" />
      <path d="M12 2l3 3-3 3-3-3z" fill="currentColor" opacity="0.3" />
      <circle cx="12" cy="5" r="1" fill="currentColor" opacity="0.8" />
      <path d="M8 10h8" strokeWidth="1" opacity="0.4" />
      <path d="M8 13h6" strokeWidth="1" opacity="0.3" />
      <circle cx="9" cy="16" r="0.5" fill="currentColor" opacity="0.5" />
      <circle cx="15" cy="16" r="0.5" fill="currentColor" opacity="0.5" />
    </svg>
  ),

  // Lists - Organized data flows
  Lists: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <circle cx="4" cy="6" r="2" fill="currentColor" opacity="0.6" />
      <circle cx="4" cy="12" r="2" fill="currentColor" opacity="0.4" />
      <circle cx="4" cy="18" r="2" fill="currentColor" opacity="0.6" />
      <path d="M8 6h13" />
      <path d="M8 12h13" />
      <path d="M8 18h13" />
      <circle cx="4" cy="6" r="0.5" fill="white" />
      <circle cx="4" cy="12" r="0.5" fill="white" />
      <circle cx="4" cy="18" r="0.5" fill="white" />
      <path d="M10 6l3 0" strokeWidth="2" opacity="0.8" />
      <path d="M10 12l5 0" strokeWidth="2" opacity="0.6" />
      <path d="M10 18l4 0" strokeWidth="2" opacity="0.8" />
    </svg>
  ),

  // Gallery - Upload Library / media grid
  Gallery: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <rect x="3" y="3" width="8" height="8" rx="1.5" />
      <rect x="13" y="3" width="8" height="8" rx="1.5" />
      <rect x="3" y="13" width="8" height="8" rx="1.5" />
      <rect x="13" y="13" width="8" height="8" rx="1.5" />
      <circle cx="7" cy="7" r="1.2" fill="currentColor" opacity="0.5" />
      <path d="M13 19l2.5-3 2 2.5 1.5-1.5 2 2" strokeWidth="1.2" />
    </svg>
  ),
  // PostCategories - labeled folder for organizing posts
  PostCategories: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <path d="M3 7.5A2.5 2.5 0 0 1 5.5 5h3.6a2 2 0 0 1 1.5.7l1 1.1a2 2 0 0 0 1.5.7h5.4A2.5 2.5 0 0 1 21 10v7.5a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 3 17.5z" />
      <path d="M8 13h8" opacity="0.7" />
      <path d="M8 16.5h5" opacity="0.7" />
    </svg>
  ),
  Circles: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <circle cx="12" cy="12" r="9" opacity="0.3" />
      <circle cx="12" cy="12" r="5" opacity="0.5" />
      <circle cx="12" cy="8" r="2" fill="currentColor" opacity="0.7" />
      <circle cx="8" cy="14" r="2" fill="currentColor" opacity="0.5" />
      <circle cx="16" cy="14" r="2" fill="currentColor" opacity="0.7" />
      <path d="M12 10v2" opacity="0.4" />
      <path d="M10 13l-1 1" opacity="0.4" />
      <path d="M14 13l1 1" opacity="0.4" />
    </svg>
  ),

  // Settings - Parametric gear system
  Settings: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <circle cx="12" cy="12" r="3" />
      <circle cx="12" cy="12" r="6" fill="currentColor" opacity="0.05" />
      <path d="M12 1v6m0 6v6" opacity="0.4" />
      <path d="M21 12h-6m-6 0H3" opacity="0.4" />
      <path d="M18.36 6.64l-4.24 4.24m-4.24 4.24L5.64 18.36" opacity="0.3" />
      <path d="M18.36 17.36l-4.24-4.24m-4.24-4.24L5.64 5.64" opacity="0.3" />
      <circle cx="12" cy="4" r="1" fill="currentColor" opacity="0.6" />
      <circle cx="12" cy="20" r="1" fill="currentColor" opacity="0.6" />
      <circle cx="4" cy="12" r="1" fill="currentColor" opacity="0.6" />
      <circle cx="20" cy="12" r="1" fill="currentColor" opacity="0.6" />
      <circle cx="12" cy="12" r="1" fill="currentColor" />
    </svg>
  ),

  // Profile - Geometric avatar
  Profile: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.1" />
      <circle cx="12" cy="8" r="3" />
      <path d="M6 20c0-4 3-6 6-6s6 2 6 6" />
      <circle cx="12" cy="8" r="5" fill="currentColor" opacity="0.05" />
      <circle cx="10" cy="7" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="14" cy="7" r="0.5" fill="currentColor" opacity="0.8" />
      <path d="M10 9c1 1 3 1 4 0" strokeWidth="1" opacity="0.6" />
      <circle cx="8" cy="16" r="1" fill="currentColor" opacity="0.3" />
      <circle cx="16" cy="16" r="1" fill="currentColor" opacity="0.3" />
    </svg>
  ),

  // Logout - Exit portal
  Logout: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16,17 21,12 16,7" />
      <line x1="21" y1="12" x2="9" y2="12" />
      <rect x="3" y="3" width="6" height="18" fill="currentColor" opacity="0.1" />
      <circle cx="6" cy="12" r="1" fill="currentColor" opacity="0.6" />
      <path d="M12 12h9" strokeWidth="2" />
      <circle cx="19" cy="12" r="1" fill="currentColor" />
      <path d="M15 8l4 4-4 4" strokeWidth="2" />
    </svg>
  ),

  // Like - Energetic heart
  Like: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" fill="currentColor" opacity="0.2" />
      <circle cx="12" cy="12" r="2" fill="currentColor" opacity="0.6" />
      <path d="M8 8c2 0 4 2 4 4" strokeWidth="1" opacity="0.4" />
      <path d="M16 8c-2 0-4 2-4 4" strokeWidth="1" opacity="0.4" />
      <circle cx="9" cy="10" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="15" cy="10" r="0.5" fill="currentColor" opacity="0.8" />
    </svg>
  ),

  // Comments - Connected conversation
  Comments: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" fill="currentColor" opacity="0.1" />
      <path d="M8 10l8 0" strokeWidth="1.5" />
      <path d="M8 14l4 0" strokeWidth="1.5" />
      <circle cx="16" cy="14" r="1" fill="currentColor" opacity="0.6" />
      <path d="M14 8l4 4" strokeWidth="0.5" opacity="0.3" />
      <path d="M18 8l-4 4" strokeWidth="0.5" opacity="0.3" />
    </svg>
  ),

  // Share - Network connection
  Share: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
      <circle cx="18" cy="5" r="5" fill="currentColor" opacity="0.05" />
      <circle cx="6" cy="12" r="5" fill="currentColor" opacity="0.05" />
      <circle cx="18" cy="19" r="5" fill="currentColor" opacity="0.05" />
      <circle cx="18" cy="5" r="1" fill="currentColor" />
      <circle cx="6" cy="12" r="1" fill="currentColor" />
      <circle cx="18" cy="19" r="1" fill="currentColor" />
      <path d="M12 2l6 10-6 10L6 12z" fill="currentColor" opacity="0.1" />
    </svg>
  ),

  Reshare: ({ size = 24, className = "", ...props }) => (
  <svg width={size} height={size} viewBox="-2 -2 28 28" fill="currentColor" className={className} {...props}>
    <circle cx="12" cy="12" r="9" opacity="0.1"/>
    <circle cx="12" cy="12" r="2.5"/>
    <circle cx="12" cy="12" r="5" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.6"/>
    <circle cx="12" cy="12" r="7.5" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.4"/>
    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.2"/>
  </svg>
),

  // Menu - Fluid hamburger
  Menu: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
      <circle cx="5" cy="6" r="1" fill="currentColor" opacity="0.8" />
      <circle cx="5" cy="12" r="1" fill="currentColor" opacity="0.6" />
      <circle cx="5" cy="18" r="1" fill="currentColor" opacity="0.8" />
      <circle cx="19" cy="6" r="1" fill="currentColor" opacity="0.6" />
      <circle cx="19" cy="12" r="1" fill="currentColor" opacity="0.8" />
      <circle cx="19" cy="18" r="1" fill="currentColor" opacity="0.6" />
      <path d="M7 6h10" strokeWidth="2" />
      <path d="M7 12h10" strokeWidth="2" />
      <path d="M7 18h10" strokeWidth="2" />
    </svg>
  ),

  // Post - Content creation
  Post: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <rect x="3" y="3" width="18" height="18" rx="2" fill="currentColor" opacity="0.1" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="M21 15l-5-5L5 21" />
      <circle cx="8.5" cy="8.5" r="3" fill="currentColor" opacity="0.1" />
      <path d="M7 17h10" strokeWidth="1.5" />
      <path d="M7 20h6" strokeWidth="1" opacity="0.6" />
      <circle cx="17" cy="7" r="1" fill="currentColor" opacity="0.6" />
      <path d="M12 3v6" strokeWidth="1" opacity="0.3" />
      <path d="M21 12h-6" strokeWidth="1" opacity="0.3" />
    </svg>
  ),

  // Copy/Duplicate - Two overlapping documents with depth
  Copy: ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <defs>
      <linearGradient id="copyGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
      </linearGradient>
    </defs>
    
    {/* Back layer */}
    <rect x="8" y="8" width="13" height="13" rx="2" />
    <rect x="8" y="8" width="13" height="13" rx="2" fill="url(#copyGrad)" opacity="0.1" />
    
    {/* Front layer */}
    <rect x="3" y="3" width="13" height="13" rx="2" />
    <rect x="3" y="3" width="13" height="13" rx="2" fill="currentColor" opacity="0.05" />
    
    {/* Content lines on front */}
    <line x1="6" y1="8" x2="13" y2="8" strokeWidth="1" opacity="0.6" />
    <line x1="6" y1="11" x2="11" y2="11" strokeWidth="1" opacity="0.5" />
    
    {/* Duplication energy */}
    <path d="M16 3h2" strokeWidth="0.8" opacity="0.6" />
    <path d="M21 8v2" strokeWidth="0.8" opacity="0.6" />
    <path d="M16 16l2 2" strokeWidth="0.6" opacity="0.5" />
    
    {/* Corner accents */}
    <circle cx="3" cy="3" r="0.5" fill="currentColor" opacity="0.8" />
    <circle cx="16" cy="3" r="0.5" fill="currentColor" opacity="0.8" />
    <circle cx="8" cy="8" r="0.5" fill="currentColor" opacity="0.7" />
    <circle cx="21" cy="8" r="0.5" fill="currentColor" opacity="0.7" />
    <circle cx="21" cy="21" r="0.5" fill="currentColor" opacity="0.6" />
    
    {/* Layer separation dots */}
    <circle cx="12" cy="12" r="0.3" fill="currentColor" opacity="0.5" />
    <circle cx="14" cy="14" r="0.3" fill="currentColor" opacity="0.5" />
    <circle cx="10" cy="10" r="0.3" fill="currentColor" opacity="0.5" />
  </svg>
),

  // Trash/Delete - Modern trash bin with depth
  Trash: ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <defs>
      <linearGradient id="trashGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
      </linearGradient>
    </defs>
    
    {/* Trash can body */}
    <path d="M3 6h18v14c0 1-1 2-2 2H5c-1 0-2-1-2-2V6z" />
    <path d="M3 6h18v14c0 1-1 2-2 2H5c-1 0-2-1-2-2V6z" fill="url(#trashGrad)" opacity="0.1" />
    
    {/* Lid */}
    <path d="M2 6h20" strokeWidth="2" />
    
    {/* Handle */}
    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
    
    {/* Delete lines inside */}
    <line x1="10" y1="11" x2="10" y2="17" strokeWidth="1" opacity="0.6" />
    <line x1="14" y1="11" x2="14" y2="17" strokeWidth="1" opacity="0.6" />
    
    {/* Energy dissipation */}
    <path d="M7 2l-1-1" strokeWidth="0.5" opacity="0.5" />
    <path d="M17 2l1-1" strokeWidth="0.5" opacity="0.5" />
    <path d="M3 10l-1 0" strokeWidth="0.5" opacity="0.4" />
    <path d="M21 10l1 0" strokeWidth="0.5" opacity="0.4" />
    
    {/* Top accent dots */}
    <circle cx="6" cy="6" r="0.3" fill="currentColor" opacity="0.6" />
    <circle cx="12" cy="6" r="0.3" fill="currentColor" opacity="0.6" />
    <circle cx="18" cy="6" r="0.3" fill="currentColor" opacity="0.6" />
    
    {/* Handle detail */}
    <circle cx="10" cy="3" r="0.3" fill="currentColor" opacity="0.7" />
    <circle cx="14" cy="3" r="0.3" fill="currentColor" opacity="0.7" />
    
    {/* Bottom energy */}
    <circle cx="12" cy="19" r="0.4" fill="currentColor" opacity="0.5" />
  </svg>
),

  // Plus - Quantum add with energy field
  Plus: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <circle cx="12" cy="12" r="10" opacity="0.1" fill="currentColor" />
      <circle cx="12" cy="12" r="6" opacity="0.05" fill="currentColor" />
      <circle cx="12" cy="12" r="3" opacity="0.08" fill="currentColor" />
      <path d="M12 8v8" strokeWidth="2.5" />
      <path d="M8 12h8" strokeWidth="2.5" />
      <circle cx="12" cy="8" r="1" fill="currentColor" opacity="0.8" />
      <circle cx="12" cy="16" r="1" fill="currentColor" opacity="0.8" />
      <circle cx="8" cy="12" r="1" fill="currentColor" opacity="0.8" />
      <circle cx="16" cy="12" r="1" fill="currentColor" opacity="0.8" />
      <path d="M10 10l4 4" strokeWidth="0.5" opacity="0.3" />
      <path d="M14 10l-4 4" strokeWidth="0.5" opacity="0.3" />
      <circle cx="12" cy="12" r="0.5" fill="currentColor" />
    </svg>
  ),

  // Vibe - concentric energy rings around a soundwave/pulse core (Stories-style ephemeral energy)
  Vibe: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      {/* Outer energy ring — the "story ring" */}
      <circle cx="12" cy="12" r="10" opacity="0.35" strokeDasharray="1.5 2" />
      {/* Middle ring */}
      <circle cx="12" cy="12" r="7" opacity="0.6" />
      {/* Inner solid core */}
      <circle cx="12" cy="12" r="3.25" fill="currentColor" stroke="none" />
      {/* Pulse bars radiating from the core (waveform / vibe) */}
      <path d="M6.5 12v-1.5" opacity="0.85" strokeWidth="1.75" />
      <path d="M17.5 12v1.5" opacity="0.85" strokeWidth="1.75" />
      <path d="M9 12v-2.5" opacity="0.9" strokeWidth="1.75" />
      <path d="M15 12v2.5" opacity="0.9" strokeWidth="1.75" />
      {/* Highlight sparkle */}
      <circle cx="15.5" cy="8.5" r="0.9" fill="currentColor" opacity="0.9" stroke="none" />
      <circle cx="8" cy="16" r="0.6" fill="currentColor" opacity="0.6" stroke="none" />
    </svg>
  ),

  // Minus - Dynamic minus with energy field (based on Plus pattern)
Minus: ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <circle cx="12" cy="12" r="10" opacity="0.1" fill="currentColor" />
    <circle cx="12" cy="12" r="6" opacity="0.05" fill="currentColor" />
    <circle cx="12" cy="12" r="3" opacity="0.08" fill="currentColor" />
    <path d="M8 12h8" strokeWidth="2.5" />
    <circle cx="8" cy="12" r="1" fill="currentColor" opacity="0.8" />
    <circle cx="16" cy="12" r="1" fill="currentColor" opacity="0.8" />
    <path d="M10 10l4 4" strokeWidth="0.5" opacity="0.3" />
    <path d="M14 10l-4 4" strokeWidth="0.5" opacity="0.3" />
    <circle cx="12" cy="12" r="0.5" fill="currentColor" />
    <path d="M6 12c1-1 2-1 4 0" strokeWidth="1" opacity="0.4" />
    <path d="M14 12c1-1 2-1 4 0" strokeWidth="1" opacity="0.4" />
    <circle cx="4" cy="12" r="0.5" fill="currentColor" opacity="0.5" />
    <circle cx="20" cy="12" r="0.5" fill="currentColor" opacity="0.5" />
    <path d="M12 8v1" strokeWidth="0.5" opacity="0.3" />
    <path d="M12 15v1" strokeWidth="0.5" opacity="0.3" />
  </svg>
),

  // ChevronLeft - Dynamic left arrow with flow
  ChevronLeft: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <path d="M15 18l-6-6 6-6" strokeWidth="2.5" />
      <circle cx="15" cy="6" r="1" fill="currentColor" opacity="0.8" />
      <circle cx="15" cy="18" r="1" fill="currentColor" opacity="0.8" />
      <circle cx="9" cy="12" r="1.5" fill="currentColor" opacity="0.6" />
      <path d="M20 12c-2-1-4-1-6 0" strokeWidth="1" opacity="0.4" />
      <path d="M18 8c-1 1-2 3-3 4" strokeWidth="0.5" opacity="0.3" />
      <path d="M18 16c-1-1-2-3-3-4" strokeWidth="0.5" opacity="0.3" />
      <circle cx="21" cy="12" r="0.5" fill="currentColor" opacity="0.5" />
      <circle cx="6" cy="12" r="2" fill="currentColor" opacity="0.1" />
      <path d="M4 12h2" strokeWidth="1" opacity="0.6" />
    </svg>
  ),

  // ChevronRight - Dynamic right arrow with flow
  ChevronRight: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <path d="M9 18l6-6-6-6" strokeWidth="2.5" />
      <circle cx="9" cy="6" r="1" fill="currentColor" opacity="0.8" />
      <circle cx="9" cy="18" r="1" fill="currentColor" opacity="0.8" />
      <circle cx="15" cy="12" r="1.5" fill="currentColor" opacity="0.6" />
      <path d="M4 12c2-1 4-1 6 0" strokeWidth="1" opacity="0.4" />
      <path d="M6 8c1 1 2 3 3 4" strokeWidth="0.5" opacity="0.3" />
      <path d="M6 16c1-1 2-3 3-4" strokeWidth="0.5" opacity="0.3" />
      <circle cx="3" cy="12" r="0.5" fill="currentColor" opacity="0.5" />
      <circle cx="18" cy="12" r="2" fill="currentColor" opacity="0.1" />
      <path d="M18 12h2" strokeWidth="1" opacity="0.6" />
    </svg>
  ),

  // ChevronUp - Dynamic up arrow with flow
ChevronUp: ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <path d="M18 15l-6-6-6 6" strokeWidth="2.5" />
    <circle cx="18" cy="15" r="1" fill="currentColor" opacity="0.8" />
    <circle cx="6" cy="15" r="1" fill="currentColor" opacity="0.8" />
    <circle cx="12" cy="9" r="1.5" fill="currentColor" opacity="0.6" />
    <path d="M12 20c-1-2-1-4 0-6" strokeWidth="1" opacity="0.4" />
    <path d="M8 18c1-1 3-2 4-3" strokeWidth="0.5" opacity="0.3" />
    <path d="M16 18c-1-1-3-2-4-3" strokeWidth="0.5" opacity="0.3" />
    <circle cx="12" cy="21" r="0.5" fill="currentColor" opacity="0.5" />
    <circle cx="12" cy="6" r="2" fill="currentColor" opacity="0.1" />
    <path d="M12 6v-2" strokeWidth="1" opacity="0.6" />
  </svg>
),

Calendar: ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    {...props}
  >
    {/* Background glow */}
    <rect x="3" y="4" width="18" height="18" rx="2" opacity="0.15"/>
    
    {/* Calendar body */}
    <path fillRule="evenodd" clipRule="evenodd" d="M7 2a1 1 0 0 1 1 1v1h8V3a1 1 0 1 1 2 0v1h1a3 3 0 0 1 3 3v12a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3h1V3a1 1 0 0 1 1-1zM5 6a1 1 0 0 0-1 1v2h16V7a1 1 0 0 0-1-1H5zm15 5H4v8a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-8z"/>
    
    {/* Date markers */}
    <circle cx="8" cy="14" r="1"/>
    <circle cx="12" cy="14" r="1"/>
    <circle cx="16" cy="14" r="1"/>
    <circle cx="8" cy="17.5" r="1"/>
    <circle cx="12" cy="17.5" r="1"/>
    
    {/* Energy particles around calendar */}
    <circle cx="5" cy="3" r="0.8" opacity="0.6"/>
    <circle cx="10" cy="2" r="0.7" opacity="0.5"/>
    <circle cx="14" cy="2" r="0.7" opacity="0.5"/>
    <circle cx="19" cy="3" r="0.8" opacity="0.6"/>
    <circle cx="21" cy="8" r="0.8" opacity="0.5"/>
    <circle cx="21" cy="16" r="0.8" opacity="0.5"/>
    <circle cx="19" cy="21" r="0.8" opacity="0.6"/>
    <circle cx="12" cy="22" r="0.7" opacity="0.5"/>
    <circle cx="5" cy="21" r="0.8" opacity="0.6"/>
    <circle cx="3" cy="16" r="0.8" opacity="0.5"/>
    <circle cx="3" cy="8" r="0.8" opacity="0.5"/>
    
    {/* Flow lines */}
    <path d="M6 3l-0.5-0.5" strokeWidth="0.5" stroke="currentColor" opacity="0.3" fill="none"/>
    <path d="M18 3l0.5-0.5" strokeWidth="0.5" stroke="currentColor" opacity="0.3" fill="none"/>
    <path d="M20 10l0.8-0.5" strokeWidth="0.5" stroke="currentColor" opacity="0.3" fill="none"/>
    <path d="M20 14l0.8 0.5" strokeWidth="0.5" stroke="currentColor" opacity="0.3" fill="none"/>
    
    {/* Sparkles */}
    <path d="M22 12l0.3-0.8 0.3 0.8 0.8 0.3-0.8 0.3-0.3 0.8-0.3-0.8-0.8-0.3z" opacity="0.4"/>
    <path d="M2 12l0.3-0.8 0.3 0.8 0.8 0.3-0.8 0.3-0.3 0.8-0.3-0.8-0.8-0.3z" opacity="0.4"/>
    <path d="M12 23l0.2-0.6 0.2 0.6 0.6 0.2-0.6 0.2-0.2 0.6-0.2-0.6-0.6-0.2z" opacity="0.35"/>
  </svg>
),

Download: ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    {...props}
  >
    {/* Background glow */}
    <rect x="4" y="2" width="16" height="20" rx="1" opacity="0.15"/>
    
    {/* Download arrow */}
    <path d="M12 3a1 1 0 0 1 1 1v9.586l2.293-2.293a1 1 0 0 1 1.414 1.414l-4 4a1 1 0 0 1-1.414 0l-4-4a1 1 0 1 1 1.414-1.414L11 13.586V4a1 1 0 0 1 1-1z"/>
    
    {/* Download tray/base */}
    <path d="M4 17a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v3a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-3z"/>
    
    {/* Energy particles flowing down */}
    <circle cx="12" cy="2" r="1" opacity="0.7"/>
    <circle cx="10" cy="5" r="0.8" opacity="0.6"/>
    <circle cx="14" cy="5" r="0.8" opacity="0.6"/>
    <circle cx="9" cy="8" r="0.7" opacity="0.5"/>
    <circle cx="15" cy="8" r="0.7" opacity="0.5"/>
    <circle cx="8" cy="11" r="0.8" opacity="0.6"/>
    <circle cx="16" cy="11" r="0.8" opacity="0.6"/>
    <circle cx="7" cy="14" r="0.7" opacity="0.5"/>
    <circle cx="17" cy="14" r="0.7" opacity="0.5"/>
    
    {/* Flow lines showing downward motion */}
    <path d="M10 4l-0.5 1" strokeWidth="0.5" stroke="currentColor" opacity="0.3" fill="none"/>
    <path d="M14 4l0.5 1" strokeWidth="0.5" stroke="currentColor" opacity="0.3" fill="none"/>
    <path d="M9 7l-0.5 1" strokeWidth="0.5" stroke="currentColor" opacity="0.3" fill="none"/>
    <path d="M15 7l0.5 1" strokeWidth="0.5" stroke="currentColor" opacity="0.3" fill="none"/>
    <path d="M8 10l-0.5 1" strokeWidth="0.5" stroke="currentColor" opacity="0.3" fill="none"/>
    <path d="M16 10l0.5 1" strokeWidth="0.5" stroke="currentColor" opacity="0.3" fill="none"/>
    
    {/* Sparkles */}
    <path d="M8 3l0.2-0.6 0.2 0.6 0.6 0.2-0.6 0.2-0.2 0.6-0.2-0.6-0.6-0.2z" opacity="0.4"/>
    <path d="M16 3l0.2-0.6 0.2 0.6 0.6 0.2-0.6 0.2-0.2 0.6-0.2-0.6-0.6-0.2z" opacity="0.4"/>
    <path d="M6 12l0.3-0.8 0.3 0.8 0.8 0.3-0.8 0.3-0.3 0.8-0.3-0.8-0.8-0.3z" opacity="0.35"/>
    <path d="M18 12l0.3-0.8 0.3 0.8 0.8 0.3-0.8 0.3-0.3 0.8-0.3-0.8-0.8-0.3z" opacity="0.35"/>
    <path d="M4 19l0.2-0.5 0.2 0.5 0.5 0.2-0.5 0.2-0.2 0.5-0.2-0.5-0.5-0.2z" opacity="0.3"/>
    <path d="M20 19l0.2-0.5 0.2 0.5 0.5 0.2-0.5 0.2-0.2 0.5-0.2-0.5-0.5-0.2z" opacity="0.3"/>
  </svg>
),

// ChevronDown - Dynamic down arrow with flow
ChevronDown: ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <path d="M6 9l6 6 6-6" strokeWidth="2.5" />
    <circle cx="6" cy="9" r="1" fill="currentColor" opacity="0.8" />
    <circle cx="18" cy="9" r="1" fill="currentColor" opacity="0.8" />
    <circle cx="12" cy="15" r="1.5" fill="currentColor" opacity="0.6" />
    <path d="M12 4c1 2 1 4 0 6" strokeWidth="1" opacity="0.4" />
    <path d="M16 6c-1 1-3 2-4 3" strokeWidth="0.5" opacity="0.3" />
    <path d="M8 6c1 1 3 2 4 3" strokeWidth="0.5" opacity="0.3" />
    <circle cx="12" cy="3" r="0.5" fill="currentColor" opacity="0.5" />
    <circle cx="12" cy="18" r="2" fill="currentColor" opacity="0.1" />
    <path d="M12 18v2" strokeWidth="1" opacity="0.6" />
  </svg>
),

  // Search - Neural search with discovery patterns
  Search: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <circle cx="11" cy="11" r="8" opacity="0.1" fill="currentColor" />
      <circle cx="11" cy="11" r="5" opacity="0.05" fill="currentColor" />
      <circle cx="11" cy="11" r="6" strokeWidth="2" />
      <path d="M21 21l-4.35-4.35" strokeWidth="2.5" />
      <circle cx="9" cy="9" r="1" fill="currentColor" opacity="0.8" />
      <circle cx="13" cy="9" r="1" fill="currentColor" opacity="0.6" />
      <circle cx="11" cy="13" r="1" fill="currentColor" opacity="0.8" />
      <path d="M8 13c1.5-0.5 3-0.5 4.5 0" strokeWidth="1" opacity="0.5" />
      <path d="M7 7l8 8" strokeWidth="0.5" opacity="0.3" />
      <path d="M15 7l-8 8" strokeWidth="0.5" opacity="0.3" />
      <circle cx="19" cy="19" r="1" fill="currentColor" opacity="0.7" />
      <path d="M20 20l1 1" strokeWidth="1.5" opacity="0.6" />
    </svg>
  ),

  // DotsHorizontal - Modern 3-dot menu with energy field
  DotsHorizontal: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <circle cx="5" cy="12" r="2" fill="currentColor" opacity="0.8" />
      <circle cx="12" cy="12" r="2" fill="currentColor" opacity="0.9" />
      <circle cx="19" cy="12" r="2" fill="currentColor" opacity="0.8" />
      <circle cx="5" cy="12" r="4" fill="currentColor" opacity="0.1" />
      <circle cx="12" cy="12" r="4" fill="currentColor" opacity="0.15" />
      <circle cx="19" cy="12" r="4" fill="currentColor" opacity="0.1" />
      <circle cx="5" cy="12" r="0.5" fill="white" opacity="0.9" />
      <circle cx="12" cy="12" r="0.5" fill="white" opacity="0.9" />
      <circle cx="19" cy="12" r="0.5" fill="white" opacity="0.9" />
      <path d="M3 12h2M10 12h4M17 12h4" strokeWidth="0.5" opacity="0.3" />
      <circle cx="2" cy="12" r="0.5" fill="currentColor" opacity="0.4" />
      <circle cx="22" cy="12" r="0.5" fill="currentColor" opacity="0.4" />
    </svg>
  ),

  // DotsVertical - Modern vertical 3-dot menu with energy field
  DotsVertical: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <circle cx="12" cy="5" r="2" fill="currentColor" opacity="0.8" />
      <circle cx="12" cy="12" r="2" fill="currentColor" opacity="0.9" />
      <circle cx="12" cy="19" r="2" fill="currentColor" opacity="0.8" />
      <circle cx="12" cy="5" r="4" fill="currentColor" opacity="0.1" />
      <circle cx="12" cy="12" r="4" fill="currentColor" opacity="0.15" />
      <circle cx="12" cy="19" r="4" fill="currentColor" opacity="0.1" />
      <circle cx="12" cy="5" r="0.5" fill="white" opacity="0.9" />
      <circle cx="12" cy="12" r="0.5" fill="white" opacity="0.9" />
      <circle cx="12" cy="19" r="0.5" fill="white" opacity="0.9" />
      <path d="M12 3v2M12 10v4M12 17v4" strokeWidth="0.5" opacity="0.3" />
      <circle cx="12" cy="2" r="0.5" fill="currentColor" opacity="0.4" />
      <circle cx="12" cy="22" r="0.5" fill="currentColor" opacity="0.4" />
    </svg>
  ),

  // Wave - Message sending (sleek wave propagation)
  Wave: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <circle cx="12" cy="12" r="1.5" fill="currentColor" opacity="0.9" />
      <path d="M8 12c0-2.2 1.8-4 4-4s4 1.8 4 4" strokeWidth="1" opacity="0.6" />
      <path d="M6 12c0-3.3 2.7-6 6-6s6 2.7 6 6" strokeWidth="1" opacity="0.4" />
      <path d="M4 12c0-4.4 3.6-8 8-8s8 3.6 8 8" strokeWidth="1" opacity="0.2" />
      <circle cx="12" cy="12" r="0.5" fill="white" opacity="0.8" />
    </svg>
  ),

  // Sent - Message sent (minimalist check with glow)
  Sent: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <circle cx="12" cy="12" r="8" fill="currentColor" opacity="0.05" />
      <path d="M8 12.5l3 3 6-6" strokeWidth="2" />
      <circle cx="8" cy="12.5" r="0.5" fill="currentColor" opacity="0.6" />
      <circle cx="11" cy="15.5" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="17" cy="9.5" r="0.5" fill="currentColor" opacity="0.6" />
      <path d="M12 12c0 0 0 0 0 0" strokeWidth="0.5" />
    </svg>
  ),

  // Delivered - Message delivered (dual sleek checks)
  Delivered: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <circle cx="12" cy="12" r="8" fill="currentColor" opacity="0.08" />
      <path d="M6 12.5l3 3 6-6" strokeWidth="1.5" opacity="0.7" />
      <path d="M10 12.5l3 3 6-6" strokeWidth="2" />
      <circle cx="6" cy="12.5" r="0.4" fill="currentColor" opacity="0.5" />
      <circle cx="9" cy="15.5" r="0.4" fill="currentColor" opacity="0.6" />
      <circle cx="15" cy="9.5" r="0.4" fill="currentColor" opacity="0.5" />
      <circle cx="10" cy="12.5" r="0.5" fill="currentColor" opacity="0.7" />
      <circle cx="13" cy="15.5" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="19" cy="9.5" r="0.5" fill="currentColor" opacity="0.7" />
    </svg>
  ),

  // Viewed - Message read (sleek eye with modern gradient)
  Viewed: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <defs>
        <linearGradient id="viewedGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="currentColor" />
          <stop offset="100%" stopColor="currentColor" />
        </linearGradient>
      </defs>
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" stroke="url(#viewedGrad)" strokeWidth="1.5" />
      <circle cx="12" cy="12" r="3" stroke="url(#viewedGrad)" strokeWidth="1.5" />
      <circle cx="12" cy="12" r="1.5" fill="url(#viewedGrad)" />
      <circle cx="12" cy="12" r="6" fill="url(#viewedGrad)" opacity="0.1" />
      <path d="M8 8l8 8M16 8l-8 8" stroke="url(#viewedGrad)" strokeWidth="0.3" opacity="0.4" />
      <circle cx="12" cy="12" r="0.5" fill="white" opacity="0.9" />
    </svg>
  ),

  // Play - Dynamic play with energy field
  Play: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <defs>
        <linearGradient id="playGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
        </linearGradient>
      </defs>
      <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.1" />
      <circle cx="12" cy="12" r="7" fill="currentColor" opacity="0.05" />
      <polygon points="10,7 18,12 10,17" strokeWidth="2" />
      <polygon points="10,7 18,12 10,17" fill="url(#playGrad)" opacity="0.2" />
      <circle cx="10" cy="7" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="18" cy="12" r="0.8" fill="currentColor" opacity="0.9" />
      <circle cx="10" cy="17" r="0.5" fill="currentColor" opacity="0.8" />
      <path d="M14 12h4" strokeWidth="0.5" opacity="0.3" />
      <path d="M10 7v10" strokeWidth="0.5" opacity="0.3" />
      <circle cx="12" cy="12" r="1" fill="currentColor" opacity="0.6" />
      <path d="M15 9c1 1 1 4 0 6" strokeWidth="0.5" opacity="0.4" />
    </svg>
  ),

  // Pause - Balanced dual bars with energy
  Pause: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.1" />
      <circle cx="12" cy="12" r="7" fill="currentColor" opacity="0.05" />
      <rect x="9" y="7" width="2" height="10" rx="1" strokeWidth="2" />
      <rect x="14" y="7" width="2" height="10" rx="1" strokeWidth="2" />
      <rect x="9" y="7" width="2" height="10" rx="1" fill="currentColor" opacity="0.2" />
      <rect x="14" y="7" width="2" height="10" rx="1" fill="currentColor" opacity="0.2" />
      <circle cx="10" cy="7" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="10" cy="17" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="15" cy="7" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="15" cy="17" r="0.5" fill="currentColor" opacity="0.8" />
      <path d="M7 12h3" strokeWidth="0.5" opacity="0.3" />
      <path d="M15 12h3" strokeWidth="0.5" opacity="0.3" />
      <circle cx="12" cy="12" r="1" fill="currentColor" opacity="0.5" />
      <path d="M8 9c-1 1-1 4 0 6" strokeWidth="0.5" opacity="0.4" />
      <path d="M16 9c1 1 1 4 0 6" strokeWidth="0.5" opacity="0.4" />
    </svg>
  ),

  // FullScreen - Expanding corners with energy field
  FullScreen: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <rect x="3" y="3" width="18" height="18" rx="2" fill="currentColor" opacity="0.08" />
      <path d="M8 3v4H4" strokeWidth="2" />
      <path d="M16 3v4h4" strokeWidth="2" />
      <path d="M8 21v-4H4" strokeWidth="2" />
      <path d="M16 21v-4h4" strokeWidth="2" />
      <circle cx="4" cy="4" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="20" cy="4" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="4" cy="20" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="20" cy="20" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="12" cy="12" r="4" fill="currentColor" opacity="0.05" />
      <circle cx="12" cy="12" r="1" fill="currentColor" opacity="0.5" />
      <path d="M8 8l8 8" strokeWidth="0.5" opacity="0.3" />
      <path d="M16 8l-8 8" strokeWidth="0.5" opacity="0.3" />
      <path d="M3 8l6 0" strokeWidth="0.5" opacity="0.4" />
      <path d="M15 3l0 6" strokeWidth="0.5" opacity="0.4" />
      <path d="M21 16l-6 0" strokeWidth="0.5" opacity="0.4" />
      <path d="M9 21l0-6" strokeWidth="0.5" opacity="0.4" />
    </svg>
  ),
  
  // ExitFullScreen - Contracting corners with energy field
  ExitFullScreen: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <rect x="3" y="3" width="18" height="18" rx="2" fill="currentColor" opacity="0.08" />
      <path d="M4 8h4V4" strokeWidth="2" />
      <path d="M20 8h-4V4" strokeWidth="2" />
      <path d="M4 16h4v4" strokeWidth="2" />
      <path d="M20 16h-4v4" strokeWidth="2" />
      <circle cx="8" cy="8" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="16" cy="8" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="8" cy="16" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="16" cy="16" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="12" cy="12" r="4" fill="currentColor" opacity="0.05" />
      <circle cx="12" cy="12" r="1" fill="currentColor" opacity="0.5" />
      <path d="M8 8l8 8" strokeWidth="0.5" opacity="0.3" />
      <path d="M16 8l-8 8" strokeWidth="0.5" opacity="0.3" />
      <path d="M9 3l0 6" strokeWidth="0.5" opacity="0.4" />
      <path d="M3 15l6 0" strokeWidth="0.5" opacity="0.4" />
      <path d="M15 21l0-6" strokeWidth="0.5" opacity="0.4" />
      <path d="M21 9l-6 0" strokeWidth="0.5" opacity="0.4" />
    </svg>
  ),

  VolumeUp: ({ size = 24, className = "", ...props }) => (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        {...props}
      >
        <polygon points="11,5 6,9 2,9 2,15 6,15 11,19" />
        <polygon points="11,5 6,9 2,9 2,15 6,15 11,19" fill="currentColor" opacity="0.15" />
        <path d="M15 5c4 2 4 12 0 14" strokeWidth="1.5" />
        <path d="M19 2c5 3 5 17 0 20" strokeWidth="1.5" opacity="0.7" />
        <circle cx="6" cy="9" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="11" cy="5" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="11" cy="19" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="15" cy="12" r="1" fill="currentColor" opacity="0.6" />
        <circle cx="19" cy="12" r="1" fill="currentColor" opacity="0.4" />
        <path d="M8 12c0-1 1-2 3-3" strokeWidth="0.5" opacity="0.4" />
        <circle cx="11" cy="12" r="5" fill="currentColor" opacity="0.05" />
      </svg>
    ),
    
    VolumeMedium: ({ size = 24, className = "", ...props }) => (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        {...props}
      >
        <polygon points="11,5 6,9 2,9 2,15 6,15 11,19" />
        <polygon points="11,5 6,9 2,9 2,15 6,15 11,19" fill="currentColor" opacity="0.15" />
        <path d="M15 5c4 2 4 12 0 14" strokeWidth="1.5" />
        <circle cx="6" cy="9" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="11" cy="5" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="11" cy="19" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="15" cy="12" r="1" fill="currentColor" opacity="0.6" />
        <path d="M8 12c0-1 1-2 3-3" strokeWidth="0.5" opacity="0.4" />
        <circle cx="11" cy="12" r="5" fill="currentColor" opacity="0.05" />
      </svg>
    ),
    
    VolumeLow: ({ size = 24, className = "", ...props }) => (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        {...props}
      >
        <polygon points="11,5 6,9 2,9 2,15 6,15 11,19" />
        <polygon points="11,5 6,9 2,9 2,15 6,15 11,19" fill="currentColor" opacity="0.15" />
        <path d="M15 8c2 2 2 6 0 8" strokeWidth="1.5" />
        <circle cx="6" cy="9" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="11" cy="5" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="11" cy="19" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="15" cy="12" r="1" fill="currentColor" opacity="0.6" />
        <path d="M8 12c0-1 1-2 3-3" strokeWidth="0.5" opacity="0.4" />
        <circle cx="11" cy="12" r="5" fill="currentColor" opacity="0.05" />
      </svg>
    ),
    
    VolumeMute: ({ size = 24, className = "", ...props }) => (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        {...props}
      >
        <polygon points="11,5 6,9 2,9 2,15 6,15 11,19" />
        <polygon points="11,5 6,9 2,9 2,15 6,15 11,19" fill="currentColor" opacity="0.15" />
        <line x1="23" y1="9" x2="17" y2="15" strokeWidth="2" />
        <line x1="17" y1="9" x2="23" y2="15" strokeWidth="2" />
        <circle cx="20" cy="12" r="3" fill="currentColor" opacity="0.1" />
        <circle cx="6" cy="9" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="11" cy="5" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="11" cy="19" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="17" cy="9" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="23" cy="9" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="17" cy="15" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="23" cy="15" r="0.5" fill="currentColor" opacity="0.8" />
        <path d="M8 12c0-1 1-2 3-3" strokeWidth="0.5" opacity="0.4" />
        <circle cx="11" cy="12" r="5" fill="currentColor" opacity="0.05" />
      </svg>
    ),

    // Paperclip - Modern attachment icon with dimension
  Paperclip: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" fill="currentColor" opacity="0.1" />
      <circle cx="12" cy="12" r="5" fill="currentColor" opacity="0.05" />
      <circle cx="17.3" cy="6.7" r="0.6" fill="currentColor" opacity="0.8" />
      <circle cx="6.7" cy="17.3" r="0.6" fill="currentColor" opacity="0.8" />
      <circle cx="14.5" cy="9.5" r="0.5" fill="currentColor" opacity="0.6" />
      <circle cx="9.5" cy="14.5" r="0.5" fill="currentColor" opacity="0.6" />
      <path d="M12 12l3.5-3.5" strokeWidth="0.5" opacity="0.3" />
      <path d="M8.5 8.5l3 3" strokeWidth="0.5" opacity="0.3" />
      <path d="M12.5 15.5l3 3" strokeWidth="0.5" opacity="0.3" />
    </svg>
  ),
  
  // Bold - Dimensional text formatting
  Bold: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <defs>
        <linearGradient id="boldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
        </linearGradient>
      </defs>
      <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
      <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
      <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" fill="url(#boldGrad)" opacity="0.2" />
      <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" fill="url(#boldGrad)" opacity="0.15" />
      <circle cx="6" cy="4" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="6" cy="12" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="6" cy="20" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="14" cy="4" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="15" cy="12" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="15" cy="20" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="12" cy="8" r="1" fill="currentColor" opacity="0.6" />
      <circle cx="13" cy="16" r="1" fill="currentColor" opacity="0.6" />
      <path d="M8 4v16" strokeWidth="0.5" opacity="0.3" />
      <path d="M12 6c1 0 2 0.5 2.5 1.5" strokeWidth="0.5" opacity="0.4" />
      <path d="M13 14c1 0 2 0.5 2.5 1.5" strokeWidth="0.5" opacity="0.4" />
    </svg>
  ),
  
  // Italic - Sleek emphasis formatting
  Italic: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <line x1="19" y1="4" x2="10" y2="4" />
      <line x1="14" y1="20" x2="5" y2="20" />
      <line x1="15" y1="4" x2="9" y2="20" />
      <path d="M19 4h-9L5 20h9" fill="currentColor" opacity="0.05" />
      <circle cx="19" cy="4" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="10" cy="4" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="14" cy="20" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="5" cy="20" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="15" cy="4" r="0.6" fill="currentColor" opacity="0.7" />
      <circle cx="9" cy="20" r="0.6" fill="currentColor" opacity="0.7" />
      <circle cx="12" cy="12" r="1" fill="currentColor" opacity="0.5" />
      <path d="M13 8c0.5-1 1-1.5 2-2" strokeWidth="0.5" opacity="0.4" />
      <path d="M9 16c0.5-1 1-1.5 2-2" strokeWidth="0.5" opacity="0.4" />
    </svg>
  ),
  
  // TextList - Organized content list
  TextList: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <circle cx="4" cy="6" r="1.5" fill="currentColor" opacity="0.6" />
      <circle cx="4" cy="12" r="1.5" fill="currentColor" opacity="0.5" />
      <circle cx="4" cy="18" r="1.5" fill="currentColor" opacity="0.6" />
      <line x1="8" y1="6" x2="21" y2="6" strokeWidth="1.5" />
      <line x1="8" y1="12" x2="21" y2="12" strokeWidth="1.5" />
      <line x1="8" y1="18" x2="21" y2="18" strokeWidth="1.5" />
      <circle cx="4" cy="6" r="0.5" fill="white" opacity="0.8" />
      <circle cx="4" cy="12" r="0.5" fill="white" opacity="0.8" />
      <circle cx="4" cy="18" r="0.5" fill="white" opacity="0.8" />
      <rect x="8" y="5" width="13" height="2" rx="1" fill="currentColor" opacity="0.1" />
      <rect x="8" y="11" width="13" height="2" rx="1" fill="currentColor" opacity="0.1" />
      <rect x="8" y="17" width="13" height="2" rx="1" fill="currentColor" opacity="0.1" />
      <circle cx="11" cy="6" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="11" cy="12" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="11" cy="18" r="0.5" fill="currentColor" opacity="0.8" />
      <path d="M13 6h6" strokeWidth="2" opacity="0.8" />
      <path d="M13 12h4" strokeWidth="2" opacity="0.6" />
      <path d="M13 18h5" strokeWidth="2" opacity="0.7" />
    </svg>
  ),
  
  // BulletList - Organized bulleted list
  BulletList: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <circle cx="4" cy="6" r="1.5" fill="currentColor" opacity="0.7" />
      <circle cx="4" cy="12" r="1.5" fill="currentColor" opacity="0.6" />
      <circle cx="4" cy="18" r="1.5" fill="currentColor" opacity="0.7" />
      <line x1="8" y1="6" x2="21" y2="6" strokeWidth="1.5" />
      <line x1="8" y1="12" x2="21" y2="12" strokeWidth="1.5" />
      <line x1="8" y1="18" x2="21" y2="18" strokeWidth="1.5" />
      <circle cx="4" cy="6" r="0.5" fill="white" opacity="0.9" />
      <circle cx="4" cy="12" r="0.5" fill="white" opacity="0.9" />
      <circle cx="4" cy="18" r="0.5" fill="white" opacity="0.9" />
      <rect x="8" y="5" width="13" height="2" rx="1" fill="currentColor" opacity="0.1" />
      <rect x="8" y="11" width="13" height="2" rx="1" fill="currentColor" opacity="0.1" />
      <rect x="8" y="17" width="13" height="2" rx="1" fill="currentColor" opacity="0.1" />
      <circle cx="11" cy="6" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="11" cy="12" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="11" cy="18" r="0.5" fill="currentColor" opacity="0.8" />
      <path d="M13 6h6" strokeWidth="2" opacity="0.8" />
      <path d="M13 12h4" strokeWidth="2" opacity="0.6" />
      <path d="M13 18h5" strokeWidth="2" opacity="0.7" />
    </svg>
  ),
  
  // NumberList - Organized numbered list
  NumberList: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <path d="M4 6h1" strokeWidth="2" />
      <path d="M4 12h1v-3" strokeWidth="1.5" />
      <path d="M3 18h3" strokeWidth="1.5" />
      <path d="M4 17v-1h1" strokeWidth="1.5" />
      <circle cx="4.5" cy="6" r="2" fill="currentColor" opacity="0.1" />
      <circle cx="4.5" cy="12" r="2" fill="currentColor" opacity="0.1" />
      <circle cx="4.5" cy="18" r="2" fill="currentColor" opacity="0.1" />
      <line x1="8" y1="6" x2="21" y2="6" strokeWidth="1.5" />
      <line x1="8" y1="12" x2="21" y2="12" strokeWidth="1.5" />
      <line x1="8" y1="18" x2="21" y2="18" strokeWidth="1.5" />
      <rect x="8" y="5" width="13" height="2" rx="1" fill="currentColor" opacity="0.1" />
      <rect x="8" y="11" width="13" height="2" rx="1" fill="currentColor" opacity="0.1" />
      <rect x="8" y="17" width="13" height="2" rx="1" fill="currentColor" opacity="0.1" />
      <circle cx="11" cy="6" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="11" cy="12" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="11" cy="18" r="0.5" fill="currentColor" opacity="0.8" />
      <path d="M13 6h6" strokeWidth="2" opacity="0.8" />
      <path d="M13 12h4" strokeWidth="2" opacity="0.6" />
      <path d="M13 18h5" strokeWidth="2" opacity="0.7" />
    </svg>
  ),

  Online: ({ size = 24, className = "", ...props }) => (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
      {...props}
      >
        <defs>
          <radialGradient id="onlineGradient" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
            <stop offset="0%" stopColor="#22c55e" stopOpacity="1" />
            <stop offset="70%" stopColor="#16a34a" stopOpacity="1" />
            <stop offset="100%" stopColor="#15803d" stopOpacity="1" />
          </radialGradient>
        </defs>
        <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeOpacity="0.2" />
        <circle cx="12" cy="12" r="7" fill="url(#onlineGradient)" />
        <circle cx="12" cy="12" r="3" fill="white" fillOpacity="0.3" />
        <circle cx="10" cy="10" r="1" fill="white" fillOpacity="0.6" />
        
        {/* Subtle pulse animation elements */}
        <circle cx="12" cy="12" r="11" stroke="currentColor" strokeOpacity="0.05" strokeWidth="0.5" />
        <circle cx="12" cy="12" r="13" stroke="currentColor" strokeOpacity="0.03" strokeWidth="0.5" />
        
        {/* Energy lines */}
        <path d="M17 12h3" strokeWidth="0.5" strokeOpacity="0.4" />
        <path d="M4 12h3" strokeWidth="0.5" strokeOpacity="0.4" />
        <path d="M12 17v3" strokeWidth="0.5" strokeOpacity="0.4" />
        <path d="M12 4v3" strokeWidth="0.5" strokeOpacity="0.4" />
        
        {/* Dots at corners for depth */}
        <circle cx="16.5" cy="7.5" r="0.5" fill="currentColor" fillOpacity="0.5" />
        <circle cx="7.5" cy="7.5" r="0.5" fill="currentColor" fillOpacity="0.5" />
        <circle cx="16.5" cy="16.5" r="0.5" fill="currentColor" fillOpacity="0.5" />
        <circle cx="7.5" cy="16.5" r="0.5" fill="currentColor" fillOpacity="0.5" />
      </svg>
    ),
    
    Offline: ({ size = 24, className = "", ...props }) => (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        {...props}
      >
        <defs>
          <radialGradient id="offlineGradient" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
            <stop offset="0%" stopColor="#94a3b8" stopOpacity="1" />
            <stop offset="70%" stopColor="#64748b" stopOpacity="1" />
            <stop offset="100%" stopColor="#475569" stopOpacity="1" />
          </radialGradient>
        </defs>
        <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeOpacity="0.2" />
        <circle cx="12" cy="12" r="7" fill="url(#offlineGradient)" />
        <line x1="9" y1="9" x2="15" y2="15" stroke="white" strokeOpacity="0.4" strokeWidth="1.5" />
        <line x1="15" y1="9" x2="9" y2="15" stroke="white" strokeOpacity="0.4" strokeWidth="1.5" />
        
        {/* Subtle details */}
        <circle cx="12" cy="12" r="11" stroke="currentColor" strokeOpacity="0.05" strokeWidth="0.5" />
        <circle cx="12" cy="12" r="3" fill="white" fillOpacity="0.1" />
        
        {/* Corner dots for depth */}
        <circle cx="16.5" cy="7.5" r="0.5" fill="currentColor" fillOpacity="0.4" />
        <circle cx="7.5" cy="7.5" r="0.5" fill="currentColor" fillOpacity="0.4" />
        <circle cx="16.5" cy="16.5" r="0.5" fill="currentColor" fillOpacity="0.4" />
        <circle cx="7.5" cy="16.5" r="0.5" fill="currentColor" fillOpacity="0.4" />
      </svg>
    ),

    // SendMessage - Dynamic paper plane with energy trail
  SendMessage: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <defs>
        <linearGradient id="sendGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
        </linearGradient>
      </defs>
      <path d="M22 2L11 13" strokeWidth="1.5" />
      <path d="M22 2l-7 20-4-9-9-4 20-7z" strokeWidth="1.5" />
      <path d="M22 2l-7 20-4-9-9-4 20-7z" fill="url(#sendGrad)" opacity="0.1" />
      <circle cx="22" cy="2" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="11" cy="13" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="15" cy="22" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="2" cy="9" r="0.5" fill="currentColor" opacity="0.8" />
      
      {/* Energy trail elements */}
      <path d="M19 5l-5 5" strokeWidth="0.5" opacity="0.3" />
      <path d="M22 2l-3 3" strokeWidth="0.5" opacity="0.5" />
      <path d="M13 10l-2 3" strokeWidth="0.5" opacity="0.3" />
      <path d="M12.5 16.5l2.5 5.5" strokeWidth="0.5" opacity="0.3" />
      <path d="M5.5 7.5l-3.5 1.5" strokeWidth="0.5" opacity="0.3" />
      
      {/* Dimensional elements */}
      <circle cx="17" cy="7" r="0.6" fill="currentColor" opacity="0.6" />
      <circle cx="7" cy="5.5" r="0.4" fill="currentColor" opacity="0.4" />
      <circle cx="10.5" cy="17.5" r="0.4" fill="currentColor" opacity="0.4" />
      <path d="M11 13l-4-4" strokeWidth="0.5" opacity="0.5" />
      <path d="M11 13l4 9" strokeWidth="0.5" opacity="0.5" />
    </svg>
  ),
  
  // SendMessageFilled - Solid paper plane with energy trail
  SendMessageFilled: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <defs>
        <linearGradient id="sendFilledGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="1" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.7" />
        </linearGradient>
      </defs>
      <path d="M22 2L11 13" strokeWidth="1.5" />
      <path d="M22 2l-7 20-4-9-9-4 20-7z" fill="url(#sendFilledGrad)" strokeWidth="1.5" />
      <circle cx="22" cy="2" r="0.5" fill="white" opacity="0.8" />
      <circle cx="11" cy="13" r="0.8" fill="white" opacity="0.6" />
      <circle cx="15" cy="22" r="0.5" fill="white" opacity="0.5" />
      <circle cx="2" cy="9" r="0.5" fill="white" opacity="0.5" />
      
      {/* Energy trail elements */}
      <path d="M19 5l-5 5" stroke="white" strokeWidth="0.5" opacity="0.3" />
      <path d="M22 2l-3 3" stroke="white" strokeWidth="0.5" opacity="0.5" />
      <path d="M13 10l-2 3" stroke="white" strokeWidth="0.5" opacity="0.3" />
      <path d="M12.5 16.5l2.5 5.5" stroke="white" strokeWidth="0.5" opacity="0.3" />
      <path d="M5.5 7.5l-3.5 1.5" stroke="white" strokeWidth="0.5" opacity="0.3" />
      
      {/* Dimensional elements */}
      <circle cx="17" cy="7" r="0.6" fill="white" opacity="0.5" />
      <circle cx="7" cy="5.5" r="0.4" fill="white" opacity="0.4" />
      <circle cx="10.5" cy="17.5" r="0.4" fill="white" opacity="0.4" />
      <path d="M11 13l-4-4" stroke="white" strokeWidth="0.5" opacity="0.4" />
      <path d="M11 13l4 9" stroke="white" strokeWidth="0.5" opacity="0.4" />
    </svg>
  ),
  
  // SendMessageCircle - Paper plane in circular button style
  SendMessageCircle: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <defs>
        <radialGradient id="sendCircleGrad" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.1" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.05" />
        </radialGradient>
      </defs>
      <circle cx="12" cy="12" r="10" fill="url(#sendCircleGrad)" />
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.2" />
      
      {/* Paper plane, scaled and centered */}
      <path d="M18 8l-7 7" strokeWidth="1.5" />
      <path d="M18 8l-4 12-3-5-5-3 12-4z" strokeWidth="1.5" />
      <path d="M18 8l-4 12-3-5-5-3 12-4z" fill="currentColor" opacity="0.15" />
      <circle cx="18" cy="8" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="11" cy="15" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="14" cy="20" r="0.4" fill="currentColor" opacity="0.7" />
      <circle cx="6" cy="12" r="0.4" fill="currentColor" opacity="0.7" />
      
      {/* Energy trail elements */}
      <path d="M16 10l-3 3" strokeWidth="0.5" opacity="0.3" />
      <path d="M18 8l-2 2" strokeWidth="0.5" opacity="0.5" />
      <path d="M12 13l-1 2" strokeWidth="0.5" opacity="0.3" />
      <path d="M11.5 16.5l2.5 3.5" strokeWidth="0.5" opacity="0.3" />
      <path d="M8 10.5l-2 1.5" strokeWidth="0.5" opacity="0.3" />
      
      {/* Additional details for depth */}
      <circle cx="12" cy="12" r="7" stroke="currentColor" strokeOpacity="0.1" />
      <circle cx="16.5" cy="7.5" r="0.5" fill="currentColor" opacity="0.5" />
      <circle cx="7.5" cy="7.5" r="0.5" fill="currentColor" opacity="0.5" />
      <circle cx="16.5" cy="16.5" r="0.5" fill="currentColor" opacity="0.5" />
      <circle cx="7.5" cy="16.5" r="0.5" fill="currentColor" opacity="0.5" />
    </svg>
  ),

  CreatePoll: ({ size = 24, className = "", ...props }) => (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
      {...props}
      >
        <defs>
          <linearGradient id="pollGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
          </linearGradient>
        </defs>
        <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" />
        <rect x="3" y="3" width="18" height="18" rx="2" fill="currentColor" opacity="0.05" />
        
        {/* Poll bars */}
        <rect x="6" y="7" width="12" height="2" rx="1" fill="url(#pollGradient)" opacity="0.7" />
        <rect x="6" y="11" width="8" height="2" rx="1" fill="url(#pollGradient)" opacity="0.5" />
        <rect x="6" y="15" width="10" height="2" rx="1" fill="url(#pollGradient)" opacity="0.6" />
        
        {/* Percentage indicators */}
        <text x="19" y="9" fontSize="3" fontWeight="bold" fill="currentColor" opacity="0.8">75%</text>
        <text x="15" y="13" fontSize="3" fontWeight="bold" fill="currentColor" opacity="0.8">45%</text>
        <text x="17" y="17" fontSize="3" fontWeight="bold" fill="currentColor" opacity="0.8">60%</text>
        
        {/* Depth elements */}
        <circle cx="3" cy="3" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="21" cy="3" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="3" cy="21" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="21" cy="21" r="0.5" fill="currentColor" opacity="0.8" />
        
        <circle cx="6" cy="7" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="18" cy="7" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="6" cy="11" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="14" cy="11" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="6" cy="15" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="16" cy="15" r="0.5" fill="currentColor" opacity="0.8" />
        
        {/* Energy lines */}
        <path d="M12 3v3" strokeWidth="0.5" opacity="0.3" />
        <path d="M12 18v3" strokeWidth="0.5" opacity="0.3" />
        <path d="M3 12h3" strokeWidth="0.5" opacity="0.3" />
        <path d="M18 12h3" strokeWidth="0.5" opacity="0.3" />
      </svg>
    ),
    
    Target: ({ size = 24, className = "", ...props }) => (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
      {...props}
      >
        <defs>
          <radialGradient id="targetGradient" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.1" />
            <stop offset="50%" stopColor="currentColor" stopOpacity="0.05" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0.02" />
          </radialGradient>
        </defs>
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
        <circle cx="12" cy="12" r="6" stroke="currentColor" strokeWidth="1.5" />
        <circle cx="12" cy="12" r="2" stroke="currentColor" strokeWidth="1.5" />
        <circle cx="12" cy="12" r="10" fill="url(#targetGradient)" />
        
        {/* Bullseye center */}
        <circle cx="12" cy="12" r="1" fill="currentColor" opacity="0.8" />
        
        {/* Cross hairs */}
        <path d="M12 2v4" strokeWidth="1.5" />
        <path d="M12 18v4" strokeWidth="1.5" />
        <path d="M22 12h-4" strokeWidth="1.5" />
        <path d="M6 12H2" strokeWidth="1.5" />
        
        {/* Energy fields */}
        <path d="M17 7l1.5-1.5" strokeWidth="0.5" opacity="0.5" />
        <path d="M5.5 18.5L7 17" strokeWidth="0.5" opacity="0.5" />
        <path d="M7 7L5.5 5.5" strokeWidth="0.5" opacity="0.5" />
        <path d="M18.5 18.5L17 17" strokeWidth="0.5" opacity="0.5" />
        
        {/* Dimensional accents */}
        <circle cx="12" cy="2" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="12" cy="22" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="2" cy="12" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="22" cy="12" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="12" cy="6" r="0.5" fill="currentColor" opacity="0.6" />
        <circle cx="12" cy="18" r="0.5" fill="currentColor" opacity="0.6" />
        <circle cx="6" cy="12" r="0.5" fill="currentColor" opacity="0.6" />
        <circle cx="18" cy="12" r="0.5" fill="currentColor" opacity="0.6" />
      </svg>
    ),
    
    DollarSign: ({ size = 24, className = "", ...props }) => (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
      {...props}
      >
        <defs>
          <linearGradient id="dollarGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
          </linearGradient>
        </defs>
        <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.05" />
        <path d="M12 2v20" strokeWidth="1.5" opacity="0.7" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H7" strokeWidth="1.5" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H7" fill="url(#dollarGradient)" opacity="0.1" />
        
        {/* Dimensional accents */}
        <circle cx="12" cy="2" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="12" cy="22" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="17" cy="5" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="9.5" cy="5" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="9.5" cy="12" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="14.5" cy="12" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="14.5" cy="19" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="7" cy="19" r="0.5" fill="currentColor" opacity="0.8" />
        
        {/* Energy flow */}
        <path d="M9 8c0.5-1 1.5-1.5 3-1.5" strokeWidth="0.5" opacity="0.4" />
        <path d="M15 16c-0.5 1-1.5 1.5-3 1.5" strokeWidth="0.5" opacity="0.4" />
        <path d="M12 7v10" strokeWidth="0.5" opacity="0.3" />
        
        {/* Central emphasis */}
        <circle cx="12" cy="12" r="1.5" fill="currentColor" opacity="0.2" />
        <circle cx="12" cy="12" r="0.8" fill="currentColor" opacity="0.4" />
      </svg>
    ),
    
    DollarSignCircle: ({ size = 24, className = "", ...props }) => (
      <svg
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <defs>
          <radialGradient id="dollarCircleGradient" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.2" />
            <stop offset="70%" stopColor="currentColor" stopOpacity="0.1" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0.05" />
          </radialGradient>
        </defs>
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
        <circle cx="12" cy="12" r="10" fill="url(#dollarCircleGradient)" />
        <path d="M12 6v12" strokeWidth="1.5" opacity="0.7" />
        <path d="M16 8H10a2 2 0 0 0 0 4h4a2 2 0 0 1 0 4H8" strokeWidth="1.5" />
        
        {/* Dimensional accents */}
        <circle cx="12" cy="6" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="12" cy="18" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="16" cy="8" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="10" cy="8" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="10" cy="12" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="14" cy="12" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="14" cy="16" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="8" cy="16" r="0.5" fill="currentColor" opacity="0.8" />
        
        {/* Energy fields */}
        <path d="M17 7l1.5-1.5" strokeWidth="0.5" opacity="0.3" />
        <path d="M5.5 18.5L7 17" strokeWidth="0.5" opacity="0.3" />
        <path d="M7 7L5.5 5.5" strokeWidth="0.5" opacity="0.3" />
        <path d="M18.5 18.5L17 17" strokeWidth="0.5" opacity="0.3" />
        
        {/* Central emphasis */}
        <circle cx="12" cy="12" r="1" fill="currentColor" opacity="0.3" />
      </svg>
    ),

    // Overview - Dashboard summary with interactive data
  Overview: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <defs>
        <linearGradient id="overviewGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
        </linearGradient>
      </defs>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <rect x="3" y="3" width="18" height="18" rx="2" fill="currentColor" opacity="0.05" />
      
      {/* Chart elements */}
      <path d="M3 16h18" strokeWidth="1" opacity="0.5" />
      <path d="M7 10l3 3 4-6 3 2" strokeWidth="1.5" />
      <path d="M7 10l3 3 4-6 3 2" fill="url(#overviewGrad)" opacity="0.1" />
      
      {/* Bar chart elements */}
      <rect x="7" y="16" width="2" height="3" fill="currentColor" opacity="0.7" />
      <rect x="11" y="16" width="2" height="4" fill="currentColor" opacity="0.5" />
      <rect x="15" y="16" width="2" height="2" fill="currentColor" opacity="0.6" />
      
      {/* Data points */}
      <circle cx="7" cy="10" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="10" cy="13" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="14" cy="7" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="17" cy="9" r="0.5" fill="currentColor" opacity="0.8" />
      
      {/* Depth elements */}
      <circle cx="3" cy="3" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="21" cy="3" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="3" cy="21" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="21" cy="21" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="12" cy="12" r="0.5" fill="currentColor" opacity="0.5" />
    </svg>
  ),

  // Eye Icon - Show password with vision rays
EyeIcon : ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <defs>
      <linearGradient id="eyeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
      </linearGradient>
    </defs>
    
    {/* Eye shape */}
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" fill="url(#eyeGrad)" opacity="0.1" />
    
    {/* Iris */}
    <circle cx="12" cy="12" r="3" />
    <circle cx="12" cy="12" r="3" fill="currentColor" opacity="0.15" />
    
    {/* Pupil */}
    <circle cx="12" cy="12" r="1" fill="currentColor" opacity="0.9" />
    
    {/* Vision rays */}
    <path d="M12 2v2" strokeWidth="0.8" opacity="0.6" />
    <path d="M12 20v2" strokeWidth="0.8" opacity="0.6" />
    <path d="M4.22 4.22l1.42 1.42" strokeWidth="0.8" opacity="0.5" />
    <path d="M18.36 18.36l1.42 1.42" strokeWidth="0.8" opacity="0.5" />
    <path d="M2 12h2" strokeWidth="0.8" opacity="0.6" />
    <path d="M20 12h2" strokeWidth="0.8" opacity="0.6" />
    <path d="M4.22 19.78l1.42-1.42" strokeWidth="0.8" opacity="0.5" />
    <path d="M18.36 5.64l1.42-1.42" strokeWidth="0.8" opacity="0.5" />
    
    {/* Light reflection */}
    <circle cx="13.5" cy="10.5" r="0.5" fill="currentColor" opacity="0.8" />
    
    {/* Energy accents */}
    <circle cx="8" cy="12" r="0.3" fill="currentColor" opacity="0.5" />
    <circle cx="16" cy="12" r="0.3" fill="currentColor" opacity="0.5" />
  </svg>
),

// Eye Off Icon - Hide password with strike through
EyeOffIcon : ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <defs>
      <linearGradient id="eyeOffGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
      </linearGradient>
    </defs>
    
    {/* Eye shape (partially visible) */}
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" fill="url(#eyeOffGrad)" opacity="0.1" />
    
    {/* Strike line */}
    <line x1="1" y1="1" x2="23" y2="23" strokeWidth="2" />
    
    {/* Blocked vision indicators */}
    <path d="M3 3l18 18" strokeWidth="0.5" opacity="0.3" />
    
    {/* Fading energy dots */}
    <circle cx="12" cy="12" r="0.5" fill="currentColor" opacity="0.3" />
    <circle cx="9" cy="9" r="0.3" fill="currentColor" opacity="0.4" />
    <circle cx="15" cy="15" r="0.3" fill="currentColor" opacity="0.4" />
    
    {/* Disrupted vision rays */}
    <path d="M12 5v-1" strokeWidth="0.6" opacity="0.3" />
    <path d="M19 12h1" strokeWidth="0.6" opacity="0.3" />
    <path d="M12 19v1" strokeWidth="0.6" opacity="0.3" />
    <path d="M5 12h-1" strokeWidth="0.6" opacity="0.3" />
    
    {/* Corner energy dissipation */}
    <circle cx="2" cy="2" r="0.4" fill="currentColor" opacity="0.6" />
    <circle cx="22" cy="22" r="0.4" fill="currentColor" opacity="0.6" />
  </svg>
),
  
  // EarningsReports - Financial performance metrics
  EarningsReports: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <defs>
        <linearGradient id="earningsGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
        </linearGradient>
      </defs>
      <path d="M21 14H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h18a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2z" />
      <path d="M21 14H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h18a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2z" fill="currentColor" opacity="0.05" />
      <path d="M23 14v5a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2v-5" />
      
      {/* Dollar sign */}
      <path d="M12 7v7" strokeWidth="1.5" />
      <path d="M15 9h-3.5a1.5 1.5 0 0 0 0 3h3a1.5 1.5 0 0 1 0 3H9" strokeWidth="1.5" />
      <path d="M15 9h-3.5a1.5 1.5 0 0 0 0 3h3a1.5 1.5 0 0 1 0 3H9" fill="url(#earningsGrad)" opacity="0.1" />
      
      {/* Growth indicator */}
      <path d="M17 6l2 2-2 2" strokeWidth="1" opacity="0.7" />
      
      {/* Depth elements */}
      <circle cx="12" cy="7" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="12" cy="14" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="15" cy="9" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="9" cy="15" r="0.5" fill="currentColor" opacity="0.8" />
      
      <circle cx="3" cy="5" r="0.5" fill="currentColor" opacity="0.7" />
      <circle cx="21" cy="5" r="0.5" fill="currentColor" opacity="0.7" />
      <circle cx="3" cy="19" r="0.5" fill="currentColor" opacity="0.7" />
      <circle cx="21" cy="19" r="0.5" fill="currentColor" opacity="0.7" />
      
      {/* Line divider */}
      <path d="M3 14h18" strokeWidth="0.5" opacity="0.5" />
    </svg>
  ),
  
  // ReachDiscovery - Audience reach and discovery metrics
  ReachDiscovery: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <defs>
        <radialGradient id="reachGrad" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.2" />
          <stop offset="70%" stopColor="currentColor" stopOpacity="0.1" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.05" />
        </radialGradient>
      </defs>
      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="12" cy="12" r="8" fill="url(#reachGrad)" />
      
      {/* Discovery rays */}
      <path d="M12 4v1.5" strokeWidth="1.5" />
      <path d="M12 18.5V20" strokeWidth="1.5" />
      <path d="M4 12h1.5" strokeWidth="1.5" />
      <path d="M18.5 12H20" strokeWidth="1.5" />
      
      {/* Diagonal rays */}
      <path d="M6.5 6.5l1 1" strokeWidth="1.5" />
      <path d="M16.5 16.5l1 1" strokeWidth="1.5" />
      <path d="M16.5 6.5l-1 1" strokeWidth="1.5" />
      <path d="M6.5 16.5l1 1" strokeWidth="1.5" />
      
      {/* Search icon */}
      <circle cx="12" cy="12" r="1.5" fill="currentColor" opacity="0.7" />
      <path d="M15 15l3 3" strokeWidth="1.5" />
      
      {/* Dimensional elements */}
      <circle cx="12" cy="4" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="12" cy="20" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="4" cy="12" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="20" cy="12" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="18" cy="18" r="0.5" fill="currentColor" opacity="0.8" />
    </svg>
  ),
  
  // EngagementAnalytics - User interaction metrics
  EngagementAnalytics: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <defs>
        <linearGradient id="engagementGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
        </linearGradient>
      </defs>
      <path d="M18 8c1.42 0 2.17.32 3 1.5-1.73 0-3 .5-3 2.5 0-2-1.27-2.5-3-2.5.83-1.18 1.58-1.5 3-1.5z" />
      <path d="M18 8c1.42 0 2.17.32 3 1.5-1.73 0-3 .5-3 2.5 0-2-1.27-2.5-3-2.5.83-1.18 1.58-1.5 3-1.5z" fill="url(#engagementGrad)" opacity="0.5" />
      
      <path d="M10.59 13.41c2.83.47 5.17 1.89 6.41 4.59.47-4.87-1.94-9.61-5.59-11.59-1.71-1.18-3.4-1.59-5-1.59-3.08 0-5.41 1.83-5.41 4.18 0 1.95 1.53 3.32 3.24 4" />
      <path d="M10.59 13.41c2.83.47 5.17 1.89 6.41 4.59.47-4.87-1.94-9.61-5.59-11.59-1.71-1.18-3.4-1.59-5-1.59-3.08 0-5.41 1.83-5.41 4.18 0 1.95 1.53 3.32 3.24 4" fill="currentColor" opacity="0.1" />
      
      {/* Comment box */}
      <path d="M9 18h.01" />
      <path d="M4 14.99c-.5-.5-1.5-3.5 0-5.48" />
      <path d="M11 19.99l-2-1-2 1v-5a2 2 0 0 1 2-2h.5" />
      
      {/* Interaction points */}
      <circle cx="18" cy="12" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="16.5" cy="9.5" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="19.5" cy="9.5" r="0.5" fill="currentColor" opacity="0.8" />
      
      <circle cx="10.59" cy="13.41" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="9" cy="18" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="7" cy="13" r="0.5" fill="currentColor" opacity="0.7" />
      <circle cx="4" cy="12" r="0.5" fill="currentColor" opacity="0.7" />
    </svg>
  ),
  
  // SubscriberAnalytics - Follower and subscriber metrics
  SubscriberAnalytics: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <defs>
        <linearGradient id="subscriberGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
        </linearGradient>
      </defs>
      <circle cx="8" cy="8" r="3" />
      <circle cx="16" cy="8" r="3" fill="currentColor" opacity="0.1" />
      <path d="M14 15c0-2.21-2.69-4-6-4s-6 1.79-6 4v2h12v-2z" />
      <path d="M14 15c0-2.21-2.69-4-6-4s-6 1.79-6 4v2h12v-2z" fill="url(#subscriberGrad)" opacity="0.2" />
      
      <path d="M17 15c2.21 0 4 .9 4 2v2h-5" />
      
      {/* Growth indicator */}
      <path d="M16 5l2 1-2 1" strokeWidth="1" opacity="0.7" />
      <path d="M20 10l-2 5" strokeWidth="0.75" opacity="0.6" />
      <path d="M20 10l2 1" strokeWidth="0.75" opacity="0.6" />
      
      {/* Dimensional elements */}
      <circle cx="8" cy="8" r="0.8" fill="white" opacity="0.5" />
      <circle cx="16" cy="8" r="0.8" fill="currentColor" opacity="0.6" />
      
      <circle cx="8" cy="5" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="8" cy="11" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="5" cy="8" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="11" cy="8" r="0.5" fill="currentColor" opacity="0.8" />
      
      <circle cx="2" cy="15" r="0.5" fill="currentColor" opacity="0.7" />
      <circle cx="14" cy="15" r="0.5" fill="currentColor" opacity="0.7" />
      <circle cx="21" cy="17" r="0.5" fill="currentColor" opacity="0.7" />
    </svg>
  ),
  
  // TipsPayPerView - Monetization metrics for tipping and PPV
  TipsPayPerView: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <defs>
        <linearGradient id="tipsGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
        </linearGradient>
      </defs>
      <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.05" />
      
      {/* Dollar coins */}
      <circle cx="9" cy="9" r="4" />
      <circle cx="9" cy="9" r="4" fill="url(#tipsGrad)" opacity="0.3" />
      <circle cx="15" cy="15" r="4" />
      <circle cx="15" cy="15" r="4" fill="url(#tipsGrad)" opacity="0.2" />
      
      {/* Dollar signs */}
      <path d="M9 7v4" strokeWidth="1.2" />
      <path d="M10.5 8h-2a1 1 0 0 0 0 2h1a1 1 0 0 1 0 2h-2" strokeWidth="1.2" />
      
      <path d="M15 13v4" strokeWidth="1.2" />
      <path d="M16.5 14h-2a1 1 0 0 0 0 2h1a1 1 0 0 1 0 2h-2" strokeWidth="1.2" />
      
      {/* Dimensional elements */}
      <circle cx="9" cy="9" r="1" fill="currentColor" opacity="0.2" />
      <circle cx="15" cy="15" r="1" fill="currentColor" opacity="0.2" />
      
      <circle cx="9" cy="7" r="0.4" fill="currentColor" opacity="0.8" />
      <circle cx="9" cy="11" r="0.4" fill="currentColor" opacity="0.8" />
      <circle cx="15" cy="13" r="0.4" fill="currentColor" opacity="0.8" />
      <circle cx="15" cy="17" r="0.4" fill="currentColor" opacity="0.8" />
      
      <circle cx="7" cy="9" r="0.4" fill="currentColor" opacity="0.7" />
      <circle cx="11" cy="9" r="0.4" fill="currentColor" opacity="0.7" />
      <circle cx="13" cy="15" r="0.4" fill="currentColor" opacity="0.7" />
      <circle cx="17" cy="15" r="0.4" fill="currentColor" opacity="0.7" />
    </svg>
  ),
  
  // ChargebacksRefunds - Payment returns and disputes
  ChargebacksRefunds: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <defs>
        <linearGradient id="chargebackGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
        </linearGradient>
      </defs>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <rect x="3" y="5" width="18" height="14" rx="2" fill="currentColor" opacity="0.05" />
      <line x1="3" y1="10" x2="21" y2="10" />
      
      {/* Circular return arrow */}
      <path d="M16 15a4 4 0 1 0-8 0" strokeWidth="1.5" />
      <path d="M12 15v3" strokeWidth="1.5" />
      <path d="M10 16l2-1 2 1" strokeWidth="1.2" opacity="0.8" />
      <path d="M16 15a4 4 0 1 0-8 0" fill="url(#chargebackGrad)" opacity="0.15" />
      
      {/* Credit card details */}
      <path d="M7 8h.01" strokeWidth="1.5" />
      <path d="M9 8h.01" strokeWidth="1.5" />
      
      {/* Dimensional elements */}
      <circle cx="3" cy="5" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="21" cy="5" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="3" cy="19" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="21" cy="19" r="0.5" fill="currentColor" opacity="0.8" />
      
      <circle cx="12" cy="15" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="12" cy="18" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="8" cy="15" r="0.5" fill="currentColor" opacity="0.7" />
      <circle cx="16" cy="15" r="0.5" fill="currentColor" opacity="0.7" />
      
      <circle cx="7" cy="8" r="0.5" fill="currentColor" opacity="0.7" />
      <circle cx="9" cy="8" r="0.5" fill="currentColor" opacity="0.7" />
    </svg>
  ),
  
  // PayoutReports - Payout transaction metrics
  PayoutReports: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <defs>
        <linearGradient id="payoutGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
        </linearGradient>
      </defs>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" fill="currentColor" opacity="0.05" />
      <path d="M14 2v6h6" />
      
      {/* Dollar sign */}
      <path d="M12 12v6" strokeWidth="1.2" />
      <path d="M15 14h-3a2 2 0 1 0 0 4h2a2 2 0 1 1 0 4H9" strokeWidth="1.2" />
      <path d="M15 14h-3a2 2 0 1 0 0 4h2a2 2 0 1 1 0 4H9" fill="url(#payoutGrad)" opacity="0.2" />
      
      {/* Document lines */}
      <path d="M8 18h0.01" strokeWidth="1.5" opacity="0.7" />
      <path d="M16 22h0.01" strokeWidth="1.5" opacity="0.7" />
      <path d="M8 22h0.01" strokeWidth="1.5" opacity="0.7" />
      
      {/* Dimensional elements */}
      <circle cx="14" cy="2" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="14" cy="8" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="20" cy="8" r="0.5" fill="currentColor" opacity="0.8" />
      
      <circle cx="12" cy="12" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="12" cy="18" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="15" cy="14" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="12" cy="14" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="9" cy="22" r="0.5" fill="currentColor" opacity="0.8" />
      
      <circle cx="4" cy="4" r="0.5" fill="currentColor" opacity="0.7" />
      <circle cx="4" cy="20" r="0.5" fill="currentColor" opacity="0.7" />
      <circle cx="20" cy="20" r="0.5" fill="currentColor" opacity="0.7" />
    </svg>
  ),

  // Credit Card Icon - Card with chip and payment waves
CreditCardIcon : ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <defs>
      <linearGradient id="cardGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
      </linearGradient>
    </defs>
    
    {/* Main card shape */}
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <rect x="2" y="5" width="20" height="14" rx="2" fill="url(#cardGrad)" opacity="0.1" />
    
    {/* Magnetic stripe */}
    <rect x="2" y="8" width="20" height="3" fill="currentColor" opacity="0.2" />
    
    {/* Chip */}
    <rect x="5" y="13" width="4" height="3" rx="0.5" />
    <rect x="5" y="13" width="4" height="3" rx="0.5" fill="currentColor" opacity="0.15" />
    
    {/* Chip details */}
    <line x1="6" y1="14" x2="8" y2="14" strokeWidth="0.5" opacity="0.8" />
    <line x1="6" y1="15" x2="8" y2="15" strokeWidth="0.5" opacity="0.8" />
    
    {/* Card number dots */}
    <circle cx="12" cy="14.5" r="0.3" fill="currentColor" opacity="0.6" />
    <circle cx="14" cy="14.5" r="0.3" fill="currentColor" opacity="0.6" />
    <circle cx="16" cy="14.5" r="0.3" fill="currentColor" opacity="0.6" />
    <circle cx="18" cy="14.5" r="0.3" fill="currentColor" opacity="0.6" />
    
    {/* Contactless payment waves */}
    <path d="M17 2c1 1 1 3 0 4" strokeWidth="1" opacity="0.6" />
    <path d="M19 0c2 2 2 5 0 7" strokeWidth="1" opacity="0.4" />
    <path d="M21 -2c3 3 3 7 0 10" strokeWidth="0.8" opacity="0.3" />
    
    {/* Energy accents */}
    <circle cx="2" cy="5" r="0.5" fill="currentColor" opacity="0.8" />
    <circle cx="22" cy="5" r="0.5" fill="currentColor" opacity="0.8" />
    <circle cx="19" cy="16" r="0.8" fill="currentColor" opacity="0.15" />
    <circle cx="19" cy="16" r="0.4" fill="currentColor" opacity="0.8" />
    
    {/* Transaction flow indicator */}
    <path d="M12 20v2" strokeWidth="0.5" opacity="0.4" />
    <path d="M10 21v1" strokeWidth="0.5" opacity="0.3" />
    <path d="M14 21v1" strokeWidth="0.5" opacity="0.3" />
  </svg>
),
  
  // ContentPerformance - Content metrics and analytics
  ContentPerformance: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <defs>
        <linearGradient id="contentGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
        </linearGradient>
      </defs>
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <rect x="2" y="3" width="20" height="14" rx="2" fill="currentColor" opacity="0.05" />
      
      {/* Play indicator */}
      <polygon points="10,7 10,13 15,10" fill="url(#contentGrad)" opacity="0.8" />
      
      {/* Graph indicator */}
      <path d="M8 21l3-3 2 2 3-3" strokeWidth="1.5" />
      <path d="M8 21l3-3 2 2 3-3" fill="url(#contentGrad)" opacity="0.2" />
      <circle cx="8" cy="21" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="11" cy="18" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="13" cy="20" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="16" cy="17" r="0.5" fill="currentColor" opacity="0.8" />
      
      {/* Star rating */}
      <path d="M21 19l-1.3-1" strokeWidth="0.8" opacity="0.8" />
      <path d="M19 21l-0.5-1.5" strokeWidth="0.8" opacity="0.8" />
      <path d="M17 19l1.3-1" strokeWidth="0.8" opacity="0.8" />
      
      {/* Dimensional elements */}
      <circle cx="2" cy="3" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="22" cy="3" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="2" cy="17" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="22" cy="17" r="0.5" fill="currentColor" opacity="0.8" />
      
      <circle cx="10" cy="7" r="0.4" fill="currentColor" opacity="0.8" />
      <circle cx="10" cy="13" r="0.4" fill="currentColor" opacity="0.8" />
      <circle cx="15" cy="10" r="0.4" fill="currentColor" opacity="0.8" />
    </svg>
  ),
  
  // AudienceDemographics - User demographic information
  AudienceDemographics: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <defs>
        <linearGradient id="demographicsGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
        </linearGradient>
      </defs>
      <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.05" />
      
      {/* People indicators */}
      <circle cx="12" cy="8" r="2.5" />
      <circle cx="12" cy="8" r="2.5" fill="url(#demographicsGrad)" opacity="0.3" />
      
      <circle cx="7" cy="12" r="2" fill="currentColor" opacity="0.15" />
      <path d="M7 10v4" strokeWidth="0.8" opacity="0.7" />
      <path d="M5 12h4" strokeWidth="0.8" opacity="0.7" />
      
      <circle cx="17" cy="12" r="2" fill="currentColor" opacity="0.15" />
      <path d="M17 10v4" strokeWidth="0.8" opacity="0.7" />
      <path d="M15 12h4" strokeWidth="0.8" opacity="0.7" />
      
      {/* Demographic bars */}
      <path d="M5 16h3" strokeWidth="1.5" />
      <path d="M10 16h4" strokeWidth="1.5" />
      <path d="M16 16h3" strokeWidth="1.5" />
      
      <path d="M6 18h1" strokeWidth="1.5" opacity="0.7" />
      <path d="M11 18h2" strokeWidth="1.5" opacity="0.7" />
      <path d="M17 18h1" strokeWidth="1.5" opacity="0.7" />
      
      {/* Dimensional elements */}
      <circle cx="12" cy="8" r="0.6" fill="white" opacity="0.5" />
      <circle cx="12" cy="2" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="12" cy="22" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="2" cy="12" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="22" cy="12" r="0.5" fill="currentColor" opacity="0.8" />
      
      <circle cx="5" cy="16" r="0.4" fill="currentColor" opacity="0.7" />
      <circle cx="8" cy="16" r="0.4" fill="currentColor" opacity="0.7" />
      <circle cx="10" cy="16" r="0.4" fill="currentColor" opacity="0.7" />
      <circle cx="14" cy="16" r="0.4" fill="currentColor" opacity="0.7" />
      <circle cx="16" cy="16" r="0.4" fill="currentColor" opacity="0.7" />
      <circle cx="19" cy="16" r="0.4" fill="currentColor" opacity="0.7" />
    </svg>
  ),
  
  // MarketingPromotions - Marketing campaign metrics
  MarketingPromotions: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <defs>
        <linearGradient id="marketingGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
        </linearGradient>
      </defs>
      <polygon points="3,21 12,3 21,21" />
      <polygon points="3,21 12,3 21,21" fill="url(#marketingGrad)" opacity="0.1" />
      
      {/* Megaphone */}
      <path d="M12 12l5-3v6l-5-3z" fill="currentColor" opacity="0.4" />
      <path d="M12 12l5-3v6l-5-3z" />
      
      {/* Sound waves */}
      <path d="M19 9c1 1 1 5 0 6" strokeWidth="1" />
      <path d="M21 7c2 2 2 8 0 10" strokeWidth="1" opacity="0.7" />
      
      {/* Promo text */}
      <path d="M7 12h3" strokeWidth="1.5" />
      <path d="M8 15h2" strokeWidth="1.5" opacity="0.8" />
      <path d="M6 18h6" strokeWidth="1.5" opacity="0.6" />
      
      {/* Dimensional elements */}
      <circle cx="3" cy="21" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="12" cy="3" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="21" cy="21" r="0.5" fill="currentColor" opacity="0.8" />
      
      <circle cx="12" cy="12" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="17" cy="9" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="17" cy="15" r="0.5" fill="currentColor" opacity="0.8" />
      
      <circle cx="7" cy="12" r="0.4" fill="currentColor" opacity="0.7" />
      <circle cx="10" cy="12" r="0.4" fill="currentColor" opacity="0.7" />
      <circle cx="8" cy="15" r="0.4" fill="currentColor" opacity="0.7" />
      <circle cx="10" cy="15" r="0.4" fill="currentColor" opacity="0.7" />
      <circle cx="6" cy="18" r="0.4" fill="currentColor" opacity="0.7" />
      <circle cx="12" cy="18" r="0.4" fill="currentColor" opacity="0.7" />
    </svg>
  ),

  CloseX: ({ size = 24, className = "", ...props }) => (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        {...props}
      >
        <defs>
          <linearGradient id="closeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.1" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0.05" />
          </linearGradient>
        </defs>
        <circle cx="12" cy="12" r="10" fill="url(#closeGradient)" opacity="0.7" />
        
        {/* X shape */}
        <line x1="18" y1="6" x2="6" y2="18" strokeWidth="2" />
        <line x1="6" y1="6" x2="18" y2="18" strokeWidth="2" />
        
        {/* Dimensional accents */}
        <circle cx="6" cy="6" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="18" cy="6" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="6" cy="18" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="18" cy="18" r="0.5" fill="currentColor" opacity="0.8" />
        
        {/* Energy fields */}
        <path d="M8 8l-1.5-1.5" strokeWidth="0.5" opacity="0.4" />
        <path d="M16 16l1.5 1.5" strokeWidth="0.5" opacity="0.4" />
        <path d="M16 8l1.5-1.5" strokeWidth="0.5" opacity="0.4" />
        <path d="M8 16l-1.5 1.5" strokeWidth="0.5" opacity="0.4" />
        
        {/* Central emphasis */}
        <circle cx="12" cy="12" r="1" fill="currentColor" opacity="0.3" />
      </svg>
    ),
    
    CloseXCircle: ({ size = 24, className = "", ...props }) => (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        {...props}
      >
        <defs>
          <radialGradient id="closeCircleGradient" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.2" />
            <stop offset="70%" stopColor="currentColor" stopOpacity="0.1" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0.05" />
          </radialGradient>
        </defs>
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
        <circle cx="12" cy="12" r="10" fill="url(#closeCircleGradient)" />
        
        {/* X shape */}
        <line x1="15" y1="9" x2="9" y2="15" strokeWidth="1.5" />
        <line x1="9" y1="9" x2="15" y2="15" strokeWidth="1.5" />
        
        {/* Dimensional accents */}
        <circle cx="9" cy="9" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="15" cy="9" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="9" cy="15" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="15" cy="15" r="0.5" fill="currentColor" opacity="0.8" />
        
        {/* Energy fields */}
        <path d="M17 7l1.5-1.5" strokeWidth="0.5" opacity="0.3" />
        <path d="M5.5 18.5L7 17" strokeWidth="0.5" opacity="0.3" />
        <path d="M7 7L5.5 5.5" strokeWidth="0.5" opacity="0.3" />
        <path d="M18.5 18.5L17 17" strokeWidth="0.5" opacity="0.3" />
        
        {/* Central emphasis */}
        <circle cx="12" cy="12" r="1" fill="currentColor" opacity="0.3" />
      </svg>
    ),

    ContentManagement: ({ size = 24, className = "", ...props }) => (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        {...props}
      >
        <defs>
          <linearGradient id="contentMgmtGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
          </linearGradient>
        </defs>
        
        {/* Folder structure */}
        <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-9l-2-2H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2z" />
        <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-9l-2-2H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2z" fill="currentColor" opacity="0.05" />
        
        {/* Content items */}
        <rect x="7" y="10" width="4" height="5" rx="1" fill="url(#contentMgmtGradient)" opacity="0.7" />
        <rect x="13" y="10" width="4" height="5" rx="1" fill="url(#contentMgmtGradient)" opacity="0.5" />
        
        {/* Settings/management gear */}
        <circle cx="17.5" cy="16.5" r="1.5" />
        <path d="M17.5 15v-1" strokeWidth="0.75" opacity="0.8" />
        <path d="M17.5 19v-1" strokeWidth="0.75" opacity="0.8" />
        <path d="M16 16.5h1" strokeWidth="0.75" opacity="0.8" />
        <path d="M19 16.5h1" strokeWidth="0.75" opacity="0.8" />
        
        {/* Dimensional accents */}
        <circle cx="4" cy="4" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="4" cy="20" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="20" cy="8" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="20" cy="20" r="0.5" fill="currentColor" opacity="0.8" />
        
        <circle cx="7" cy="10" r="0.4" fill="currentColor" opacity="0.8" />
        <circle cx="11" cy="10" r="0.4" fill="currentColor" opacity="0.8" />
        <circle cx="7" cy="15" r="0.4" fill="currentColor" opacity="0.8" />
        <circle cx="11" cy="15" r="0.4" fill="currentColor" opacity="0.8" />
        
        <circle cx="13" cy="10" r="0.4" fill="currentColor" opacity="0.7" />
        <circle cx="17" cy="10" r="0.4" fill="currentColor" opacity="0.7" />
        <circle cx="13" cy="15" r="0.4" fill="currentColor" opacity="0.7" />
        <circle cx="17" cy="15" r="0.4" fill="currentColor" opacity="0.7" />
        
        <circle cx="17.5" cy="16.5" r="0.5" fill="white" opacity="0.6" />
      </svg>
    ),
    
    PayoutSettings: ({ size = 24, className = "", ...props }) => (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        {...props}
      >
        <defs>
          <linearGradient id="payoutGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
          </linearGradient>
        </defs>
        
        {/* Credit card */}
        <rect x="2" y="5" width="20" height="14" rx="2" />
        <rect x="2" y="5" width="20" height="14" rx="2" fill="currentColor" opacity="0.05" />
        <path d="M2 10h20" />
        
        {/* Chip */}
        <rect x="6" y="14" width="4" height="3" rx="1" fill="url(#payoutGradient)" opacity="0.7" />
        
        {/* Dollar sign */}
        <path d="M15 13v3" strokeWidth="1" opacity="0.9" />
        <path d="M16 14h-1.5a1 1 0 0 0 0 2h1a1 1 0 0 1 0 2H14" strokeWidth="1" />
        <path d="M16 14h-1.5a1 1 0 0 0 0 2h1a1 1 0 0 1 0 2H14" fill="url(#payoutGradient)" opacity="0.2" />
        
        {/* Settings gear */}
        <circle cx="18" cy="8" r="1" fill="currentColor" opacity="0.6" />
        <path d="M18 7v-.5" strokeWidth="0.5" opacity="0.7" />
        <path d="M18 9v-.5" strokeWidth="0.5" opacity="0.7" />
        <path d="M17 8h.5" strokeWidth="0.5" opacity="0.7" />
        <path d="M19 8h-.5" strokeWidth="0.5" opacity="0.7" />
        
        {/* Dimensional accents */}
        <circle cx="2" cy="5" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="22" cy="5" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="2" cy="19" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="22" cy="19" r="0.5" fill="currentColor" opacity="0.8" />
        
        <circle cx="2" cy="10" r="0.4" fill="currentColor" opacity="0.7" />
        <circle cx="22" cy="10" r="0.4" fill="currentColor" opacity="0.7" />
        
        <circle cx="6" cy="14" r="0.3" fill="currentColor" opacity="0.8" />
        <circle cx="10" cy="14" r="0.3" fill="currentColor" opacity="0.8" />
        <circle cx="6" cy="17" r="0.3" fill="currentColor" opacity="0.8" />
        <circle cx="10" cy="17" r="0.3" fill="currentColor" opacity="0.8" />
        
        <circle cx="15" cy="13" r="0.3" fill="currentColor" opacity="0.8" />
        <circle cx="15" cy="16" r="0.3" fill="currentColor" opacity="0.8" />
      </svg>
    ),
    
    PrivacyVisibility: ({ size = 24, className = "", ...props }) => (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        {...props}
      >
        <defs>
          <linearGradient id="privacyGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
          </linearGradient>
        </defs>
        
        {/* Shield */}
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" fill="url(#privacyGradient)" opacity="0.1" />
        
        {/* Eye */}
        <path d="M12 11c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3z" fill="currentColor" opacity="0.2" />
        <path d="M12 11c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3z" />
        <circle cx="12" cy="14" r="1.5" />
        
        {/* Eye rays/visibility */}
        <path d="M12 9v1" strokeWidth="0.75" opacity="0.8" />
        <path d="M12 18v1" strokeWidth="0.75" opacity="0.8" />
        <path d="M8 11l-1 1" strokeWidth="0.75" opacity="0.8" />
        <path d="M16 17l1-1" strokeWidth="0.75" opacity="0.8" />
        <path d="M16 11l1 1" strokeWidth="0.75" opacity="0.8" />
        <path d="M8 17l-1-1" strokeWidth="0.75" opacity="0.8" />
        
        {/* Dimensional accents */}
        <circle cx="12" cy="2" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="12" cy="22" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="4" cy="5" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="20" cy="5" r="0.5" fill="currentColor" opacity="0.8" />
        
        <circle cx="12" cy="14" r="0.5" fill="white" opacity="0.8" />
        <circle cx="12" cy="9" r="0.3" fill="currentColor" opacity="0.8" />
        <circle cx="12" cy="19" r="0.3" fill="currentColor" opacity="0.8" />
        <circle cx="7" cy="12" r="0.3" fill="currentColor" opacity="0.8" />
        <circle cx="17" cy="16" r="0.3" fill="currentColor" opacity="0.8" />
        <circle cx="17" cy="12" r="0.3" fill="currentColor" opacity="0.8" />
        <circle cx="7" cy="16" r="0.3" fill="currentColor" opacity="0.8" />
      </svg>
    ),

    Checkmark: ({ size = 24, className = "", ...props }) => (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        {...props}
      >
        <defs>
          <linearGradient id="checkGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
          </linearGradient>
        </defs>
        <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.05" />
        
        {/* Checkmark path */}
        <path d="M6 12l4 4 8-8" strokeWidth="2" />
        <path d="M6 12l4 4 8-8" fill="url(#checkGradient)" opacity="0.1" />
        
        {/* Dimensional accents */}
        <circle cx="6" cy="12" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="10" cy="16" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="18" cy="8" r="0.5" fill="currentColor" opacity="0.8" />
        
        {/* Energy flow */}
        <path d="M8 14l-1.5 1.5" strokeWidth="0.5" opacity="0.3" />
        <path d="M14 10l2-2" strokeWidth="0.5" opacity="0.3" />
        <path d="M7 10l2 2" strokeWidth="0.5" opacity="0.3" />
        <path d="M12 12l3-3" strokeWidth="0.5" opacity="0.3" />
        
        {/* Center point */}
        <circle cx="12" cy="12" r="0.8" fill="currentColor" opacity="0.4" />
      </svg>
    ),

    // Color Palette Icon - Artist palette with paint spots and creative energy
ColorPaletteIcon : ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <defs>
      <linearGradient id="paletteGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
      </linearGradient>
    </defs>
    
    {/* Main palette shape */}
    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c1.19 0 1.42-.83 1.42-1.64 0-.43-.13-.86-.38-1.24-.22-.33-.23-.66.08-.9.42-.33 1.06-.52 1.71-.52H17c3.3 0 6-2.7 6-6 0-5.9-5.5-10-11-10z" />
    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c1.19 0 1.42-.83 1.42-1.64 0-.43-.13-.86-.38-1.24-.22-.33-.23-.66.08-.9.42-.33 1.06-.52 1.71-.52H17c3.3 0 6-2.7 6-6 0-5.9-5.5-10-11-10z" fill="url(#paletteGrad)" opacity="0.1" />
    
    {/* Paint spots */}
    <circle cx="6.5" cy="12" r="1.5" />
    <circle cx="6.5" cy="12" r="1.5" fill="currentColor" opacity="0.2" />
    <circle cx="6.5" cy="12" r="0.7" fill="currentColor" opacity="0.8" />
    
    <circle cx="9.5" cy="7" r="1.5" />
    <circle cx="9.5" cy="7" r="1.5" fill="currentColor" opacity="0.2" />
    <circle cx="9.5" cy="7" r="0.7" fill="currentColor" opacity="0.7" />
    
    <circle cx="15" cy="7" r="1.5" />
    <circle cx="15" cy="7" r="1.5" fill="currentColor" opacity="0.2" />
    <circle cx="15" cy="7" r="0.7" fill="currentColor" opacity="0.6" />
    
    <circle cx="17.5" cy="12" r="1.5" />
    <circle cx="17.5" cy="12" r="1.5" fill="currentColor" opacity="0.2" />
    <circle cx="17.5" cy="12" r="0.7" fill="currentColor" opacity="0.9" />
    
    {/* Creative energy sparks */}
    <circle cx="12" cy="4" r="0.3" fill="currentColor" opacity="0.6" />
    <circle cx="8" cy="10" r="0.3" fill="currentColor" opacity="0.5" />
    <circle cx="13" cy="11" r="0.3" fill="currentColor" opacity="0.6" />
    <circle cx="16" cy="15" r="0.3" fill="currentColor" opacity="0.5" />
    
    {/* Artistic waves */}
    <path d="M20 8c1 0 1 2 0 2" strokeWidth="0.8" opacity="0.5" />
    <path d="M21 6c1.5 0 1.5 3 0 3" strokeWidth="0.8" opacity="0.4" />
    <path d="M22 4c2 0 2 4 0 4" strokeWidth="0.6" opacity="0.3" />
    
    {/* Thumb hole accent */}
    <circle cx="14.5" cy="17.5" r="0.5" fill="currentColor" opacity="0.8" />
  </svg>
),
    
    CheckmarkCircle: ({ size = 24, className = "", ...props }) => (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        {...props}
      >
        <defs>
          <radialGradient id="checkCircleGradient" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.2" />
            <stop offset="70%" stopColor="currentColor" stopOpacity="0.1" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0.05" />
          </radialGradient>
        </defs>
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
        <circle cx="12" cy="12" r="10" fill="url(#checkCircleGradient)" />
        
        {/* Checkmark path */}
        <path d="M8 12l3 3 5-5" strokeWidth="1.5" />
        
        {/* Dimensional accents */}
        <circle cx="8" cy="12" r="0.4" fill="currentColor" opacity="0.8" />
        <circle cx="11" cy="15" r="0.4" fill="currentColor" opacity="0.8" />
        <circle cx="16" cy="10" r="0.4" fill="currentColor" opacity="0.8" />
        
        {/* Energy fields */}
        <path d="M17 7l1.5-1.5" strokeWidth="0.5" opacity="0.3" />
        <path d="M5.5 18.5L7 17" strokeWidth="0.5" opacity="0.3" />
        <path d="M7 7L5.5 5.5" strokeWidth="0.5" opacity="0.3" />
        <path d="M18.5 18.5L17 17" strokeWidth="0.5" opacity="0.3" />
        
        {/* Center point */}
        <circle cx="12" cy="12" r="0.8" fill="currentColor" opacity="0.3" />
      </svg>
    ),

    Verified: ({ size = 24, className = "", ...props }) => (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        {...props}
      >
        <defs>
          <linearGradient id="verifiedGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
          </linearGradient>
        </defs>
        
        {/* Shield/badge shape */}
        <path d="M12 22c4-2 8-5 8-10V5l-8-3-8 3v7c0 5 4 8 8 10z" />
        <path d="M12 22c4-2 8-5 8-10V5l-8-3-8 3v7c0 5 4 8 8 10z" fill="url(#verifiedGradient)" opacity="0.1" />
        
        {/* Checkmark */}
        <path d="M7.5 12.5l3 3 6-6" strokeWidth="1.8" />
        
        {/* Energy pulse rays */}
        <path d="M12 3v1" strokeWidth="0.7" opacity="0.6" />
        <path d="M12 20v1" strokeWidth="0.7" opacity="0.6" />
        <path d="M5 6l1 1" strokeWidth="0.7" opacity="0.6" />
        <path d="M18 18l1 1" strokeWidth="0.7" opacity="0.6" />
        <path d="M19 6l-1 1" strokeWidth="0.7" opacity="0.6" />
        <path d="M6 18l-1 1" strokeWidth="0.7" opacity="0.6" />
        
        {/* Dimensional accents */}
        <circle cx="12" cy="2" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="12" cy="22" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="4" cy="5" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="20" cy="5" r="0.5" fill="currentColor" opacity="0.8" />
        
        <circle cx="7.5" cy="12.5" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="10.5" cy="15.5" r="0.5" fill="currentColor" opacity="0.8" />
        <circle cx="16.5" cy="9.5" r="0.5" fill="currentColor" opacity="0.8" />
        
        <circle cx="12" cy="12" r="1" fill="currentColor" opacity="0.2" />
      </svg>
    ),
    
    VerifiedBadge: ({ size = 24, className = "", ...props }) => (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        {...props}
      >
        <defs>
          <radialGradient id="verifiedBadgeGradient" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.2" />
            <stop offset="70%" stopColor="currentColor" stopOpacity="0.1" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0.05" />
          </radialGradient>
        </defs>
        
        {/* Octagonal badge shape */}
        <path d="M16 4h-8L4 8v8l4 4h8l4-4V8z" />
        <path d="M16 4h-8L4 8v8l4 4h8l4-4V8z" fill="url(#verifiedBadgeGradient)" />
        
        {/* Checkmark */}
        <path d="M8 12l3 3 5-5" strokeWidth="1.5" />
        
        {/* Energy pulse rays */}
        <path d="M12 2v1" strokeWidth="0.5" opacity="0.5" />
        <path d="M12 21v1" strokeWidth="0.5" opacity="0.5" />
        <path d="M2 12h1" strokeWidth="0.5" opacity="0.5" />
        <path d="M21 12h1" strokeWidth="0.5" opacity="0.5" />
        
        <path d="M4 4l1 1" strokeWidth="0.5" opacity="0.5" />
        <path d="M19 19l1 1" strokeWidth="0.5" opacity="0.5" />
        <path d="M4 20l1-1" strokeWidth="0.5" opacity="0.5" />
        <path d="M19 5l1-1" strokeWidth="0.5" opacity="0.5" />
        
        {/* Dimensional accents */}
        <circle cx="8" cy="4" r="0.4" fill="currentColor" opacity="0.8" />
        <circle cx="16" cy="4" r="0.4" fill="currentColor" opacity="0.8" />
        <circle cx="4" cy="8" r="0.4" fill="currentColor" opacity="0.8" />
        <circle cx="4" cy="16" r="0.4" fill="currentColor" opacity="0.8" />
        <circle cx="8" cy="20" r="0.4" fill="currentColor" opacity="0.8" />
        <circle cx="16" cy="20" r="0.4" fill="currentColor" opacity="0.8" />
        <circle cx="20" cy="16" r="0.4" fill="currentColor" opacity="0.8" />
        <circle cx="20" cy="8" r="0.4" fill="currentColor" opacity="0.8" />
        
        <circle cx="8" cy="12" r="0.4" fill="currentColor" opacity="0.8" />
        <circle cx="11" cy="15" r="0.4" fill="currentColor" opacity="0.8" />
        <circle cx="16" cy="10" r="0.4" fill="currentColor" opacity="0.8" />
        
        <circle cx="12" cy="12" r="0.8" fill="currentColor" opacity="0.3" />
      </svg>
    ),

    Reply: ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    {...props}
  >
    {/* Background circle */}
    <circle cx="12" cy="12" r="8.5" opacity="0.15"/>
    
    {/* Circular arrow as one continuous path with arrow head */}
    <path fillRule="evenodd" clipRule="evenodd" d="M12 5.75c-3.452 0-6.25 2.798-6.25 6.25S8.548 18.25 12 18.25c1.657 0 3.157-.645 4.273-1.697a1.25 1.25 0 1 1 1.704 1.83A8.73 8.73 0 0 1 12 20.75c-4.832 0-8.75-3.918-8.75-8.75S7.168 3.25 12 3.25c4.526 0 8.245 3.435 8.705 7.841l1.086-1.086a1.25 1.25 0 0 1 1.768 1.768l-3 3a1.25 1.25 0 0 1-1.768 0l-3-3a1.25 1.25 0 0 1 1.768-1.768l.691.691C17.859 7.634 15.179 5.75 12 5.75z"/>
    
    {/* Energy particles flowing along the circle */}
    <circle cx="9" cy="6.5" r="1" opacity="0.6"/>
    <circle cx="6.5" cy="9" r="0.8" opacity="0.5"/>
    <circle cx="5" cy="12" r="1.2" opacity="0.7"/>
    <circle cx="6.5" cy="15" r="0.8" opacity="0.5"/>
    <circle cx="9" cy="17.5" r="1" opacity="0.6"/>
    <circle cx="12" cy="19" r="0.8" opacity="0.4"/>
    <circle cx="15" cy="17.5" r="0.7" opacity="0.3"/>
    
    {/* Flow lines connecting particles */}
    <path d="M9 6.5l-1 1" strokeWidth="0.5" stroke="currentColor" opacity="0.3" fill="none"/>
    <path d="M6.5 9l-0.8 1.5" strokeWidth="0.5" stroke="currentColor" opacity="0.4" fill="none"/>
    <path d="M6.5 15l1 1.2" strokeWidth="0.5" stroke="currentColor" opacity="0.3" fill="none"/>
    <path d="M12 19l1.5-0.8" strokeWidth="0.5" stroke="currentColor" opacity="0.2" fill="none"/>
    
    {/* Sparkles for extra energy */}
    <path d="M7 8l0.3-0.8 0.3 0.8 0.8 0.3-0.8 0.3-0.3 0.8-0.3-0.8-0.8-0.3z" opacity="0.4"/>
    <path d="M8 16l0.2-0.6 0.2 0.6 0.6 0.2-0.6 0.2-0.2 0.6-0.2-0.6-0.6-0.2z" opacity="0.3"/>
    <path d="M14 18l0.2-0.5 0.2 0.5 0.5 0.2-0.5 0.2-0.2 0.5-0.2-0.5-0.5-0.2z" opacity="0.25"/>
  </svg>
),
    
    ReplyAll: (
      <svg
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <defs>
          <linearGradient id="replyAllGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
          </linearGradient>
        </defs>
        
        {/* Background */}
        <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.03" />
        
        {/* Primary reply arrow */}
        <path d="M8 10l4 4 4-4" />
        <path d="M12 14V6" />
        
        {/* Secondary reply arrow */}
        <path d="M5 8l4 4" opacity="0.7" />
        <path d="M9 12V4" opacity="0.7" />
        
        <path d="M8 10l4 4 4-4" fill="url(#replyAllGradient)" opacity="0.1" />
        <path d="M5 8l4 4" fill="url(#replyAllGradient)" opacity="0.05" />
        
        {/* Message bubble reference */}
        <path d="M19 16a7 7 0 0 1-14 0" strokeWidth="0.8" opacity="0.4" />
        <path d="M16 19l2-1 1 2" strokeWidth="0.8" opacity="0.5" />
        <path d="M8 19l-2-1-1 2" strokeWidth="0.8" opacity="0.5" />
        
        {/* Dimensional accents */}
        <circle cx="8" cy="10" r="0.4" fill="currentColor" opacity="0.8" />
        <circle cx="12" cy="14" r="0.4" fill="currentColor" opacity="0.8" />
        <circle cx="16" cy="10" r="0.4" fill="currentColor" opacity="0.8" />
        <circle cx="12" cy="6" r="0.4" fill="currentColor" opacity="0.8" />
        
        <circle cx="5" cy="8" r="0.4" fill="currentColor" opacity="0.7" />
        <circle cx="9" cy="12" r="0.4" fill="currentColor" opacity="0.7" />
        <circle cx="9" cy="4" r="0.4" fill="currentColor" opacity="0.7" />
        
        {/* Energy flow */}
        <path d="M10 12l-1 1" strokeWidth="0.5" opacity="0.3" />
        <path d="M14 12l1 1" strokeWidth="0.5" opacity="0.3" />
        <path d="M7 10l-1 1" strokeWidth="0.5" opacity="0.3" />
        <path d="M12 9v2" strokeWidth="0.5" opacity="0.4" />
        <path d="M9 8v2" strokeWidth="0.5" opacity="0.4" />
        
        {/* Center point */}
        <circle cx="12" cy="10" r="0.8" fill="currentColor" opacity="0.3" />
      </svg>
    ),
    
    ForwardMessage: (
      <svg
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <defs>
          <linearGradient id="forwardGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
          </linearGradient>
        </defs>
        
        {/* Background */}
        <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.03" />
        
        {/* Forward arrow */}
        <path d="M8 10l4-4 4 4" />
        <path d="M12 6v10" />
        
        <path d="M8 10l4-4 4 4" fill="url(#forwardGradient)" opacity="0.1" />
        
        {/* Message reference */}
        <path d="M19 16a7 7 0 0 1-14 0" strokeWidth="0.8" opacity="0.4" />
        <path d="M16 19l2-1 1 2" strokeWidth="0.8" opacity="0.5" />
        <path d="M8 19l-2-1-1 2" strokeWidth="0.8" opacity="0.5" />
        
        {/* Dimensional accents */}
        <circle cx="8" cy="10" r="0.4" fill="currentColor" opacity="0.8" />
        <circle cx="12" cy="6" r="0.4" fill="currentColor" opacity="0.8" />
        <circle cx="16" cy="10" r="0.4" fill="currentColor" opacity="0.8" />
        <circle cx="12" cy="16" r="0.4" fill="currentColor" opacity="0.8" />
        
        {/* Energy flow */}
        <path d="M10 8l-1-1" strokeWidth="0.5" opacity="0.3" />
        <path d="M14 8l1-1" strokeWidth="0.5" opacity="0.3" />
        <path d="M12 9v2" strokeWidth="0.5" opacity="0.4" />
        <path d="M12 13v1" strokeWidth="0.5" opacity="0.3" />
        
        {/* Center point */}
        <circle cx="12" cy="10" r="0.8" fill="currentColor" opacity="0.3" />
      </svg>
    ),

    // Message All - Broadcasting to multiple recipients with energy field
MessageAll: ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <defs>
      <linearGradient id="messageAllGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
      </linearGradient>
    </defs>
    
    {/* Main message bubble */}
    <path d="M3 6C3 4.89543 3.89543 4 5 4H19C20.1046 4 21 4.89543 21 6V14C21 15.1046 20.1046 16 19 16H7L3 20V6Z" />
    <path d="M3 6C3 4.89543 3.89543 4 5 4H19C20.1046 4 21 4.89543 21 6V14C21 15.1046 20.1046 16 19 16H7L3 20V6Z" fill="url(#messageAllGrad)" opacity="0.1" />
    
    {/* Broadcasting dots showing "all recipients" */}
    <circle cx="8" cy="10" r="1" fill="currentColor" opacity="0.8" />
    <circle cx="12" cy="10" r="1" fill="currentColor" opacity="0.9" />
    <circle cx="16" cy="10" r="1" fill="currentColor" opacity="0.8" />
    
    {/* Broadcasting energy waves */}
    <path d="M21 6c1-1 2-1 3 0" strokeWidth="1" opacity="0.6" />
    <path d="M21 10c1.5 0 2.5 0 3.5 1" strokeWidth="1" opacity="0.4" />
    <path d="M21 14c1-1 2-1 3 0" strokeWidth="1" opacity="0.6" />
    
    {/* Multiple recipient indicators */}
    <circle cx="22" cy="6" r="0.5" fill="currentColor" opacity="0.7" />
    <circle cx="23" cy="10" r="0.5" fill="currentColor" opacity="0.5" />
    <circle cx="22" cy="14" r="0.5" fill="currentColor" opacity="0.7" />
    
    {/* Dimensional accents */}
    <circle cx="3" cy="6" r="0.5" fill="currentColor" opacity="0.8" />
    <circle cx="21" cy="6" r="0.5" fill="currentColor" opacity="0.8" />
    <circle cx="3" cy="20" r="0.5" fill="currentColor" opacity="0.8" />
    <circle cx="7" cy="16" r="0.5" fill="currentColor" opacity="0.8" />
    
    {/* Message content lines */}
    <path d="M7 7h8" strokeWidth="1" opacity="0.5" />
    <path d="M7 13h4" strokeWidth="1" opacity="0.3" />
    
    {/* Energy field */}
    <circle cx="12" cy="10" r="4" fill="currentColor" opacity="0.05" />
    <path d="M9 8l6 4" strokeWidth="0.5" opacity="0.3" />
    <path d="M15 8l-6 4" strokeWidth="0.5" opacity="0.3" />
  </svg>
),

// Filter - Data filtering with flow dynamics
Filter: ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <defs>
      <linearGradient id="filterGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
      </linearGradient>
    </defs>
    
    {/* Main funnel shape */}
    <path d="M4 4H20L14 12V19L10 17V12L4 4Z" />
    <path d="M4 4H20L14 12V19L10 17V12L4 4Z" fill="url(#filterGrad)" opacity="0.1" />
    
    {/* Data flow lines showing filtering */}
    <path d="M6 6h12" strokeWidth="1.5" opacity="0.8" />
    <path d="M8 8h8" strokeWidth="1.5" opacity="0.6" />
    <path d="M10 10h4" strokeWidth="1.5" opacity="0.7" />
    
    {/* Filtering particles/dots */}
    <circle cx="7" cy="7" r="0.5" fill="currentColor" opacity="0.8" />
    <circle cx="12" cy="7" r="0.5" fill="currentColor" opacity="0.9" />
    <circle cx="17" cy="7" r="0.5" fill="currentColor" opacity="0.8" />
    
    <circle cx="9" cy="9" r="0.5" fill="currentColor" opacity="0.7" />
    <circle cx="15" cy="9" r="0.5" fill="currentColor" opacity="0.7" />
    
    <circle cx="11" cy="11" r="0.5" fill="currentColor" opacity="0.8" />
    <circle cx="13" cy="11" r="0.5" fill="currentColor" opacity="0.8" />
    
    {/* Output flow */}
    <circle cx="12" cy="15" r="0.6" fill="currentColor" opacity="0.9" />
    <circle cx="12" cy="18" r="0.5" fill="currentColor" opacity="0.8" />
    
    {/* Dimensional accents */}
    <circle cx="4" cy="4" r="0.5" fill="currentColor" opacity="0.8" />
    <circle cx="20" cy="4" r="0.5" fill="currentColor" opacity="0.8" />
    <circle cx="14" cy="12" r="0.5" fill="currentColor" opacity="0.8" />
    <circle cx="10" cy="12" r="0.5" fill="currentColor" opacity="0.8" />
    <circle cx="14" cy="19" r="0.5" fill="currentColor" opacity="0.8" />
    <circle cx="10" cy="17" r="0.5" fill="currentColor" opacity="0.8" />
    
    {/* Energy fields */}
    <path d="M6 6l6 6" strokeWidth="0.5" opacity="0.3" />
    <path d="M18 6l-6 6" strokeWidth="0.5" opacity="0.3" />
    <path d="M12 14v3" strokeWidth="0.5" opacity="0.4" />
    
    {/* Filtering indicator */}
    <circle cx="12" cy="8" r="3" fill="currentColor" opacity="0.05" />
    <path d="M10 6c1 1 3 1 4 0" strokeWidth="0.5" opacity="0.4" />
    <path d="M10 10c1-1 3-1 4 0" strokeWidth="0.5" opacity="0.4" />
  </svg>
),

// OnlineIndicator - Dynamic online status with pulsing energy
OnlineIndicator: ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <defs>
      <linearGradient id="onlineGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#22c55e" stopOpacity="1" />
        <stop offset="100%" stopColor="#16a34a" stopOpacity="1" />
      </linearGradient>
    </defs>
    
    {/* Pulsing energy rings */}
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="0.5" opacity="0.2" />
    <circle cx="12" cy="12" r="7" stroke="currentColor" strokeWidth="0.75" opacity="0.4" />
    <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1" opacity="0.6" />
    
    {/* Main online indicator */}
    <circle cx="12" cy="12" r="3" fill="url(#onlineGrad)" strokeWidth="1.5" />
    <circle cx="12" cy="12" r="3" fill="currentColor" opacity="0.1" />
    
    {/* Energy pulse points */}
    <circle cx="12" cy="12" r="1.5" fill="white" opacity="0.4" />
    <circle cx="10" cy="10" r="0.5" fill="white" opacity="0.7" />
    
    {/* Dimensional accents */}
    <circle cx="12" cy="2" r="0.5" fill="currentColor" opacity="0.5" />
    <circle cx="12" cy="22" r="0.5" fill="currentColor" opacity="0.5" />
    <circle cx="2" cy="12" r="0.5" fill="currentColor" opacity="0.5" />
    <circle cx="22" cy="12" r="0.5" fill="currentColor" opacity="0.5" />
    
    {/* Energy flow lines */}
    <path d="M17 12h2" strokeWidth="0.5" opacity="0.4" />
    <path d="M5 12h2" strokeWidth="0.5" opacity="0.4" />
    <path d="M12 17v2" strokeWidth="0.5" opacity="0.4" />
    <path d="M12 5v2" strokeWidth="0.5" opacity="0.4" />
    
    {/* Corner energy dots */}
    <circle cx="16.5" cy="7.5" r="0.4" fill="currentColor" opacity="0.6" />
    <circle cx="7.5" cy="7.5" r="0.4" fill="currentColor" opacity="0.6" />
    <circle cx="16.5" cy="16.5" r="0.4" fill="currentColor" opacity="0.6" />
    <circle cx="7.5" cy="16.5" r="0.4" fill="currentColor" opacity="0.6" />
    
    {/* Active connection lines */}
    <path d="M15 9c1-1 2-1 3 0" strokeWidth="0.5" opacity="0.5" />
    <path d="M6 15c1-1 2-1 3 0" strokeWidth="0.5" opacity="0.5" />
  </svg>
),

// OfflineIndicator - Disconnected status with muted energy
OfflineIndicator: ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <defs>
      <linearGradient id="offlineGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#94a3b8" stopOpacity="1" />
        <stop offset="100%" stopColor="#64748b" stopOpacity="1" />
      </linearGradient>
    </defs>
    
    {/* Faded energy rings */}
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="0.5" opacity="0.1" />
    <circle cx="12" cy="12" r="7" stroke="currentColor" strokeWidth="0.75" opacity="0.2" />
    <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1" opacity="0.3" />
    
    {/* Main offline indicator */}
    <circle cx="12" cy="12" r="3" fill="url(#offlineGrad)" strokeWidth="1.5" />
    <circle cx="12" cy="12" r="3" fill="currentColor" opacity="0.05" />
    
    {/* Disconnection X */}
    <path d="M10 10l4 4" strokeWidth="1.5" stroke="white" opacity="0.8" />
    <path d="M14 10l-4 4" strokeWidth="1.5" stroke="white" opacity="0.8" />
    
    {/* Muted center */}
    <circle cx="12" cy="12" r="1" fill="white" opacity="0.2" />
    
    {/* Dimensional accents (muted) */}
    <circle cx="12" cy="2" r="0.5" fill="currentColor" opacity="0.3" />
    <circle cx="12" cy="22" r="0.5" fill="currentColor" opacity="0.3" />
    <circle cx="2" cy="12" r="0.5" fill="currentColor" opacity="0.3" />
    <circle cx="22" cy="12" r="0.5" fill="currentColor" opacity="0.3" />
    
    {/* Broken connection lines */}
    <path d="M17 12h1" strokeWidth="0.5" opacity="0.2" />
    <path d="M6 12h1" strokeWidth="0.5" opacity="0.2" />
    <path d="M12 17v1" strokeWidth="0.5" opacity="0.2" />
    <path d="M12 6v1" strokeWidth="0.5" opacity="0.2" />
    
    {/* Corner energy dots (muted) */}
    <circle cx="16.5" cy="7.5" r="0.4" fill="currentColor" opacity="0.3" />
    <circle cx="7.5" cy="7.5" r="0.4" fill="currentColor" opacity="0.3" />
    <circle cx="16.5" cy="16.5" r="0.4" fill="currentColor" opacity="0.3" />
    <circle cx="7.5" cy="16.5" r="0.4" fill="currentColor" opacity="0.3" />
    
    {/* Broken connection indicators */}
    <path d="M15 9c0.5-0.5 1-0.5 1.5 0" strokeWidth="0.5" opacity="0.3" />
    <path d="M7.5 15c0.5-0.5 1-0.5 1.5 0" strokeWidth="0.5" opacity="0.3" />
    
    {/* Disconnection emphasis */}
    <circle cx="10" cy="10" r="0.3" fill="white" opacity="0.6" />
    <circle cx="14" cy="10" r="0.3" fill="white" opacity="0.6" />
    <circle cx="10" cy="14" r="0.3" fill="white" opacity="0.6" />
    <circle cx="14" cy="14" r="0.3" fill="white" opacity="0.6" />
  </svg>
),

// Upload - Dynamic upload icon with energy flow
Upload: ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <defs>
      <linearGradient id="uploadGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
      </linearGradient>
    </defs>
    
    {/* Main upload container */}
    <rect x="3" y="8" width="18" height="12" rx="2" fill="currentColor" opacity="0.05" />
    <rect x="3" y="8" width="18" height="12" rx="2" strokeWidth="1.5" />
    
    {/* Upload arrow */}
    <path d="M12 15V4" strokeWidth="2" />
    <path d="M8 8l4-4 4 4" strokeWidth="2" />
    
    {/* Dimensional accents */}
    <circle cx="12" cy="4" r="1" fill="currentColor" opacity="0.8" />
    <circle cx="8" cy="8" r="0.5" fill="currentColor" opacity="0.6" />
    <circle cx="16" cy="8" r="0.5" fill="currentColor" opacity="0.6" />
    
    {/* Energy flow indicators */}
    <path d="M6 12c2-1 4-1 6 0s4 1 6 0" strokeWidth="0.8" opacity="0.4" />
    <path d="M6 16c2 1 4 1 6 0s4-1 6 0" strokeWidth="0.8" opacity="0.4" />
    
    {/* Upload progress indicators */}
    <circle cx="7" cy="14" r="0.3" fill="currentColor" opacity="0.7" />
    <circle cx="9" cy="14" r="0.3" fill="currentColor" opacity="0.5" />
    <circle cx="11" cy="14" r="0.3" fill="currentColor" opacity="0.3" />
    
    {/* Central emphasis */}
    <circle cx="12" cy="12" r="1.5" fill="url(#uploadGradient)" opacity="0.2" />
    <circle cx="12" cy="12" r="0.8" fill="currentColor" opacity="0.1" />
  </svg>
),

// UserTag - Dynamic tag with user profile integration
UserTag: ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <defs>
      <linearGradient id="userTagGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
      </linearGradient>
      <radialGradient id="userTagEnergyField" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0.1" />
        <stop offset="70%" stopColor="currentColor" stopOpacity="0.05" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0.02" />
      </radialGradient>
    </defs>
    
    {/* Energy field background */}
    <circle cx="12" cy="12" r="10" fill="url(#userTagEnergyField)" />
    <circle cx="12" cy="12" r="7" stroke="currentColor" strokeWidth="0.5" opacity="0.15" />
    <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="0.75" opacity="0.25" />
    
    {/* Main tag shape */}
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" fill="url(#userTagGrad)" opacity="0.1" />
    
    {/* User profile circle in tag */}
    <circle cx="7" cy="7" r="2" />
    <circle cx="7" cy="7" r="2" fill="currentColor" opacity="0.15" />
    
    {/* User avatar details */}
    <circle cx="7" cy="6.5" r="0.8" fill="currentColor" opacity="0.6" />
    <path d="M5.5 8.5c0-1 0.7-1.5 1.5-1.5s1.5 0.5 1.5 1.5" strokeWidth="1" fill="currentColor" opacity="0.4" />
    
    {/* Tag hole */}
    <circle cx="7" cy="7" r="0.8" stroke="currentColor" strokeWidth="1" fill="none" />
    <circle cx="7" cy="7" r="0.3" fill="currentColor" opacity="0.8" />
    
    {/* VIP dimensional accents */}
    <circle cx="2" cy="2" r="0.5" fill="currentColor" opacity="0.8" />
    <circle cx="12" cy="2" r="0.5" fill="currentColor" opacity="0.8" />
    <circle cx="20.59" cy="10.59" r="0.8" fill="currentColor" opacity="0.8" />
    <circle cx="13.41" cy="20.59" r="0.8" fill="currentColor" opacity="0.8" />
    
    {/* Energy flow around tag */}
    <path d="M3 5c1-1 2-1 3 0" strokeWidth="0.5" opacity="0.3" />
    <path d="M15 3c1 1 1 2 0 3" strokeWidth="0.5" opacity="0.3" />
    <path d="M19 15c-1 1-2 1-3 0" strokeWidth="0.5" opacity="0.3" />
    <path d="M5 19c-1-1-1-2 0-3" strokeWidth="0.5" opacity="0.3" />
    
    {/* Tag connection points */}
    <circle cx="10" cy="10" r="0.5" fill="currentColor" opacity="0.6" />
    <circle cx="14" cy="14" r="0.5" fill="currentColor" opacity="0.6" />
    <circle cx="16" cy="16" r="0.5" fill="currentColor" opacity="0.6" />
    
    {/* String/connection line */}
    <path d="M12 12l6 6" strokeWidth="0.5" opacity="0.4" />
    <path d="M14 10l4 4" strokeWidth="0.5" opacity="0.3" />
    
    {/* Energy field extensions */}
    <circle cx="12" cy="1" r="0.4" fill="currentColor" opacity="0.5" />
    <circle cx="23" cy="12" r="0.4" fill="currentColor" opacity="0.5" />
    <circle cx="1" cy="12" r="0.4" fill="currentColor" opacity="0.5" />
    <circle cx="12" cy="23" r="0.4" fill="currentColor" opacity="0.5" />
    
    {/* Tag shine/highlight */}
    <path d="M4 4h6" strokeWidth="0.5" opacity="0.4" />
    <path d="M4 6h4" strokeWidth="0.5" opacity="0.3" />
    <path d="M15 11l3 3" strokeWidth="0.5" opacity="0.4" />
    
    {/* Corner energy dots */}
    <circle cx="6" cy="3" r="0.3" fill="currentColor" opacity="0.6" />
    <circle cx="9" cy="3" r="0.3" fill="currentColor" opacity="0.6" />
    <circle cx="3" cy="6" r="0.3" fill="currentColor" opacity="0.6" />
    <circle cx="3" cy="9" r="0.3" fill="currentColor" opacity="0.6" />
    
    {/* Tagging indicator */}
    <circle cx="16" cy="8" r="1" fill="currentColor" opacity="0.2" />
    <circle cx="16" cy="8" r="0.5" fill="currentColor" opacity="0.8" />
    <path d="M15.5 7.5l1 1" strokeWidth="0.3" stroke="white" opacity="0.6" />
  </svg>
),

// Circle Outline - Empty circle for unselected checkboxes
CircleOutline: ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <circle cx="12" cy="12" r="10" />
  </svg>
),

// Music - Dynamic music note with sound waves and energy field
Music: ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <defs>
      <linearGradient id="musicGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
      </linearGradient>
      <radialGradient id="musicEnergyField" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0.1" />
        <stop offset="70%" stopColor="currentColor" stopOpacity="0.05" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0.02" />
      </radialGradient>
    </defs>
    
    {/* Energy field background */}
    <circle cx="12" cy="12" r="10" fill="url(#musicEnergyField)" />
    <circle cx="12" cy="12" r="7" stroke="currentColor" strokeWidth="0.5" opacity="0.15" />
    <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="0.75" opacity="0.25" />
    
    {/* Main music note */}
    <path d="M9 18V5l12-2v13" />
    <path d="M9 18V5l12-2v13" fill="url(#musicGrad)" opacity="0.1" />
    <circle cx="6" cy="18" r="3" />
    <circle cx="18" cy="16" r="3" />
    <circle cx="6" cy="18" r="3" fill="currentColor" opacity="0.15" />
    <circle cx="18" cy="16" r="3" fill="currentColor" opacity="0.15" />
    
    {/* Music note details */}
    <circle cx="6" cy="18" r="1.5" fill="currentColor" opacity="0.8" />
    <circle cx="18" cy="16" r="1.5" fill="currentColor" opacity="0.8" />
    <path d="M9 10h12" strokeWidth="1" opacity="0.4" />
    
    {/* Sound waves emanating from notes */}
    <path d="M21 13c1 1 1 3 0 4" strokeWidth="1" opacity="0.6" />
    <path d="M22.5 11c1.5 1.5 1.5 5.5 0 7" strokeWidth="1" opacity="0.4" />
    <path d="M24 9c2 2 2 8 0 10" strokeWidth="0.8" opacity="0.3" />
    
    {/* Energy vibrations */}
    <path d="M3 15c-1 1-1 3 0 4" strokeWidth="1" opacity="0.6" />
    <path d="M1.5 13c-1.5 1.5-1.5 5.5 0 7" strokeWidth="1" opacity="0.4" />
    <path d="M0 11c-2 2-2 8 0 10" strokeWidth="0.8" opacity="0.3" />
    
    {/* VIP dimensional accents */}
    <circle cx="9" cy="5" r="0.8" fill="currentColor" opacity="0.8" />
    <circle cx="21" cy="3" r="0.8" fill="currentColor" opacity="0.8" />
    <circle cx="9" cy="18" r="0.5" fill="currentColor" opacity="0.6" />
    <circle cx="21" cy="16" r="0.5" fill="currentColor" opacity="0.6" />
    
    {/* Musical energy sparkles */}
    <circle cx="15" cy="8" r="0.3" fill="currentColor" opacity="0.7" />
    <circle cx="12" cy="6" r="0.3" fill="currentColor" opacity="0.7" />
    <circle cx="18" cy="12" r="0.3" fill="currentColor" opacity="0.7" />
    <circle cx="6" cy="12" r="0.3" fill="currentColor" opacity="0.7" />
    
    {/* Energy field extensions */}
    <circle cx="12" cy="2" r="0.5" fill="currentColor" opacity="0.5" />
    <circle cx="12" cy="22" r="0.5" fill="currentColor" opacity="0.5" />
    <circle cx="2" cy="12" r="0.5" fill="currentColor" opacity="0.5" />
    <circle cx="22" cy="12" r="0.5" fill="currentColor" opacity="0.5" />
    
    {/* Corner energy dots */}
    <circle cx="7.5" cy="7.5" r="0.4" fill="currentColor" opacity="0.6" />
    <circle cx="16.5" cy="7.5" r="0.4" fill="currentColor" opacity="0.6" />
    <circle cx="7.5" cy="16.5" r="0.4" fill="currentColor" opacity="0.6" />
    <circle cx="16.5" cy="16.5" r="0.4" fill="currentColor" opacity="0.6" />
    
    {/* Musical rhythm indicators */}
    <path d="M12 4v2" strokeWidth="0.5" opacity="0.4" />
    <path d="M15 5v2" strokeWidth="0.5" opacity="0.3" />
    <path d="M18 6v2" strokeWidth="0.5" opacity="0.4" />
  </svg>
),

// Lock - Dynamic lock with security field and protection energy
Lock: ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <defs>
      <linearGradient id="lockGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
      </linearGradient>
      <radialGradient id="lockEnergyField" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0.1" />
        <stop offset="70%" stopColor="currentColor" stopOpacity="0.05" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0.02" />
      </radialGradient>
    </defs>
    
    {/* Energy field background 
    <circle cx="12" cy="12" r="10" fill="url(#lockEnergyField)" />
    <circle cx="12" cy="12" r="7" stroke="currentColor" strokeWidth="0.5" opacity="0.15" />
    <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="0.75" opacity="0.25" />
    */}
    {/* Main lock body */}
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <rect x="5" y="11" width="14" height="10" rx="2" fill="url(#lockGrad)" opacity="0.1" />
    
    {/* Lock shackle */}
    <path d="M8 11V7a4 4 0 1 1 8 0v4" />
    <path d="M8 11V7a4 4 0 1 1 8 0v4" fill="currentColor" opacity="0.05" />
    
    {/* Keyhole */}
    <circle cx="12" cy="16" r="1.5" />
    <circle cx="12" cy="16" r="1.5" fill="currentColor" opacity="0.3" />
    <path d="M12 17.5v1.5" strokeWidth="1" />
    
    {/* Security shield overlay */}
    <circle cx="12" cy="16" r="0.8" fill="currentColor" opacity="0.8" />
    <circle cx="12" cy="16" r="0.3" fill="white" opacity="0.6" />
    
    {/* VIP dimensional accents */}
    <circle cx="5" cy="11" r="0.8" fill="currentColor" opacity="0.8" />
    <circle cx="19" cy="11" r="0.8" fill="currentColor" opacity="0.8" />
    <circle cx="5" cy="21" r="0.8" fill="currentColor" opacity="0.8" />
    <circle cx="19" cy="21" r="0.8" fill="currentColor" opacity="0.8" />
    
    {/* Security energy radiating from lock */}
    <path d="M3 9c1-1 2-1 3 0" strokeWidth="0.5" opacity="0.3" />
    <path d="M18 3c1 1 1 2 0 3" strokeWidth="0.5" opacity="0.3" />
    <path d="M21 15c-1 1-2 1-3 0" strokeWidth="0.5" opacity="0.3" />
    <path d="M6 21c-1-1-1-2 0-3" strokeWidth="0.5" opacity="0.3" />
    
    {/* Protection field indicators */}
    <circle cx="8" cy="7" r="0.5" fill="currentColor" opacity="0.7" />
    <circle cx="16" cy="7" r="0.5" fill="currentColor" opacity="0.7" />
    <circle cx="12" cy="3" r="0.5" fill="currentColor" opacity="0.7" />
    
    {/* Lock mechanism details */}
    <path d="M7 14h2" strokeWidth="0.5" opacity="0.4" />
    <path d="M15 14h2" strokeWidth="0.5" opacity="0.4" />
    <path d="M7 18h2" strokeWidth="0.5" opacity="0.4" />
    <path d="M15 18h2" strokeWidth="0.5" opacity="0.4" />
    
    {/* Energy field extensions */}
    <circle cx="12" cy="1" r="0.4" fill="currentColor" opacity="0.5" />
    <circle cx="12" cy="23" r="0.4" fill="currentColor" opacity="0.5" />
    <circle cx="1" cy="12" r="0.4" fill="currentColor" opacity="0.5" />
    <circle cx="23" cy="12" r="0.4" fill="currentColor" opacity="0.5" />
    
    {/* Security sparkles */}
    <circle cx="9" cy="13" r="0.3" fill="currentColor" opacity="0.6" />
    <circle cx="15" cy="13" r="0.3" fill="currentColor" opacity="0.6" />
    <circle cx="9" cy="19" r="0.3" fill="currentColor" opacity="0.6" />
    <circle cx="15" cy="19" r="0.3" fill="currentColor" opacity="0.6" />
    
    {/* Corner energy dots */}
    <circle cx="7.5" cy="7.5" r="0.4" fill="currentColor" opacity="0.6" />
    <circle cx="16.5" cy="7.5" r="0.4" fill="currentColor" opacity="0.6" />
    <circle cx="7.5" cy="16.5" r="0.4" fill="currentColor" opacity="0.6" />
    <circle cx="16.5" cy="16.5" r="0.4" fill="currentColor" opacity="0.6" />
    
    {/* Protection aura enhancement */}
    <path d="M10 7c0-1 1-2 2-2s2 1 2 2" strokeWidth="0.5" opacity="0.4" />
    <path d="M9 9c0-1.5 1.5-3 3-3s3 1.5 3 3" strokeWidth="0.5" opacity="0.3" />
  </svg>
),

Key : ({ size = 24, className = "", ...props }) => (
  <svg
  width="24"
  height="24"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="1.5"
  stroke-linecap="round"
  stroke-linejoin="round"
  xmlns="http://www.w3.org/2000/svg"
>
  {/* Key head (circular) */}
  <circle cx="8" cy="8" r="5" />
  <circle cx="8" cy="8" r="2" opacity="0.6" />
  
  {/* Key shaft */}
  <path d="M14.5 14.5L21 21" stroke-width="2" />
  <path d="M12 12L14.5 14.5" stroke-width="2" />
  
  {/* Key teeth */}
  <path d="M16 21v-3" stroke-width="1.5" />
  <path d="M19 21v-2" stroke-width="1.5" />
  
  {/* Dimensional accents */}
  <circle cx="8" cy="8" r="0.8" fill="currentColor" opacity="0.8" />
  <circle cx="21" cy="21" r="1" fill="currentColor" opacity="0.6" />
  <circle cx="16" cy="18" r="0.5" fill="currentColor" opacity="0.7" />
  <circle cx="19" cy="19" r="0.5" fill="currentColor" opacity="0.7" />
  
  {/* Energy flow */}
  <path d="M3 8c1-1 2-1 3 0" stroke-width="1" opacity="0.4" />
  <path d="M8 3c1 1 1 2 0 3" stroke-width="0.5" opacity="0.3" />
  
  {/* Field extensions */}
  <circle cx="3" cy="8" r="0.5" fill="currentColor" opacity="0.5" />
  <circle cx="8" cy="3" r="0.5" fill="currentColor" opacity="0.5" />
</svg>
),

FileIcon : ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <defs>
      <linearGradient id="fileGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
      </linearGradient>
      <radialGradient id="fileEnergyField" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0.1" />
        <stop offset="70%" stopColor="currentColor" stopOpacity="0.05" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0.02" />
      </radialGradient>
    </defs>
    
    {/* Energy field background */}
    <circle cx="12" cy="12" r="10" fill="url(#fileEnergyField)" />
    <circle cx="12" cy="12" r="7" stroke="currentColor" strokeWidth="0.5" opacity="0.15" />
    <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="0.75" opacity="0.25" />
    
    {/* Main file shape with folded corner */}
    <path d="M6 2h8l4 4v12a2 2 0 01-2 2H8a2 2 0 01-2-2V4a2 2 0 012-2z" />
    <path d="M6 2h8l4 4v12a2 2 0 01-2 2H8a2 2 0 01-2-2V4a2 2 0 012-2z" fill="url(#fileGrad)" opacity="0.1" />
    
    {/* Folded corner detail */}
    <path d="M14 2v4h4" />
    <path d="M14 2v4h4" fill="currentColor" opacity="0.15" />
    
    {/* Content lines with energy */}
    <path d="M10 9h4" strokeWidth="1" opacity="0.6" />
    <path d="M10 12h4" strokeWidth="1" opacity="0.5" />
    <path d="M10 15h4" strokeWidth="1" opacity="0.4" />
    
    {/* Media indicators overlay */}
    <circle cx="9" cy="9" r="0.5" fill="currentColor" opacity="0.8" />
    <circle cx="9" cy="12" r="0.5" fill="currentColor" opacity="0.7" />
    <circle cx="9" cy="15" r="0.5" fill="currentColor" opacity="0.6" />
    
    {/* Data flow visualization */}
    <path d="M20 8c1 0 2 1 2 2s-1 2-2 2" strokeWidth="1" opacity="0.6" />
    <path d="M21 6c2 0 3 2 3 4s-1 4-3 4" strokeWidth="1" opacity="0.4" />
    <path d="M22 4c3 0 4 3 4 6s-1 6-4 6" strokeWidth="0.8" opacity="0.3" />
    
    {/* Energy emanating from left side */}
    <path d="M4 8c-1 0-2 1-2 2s1 2 2 2" strokeWidth="1" opacity="0.6" />
    <path d="M3 6c-2 0-3 2-3 4s1 4 3 4" strokeWidth="1" opacity="0.4" />
    <path d="M2 4c-3 0-4 3-4 6s1 6 4 6" strokeWidth="0.8" opacity="0.3" />
    
    {/* Corner accent dots */}
    <circle cx="14" cy="2" r="0.8" fill="currentColor" opacity="0.8" />
    <circle cx="18" cy="6" r="0.8" fill="currentColor" opacity="0.8" />
    
    {/* File type indicators - premium content */}
    <circle cx="16" cy="18" r="1.5" fill="currentColor" opacity="0.15" />
    <circle cx="16" cy="18" r="0.8" fill="currentColor" opacity="0.8" />
    
    {/* Energy sparkles */}
    <circle cx="11" cy="7" r="0.3" fill="currentColor" opacity="0.7" />
    <circle cx="13" cy="10" r="0.3" fill="currentColor" opacity="0.7" />
    <circle cx="11" cy="13" r="0.3" fill="currentColor" opacity="0.7" />
    <circle cx="13" cy="16" r="0.3" fill="currentColor" opacity="0.7" />
    
    {/* Energy field extensions */}
    <circle cx="12" cy="2" r="0.5" fill="currentColor" opacity="0.5" />
    <circle cx="12" cy="22" r="0.5" fill="currentColor" opacity="0.5" />
    <circle cx="2" cy="12" r="0.5" fill="currentColor" opacity="0.5" />
    <circle cx="22" cy="12" r="0.5" fill="currentColor" opacity="0.5" />
    
    {/* Corner energy dots */}
    <circle cx="7.5" cy="7.5" r="0.4" fill="currentColor" opacity="0.6" />
    <circle cx="16.5" cy="7.5" r="0.4" fill="currentColor" opacity="0.6" />
    <circle cx="7.5" cy="16.5" r="0.4" fill="currentColor" opacity="0.6" />
    <circle cx="16.5" cy="16.5" r="0.4" fill="currentColor" opacity="0.6" />
    
    {/* Data transfer indicators */}
    <path d="M8 5v1" strokeWidth="0.5" opacity="0.4" />
    <path d="M10 4v1" strokeWidth="0.5" opacity="0.3" />
    <path d="M12 5v1" strokeWidth="0.5" opacity="0.4" />
    
    {/* Bottom data flow */}
    <path d="M8 19v1" strokeWidth="0.5" opacity="0.4" />
    <path d="M10 19v1" strokeWidth="0.5" opacity="0.3" />
    <path d="M12 19v1" strokeWidth="0.5" opacity="0.4" />
  </svg>
),

Globe: ({ size = 24, color, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    className={className}
    {...props}
  >
    {/* Globe outer circle */}
    <circle cx="12" cy="12" r="10" stroke={color || 'currentColor'} strokeWidth="2" fill="none"/>
    
    {/* Longitude line - vertical ellipse */}
    <ellipse cx="12" cy="12" rx="4" ry="10" stroke={color || 'currentColor'} strokeWidth="1.5" fill="none"/>
    
    {/* Center meridian */}
    <line x1="12" y1="2" x2="12" y2="22" stroke={color || 'currentColor'} strokeWidth="1.5"/>
    
    {/* Equator */}
    <line x1="2" y1="12" x2="22" y2="12" stroke={color || 'currentColor'} strokeWidth="1.5"/>
  </svg>
),

// Image Icon - Photo with composition guides
ImageIcon : ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <defs>
      <linearGradient id="imageGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
      </linearGradient>
    </defs>
    
    {/* Main image frame */}
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" fill="url(#imageGrad)" opacity="0.1" />
    
    {/* Sun/light source */}
    <circle cx="8" cy="8" r="2.5" />
    <circle cx="8" cy="8" r="2.5" fill="currentColor" opacity="0.15" />
    <circle cx="8" cy="8" r="1" fill="currentColor" opacity="0.8" />
    
    {/* Mountain landscape */}
    <path d="M21 15l-5-5-5 5" />
    <path d="M16 10l-7 7" />
    
    {/* Energy radiating from corners */}
    <circle cx="3" cy="3" r="0.5" fill="currentColor" opacity="0.8" />
    <circle cx="21" cy="3" r="0.5" fill="currentColor" opacity="0.8" />
    <circle cx="3" cy="21" r="0.5" fill="currentColor" opacity="0.6" />
    <circle cx="21" cy="21" r="0.5" fill="currentColor" opacity="0.6" />
    
    {/* Composition guide dots */}
    <circle cx="8" cy="8" r="0.3" fill="currentColor" opacity="0.6" />
    <circle cx="16" cy="8" r="0.3" fill="currentColor" opacity="0.6" />
    <circle cx="8" cy="16" r="0.3" fill="currentColor" opacity="0.6" />
    <circle cx="16" cy="16" r="0.3" fill="currentColor" opacity="0.6" />
  </svg>
),

// Grid Icon - Layout grid with energy flow
GridIcon : ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <defs>
      <linearGradient id="gridGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
      </linearGradient>
    </defs>
    
    {/* Grid squares */}
    <rect x="3" y="3" width="6" height="6" rx="1" />
    <rect x="3" y="3" width="6" height="6" rx="1" fill="url(#gridGrad)" opacity="0.1" />
    
    <rect x="11" y="3" width="6" height="6" rx="1" />
    <rect x="11" y="3" width="6" height="6" rx="1" fill="currentColor" opacity="0.05" />
    
    <rect x="3" y="11" width="6" height="6" rx="1" />
    <rect x="3" y="11" width="6" height="6" rx="1" fill="currentColor" opacity="0.05" />
    
    <rect x="11" y="11" width="6" height="6" rx="1" />
    <rect x="11" y="11" width="6" height="6" rx="1" fill="url(#gridGrad)" opacity="0.1" />
    
    {/* Energy flow lines between grids */}
    <path d="M9 6h2" strokeWidth="1" opacity="0.4" />
    <path d="M6 9v2" strokeWidth="1" opacity="0.4" />
    <path d="M14 9v2" strokeWidth="1" opacity="0.4" />
    <path d="M9 14h2" strokeWidth="1" opacity="0.4" />
    
    {/* Corner energy dots */}
    <circle cx="3" cy="3" r="0.5" fill="currentColor" opacity="0.8" />
    <circle cx="17" cy="3" r="0.5" fill="currentColor" opacity="0.8" />
    <circle cx="3" cy="17" r="0.5" fill="currentColor" opacity="0.8" />
    <circle cx="17" cy="17" r="0.5" fill="currentColor" opacity="0.8" />
    
    {/* Center connection point */}
    <circle cx="10" cy="10" r="0.8" fill="currentColor" opacity="0.2" />
    <circle cx="10" cy="10" r="0.4" fill="currentColor" opacity="0.6" />
    
    {/* Grid energy sparkles */}
    <circle cx="6" cy="6" r="0.3" fill="currentColor" opacity="0.5" />
    <circle cx="14" cy="6" r="0.3" fill="currentColor" opacity="0.5" />
    <circle cx="6" cy="14" r="0.3" fill="currentColor" opacity="0.5" />
    <circle cx="14" cy="14" r="0.3" fill="currentColor" opacity="0.5" />
    
    {/* Extended grid hints */}
    <rect x="19" y="3" width="2" height="6" rx="1" strokeWidth="0.5" opacity="0.3" />
    <rect x="19" y="11" width="2" height="6" rx="1" strokeWidth="0.5" opacity="0.3" />
    <rect x="3" y="19" width="6" height="2" rx="1" strokeWidth="0.5" opacity="0.3" />
    <rect x="11" y="19" width="6" height="2" rx="1" strokeWidth="0.5" opacity="0.3" />
  </svg>
),

// Video Icon - Play button with streaming waves
VideoIcon : ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <defs>
      <linearGradient id="videoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
      </linearGradient>
    </defs>
    
    {/* Main video container */}
    <rect x="2" y="6" width="20" height="12" rx="2" />
    <rect x="2" y="6" width="20" height="12" rx="2" fill="url(#videoGrad)" opacity="0.1" />
    
    {/* Play button */}
    <path d="M10 9v6l5-3z" />
    <path d="M10 9v6l5-3z" fill="currentColor" opacity="0.2" />
    
    {/* Streaming waves */}
    <path d="M22 9c1 0 1 3 0 3" strokeWidth="1" opacity="0.6" />
    <path d="M23 7c1.5 0 1.5 5 0 5" strokeWidth="1" opacity="0.4" />
    <path d="M24 5c2 0 2 7 0 7" strokeWidth="0.8" opacity="0.3" />
    
    {/* Film strip holes */}
    <circle cx="5" cy="8" r="0.5" fill="currentColor" opacity="0.6" />
    <circle cx="19" cy="8" r="0.5" fill="currentColor" opacity="0.6" />
    <circle cx="5" cy="16" r="0.5" fill="currentColor" opacity="0.6" />
    <circle cx="19" cy="16" r="0.5" fill="currentColor" opacity="0.6" />
    
    {/* Energy dots */}
    <circle cx="12" cy="3" r="0.4" fill="currentColor" opacity="0.7" />
    <circle cx="12" cy="21" r="0.4" fill="currentColor" opacity="0.7" />
  </svg>
),

// Mic Icon - Microphone with sound waves
MicIcon : ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <defs>
      <linearGradient id="micGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
      </linearGradient>
    </defs>
    
    {/* Main microphone */}
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" fill="url(#micGrad)" opacity="0.1" />
    
    {/* Mic stand */}
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="23" />
    <line x1="8" y1="23" x2="16" y2="23" />
    
    {/* Sound waves */}
    <path d="M17 4c1 1 1 4 0 5" strokeWidth="1" opacity="0.6" />
    <path d="M19 2c2 2 2 7 0 9" strokeWidth="1" opacity="0.4" />
    <path d="M7 4c-1 1-1 4 0 5" strokeWidth="1" opacity="0.6" />
    <path d="M5 2c-2 2-2 7 0 9" strokeWidth="1" opacity="0.4" />
    
    {/* Mic mesh detail */}
    <circle cx="12" cy="5" r="0.5" fill="currentColor" opacity="0.6" />
    <circle cx="12" cy="7" r="0.5" fill="currentColor" opacity="0.5" />
    <circle cx="12" cy="9" r="0.5" fill="currentColor" opacity="0.4" />
    
    {/* Energy accents */}
    <circle cx="12" cy="1" r="0.5" fill="currentColor" opacity="0.8" />
    <circle cx="9" cy="12" r="0.3" fill="currentColor" opacity="0.6" />
    <circle cx="15" cy="12" r="0.3" fill="currentColor" opacity="0.6" />
  </svg>
),

// Unlock Icon - Shackle wide open at 90 degrees
UnlockIcon: ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <defs>
      <linearGradient id="unlockGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
      </linearGradient>
    </defs>
    
    {/* Lock body */}
    <rect x="5" y="12" width="12" height="9" rx="2" ry="2" />
    <rect x="5" y="12" width="12" height="9" rx="2" ry="2" fill="url(#unlockGrad)" opacity="0.15" />
    
    {/* Shackle - 90 degrees open, swung to the right */}
    <path d="M7 12V9a4 4 0 0 1 4-4h0" />
    <path d="M11 5h6a2 2 0 0 1 2 2v2" />
    
    {/* Keyhole */}
    <circle cx="11" cy="16.5" r="1" fill="currentColor" opacity="0.6" />
    
    {/* Energy burst from the open shackle */}
    <path d="M20 6l2-1" strokeWidth="1.2" opacity="0.9" />
    <path d="M21 9l2 0" strokeWidth="1.2" opacity="0.8" />
    <path d="M20 12l1.5 1" strokeWidth="1" opacity="0.6" />
    
    {/* Top burst */}
    <path d="M14 3l0-2" strokeWidth="1" opacity="0.7" />
    <path d="M17 2l1-1.5" strokeWidth="1" opacity="0.6" />
    
    {/* Freedom sparkles */}
    <circle cx="21" cy="4" r="0.7" fill="currentColor" opacity="0.9" />
    <circle cx="23" cy="7" r="0.5" fill="currentColor" opacity="0.7" />
    <circle cx="22" cy="11" r="0.4" fill="currentColor" opacity="0.5" />
    <circle cx="18" cy="1" r="0.5" fill="currentColor" opacity="0.6" />
    <circle cx="15" cy="1" r="0.3" fill="currentColor" opacity="0.5" />
    
    {/* Motion arc showing swing */}
    <path d="M13 4c2 0 3.5 0.5 4.5 1.5" strokeWidth="0.5" opacity="0.3" strokeDasharray="1.5 1" />
  </svg>
),

// Volume Icon - Speaker with dynamic sound waves
VolumeIcon : ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <defs>
      <linearGradient id="volumeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
      </linearGradient>
    </defs>
    
    {/* Speaker cone */}
    <path d="M11 5L6 9H2v6h4l5 4z" />
    <path d="M11 5L6 9H2v6h4l5 4z" fill="url(#volumeGrad)" opacity="0.1" />
    
    {/* Sound waves */}
    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" strokeWidth="1.2" />
    <path d="M18.36 5.64a9 9 0 0 1 0 12.72" strokeWidth="1" opacity="0.7" />
    <path d="M21.19 2.81a13 13 0 0 1 0 18.38" strokeWidth="0.8" opacity="0.5" />
    
    {/* Speaker detail dots */}
    <circle cx="4" cy="10" r="0.3" fill="currentColor" opacity="0.6" />
    <circle cx="4" cy="12" r="0.3" fill="currentColor" opacity="0.6" />
    <circle cx="4" cy="14" r="0.3" fill="currentColor" opacity="0.6" />
    
    {/* Energy emanation points */}
    <circle cx="11" cy="5" r="0.5" fill="currentColor" opacity="0.8" />
    <circle cx="11" cy="19" r="0.5" fill="currentColor" opacity="0.8" />
    
    {/* Volume level indicators */}
    <circle cx="13" cy="12" r="0.4" fill="currentColor" opacity="0.7" />
    <circle cx="16" cy="12" r="0.4" fill="currentColor" opacity="0.6" />
    <circle cx="19" cy="12" r="0.4" fill="currentColor" opacity="0.5" />
  </svg>
),

// Info Icon - Information symbol with radiating knowledge
InfoIcon : ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <defs>
      <linearGradient id="infoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
      </linearGradient>
    </defs>
    
    {/* Main circle */}
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="10" fill="url(#infoGrad)" opacity="0.1" />
    
    {/* Info dot */}
    <circle cx="12" cy="8" r="1" fill="currentColor" />
    
    {/* Info line */}
    <line x1="12" y1="12" x2="12" y2="16" strokeWidth="2" />
    
    {/* Knowledge radiating outward */}
    <path d="M12 2v-1" strokeWidth="1" opacity="0.6" />
    <path d="M12 22v1" strokeWidth="1" opacity="0.6" />
    <path d="M2 12h-1" strokeWidth="1" opacity="0.6" />
    <path d="M22 12h1" strokeWidth="1" opacity="0.6" />
    
    {/* Diagonal rays */}
    <path d="M5.5 5.5l-1-1" strokeWidth="0.8" opacity="0.5" />
    <path d="M18.5 5.5l1-1" strokeWidth="0.8" opacity="0.5" />
    <path d="M5.5 18.5l-1 1" strokeWidth="0.8" opacity="0.5" />
    <path d="M18.5 18.5l1 1" strokeWidth="0.8" opacity="0.5" />
    
    {/* Inner detail circle */}
    <circle cx="12" cy="12" r="6" stroke="currentColor" strokeWidth="0.5" opacity="0.2" />
    
    {/* Energy accent dots */}
    <circle cx="12" cy="2" r="0.5" fill="currentColor" opacity="0.8" />
    <circle cx="2" cy="12" r="0.5" fill="currentColor" opacity="0.8" />
    <circle cx="22" cy="12" r="0.5" fill="currentColor" opacity="0.8" />
    <circle cx="12" cy="22" r="0.5" fill="currentColor" opacity="0.8" />
    
    {/* Corner energy sparkles */}
    <circle cx="7" cy="7" r="0.3" fill="currentColor" opacity="0.6" />
    <circle cx="17" cy="7" r="0.3" fill="currentColor" opacity="0.6" />
    <circle cx="7" cy="17" r="0.3" fill="currentColor" opacity="0.6" />
    <circle cx="17" cy="17" r="0.3" fill="currentColor" opacity="0.6" />
  </svg>
),

TwoFactorAuth: ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    {/* Outer shield */}
    <path d="M12 2L3 6v5c0 6 9 10 9 10s9-4 9-10V6l-9-4z" opacity="0.4" />
    
    {/* Inner shield */}
    <path d="M12 5L6 8v4c0 4 6 7 6 7s6-3 6-7V8l-6-3z" />
    <path d="M12 5L6 8v4c0 4 6 7 6 7s6-3 6-7V8l-6-3z" fill="currentColor" opacity="0.1" />
    
    {/* Number 2 */}
    <text x="12" y="14" fontSize="6" fontWeight="bold" fill="currentColor" textAnchor="middle" opacity="0.9">2</text>
    
    {/* Accents */}
    <circle cx="12" cy="5" r="0.4" fill="currentColor" opacity="0.7" />
    <circle cx="6" cy="8" r="0.3" fill="currentColor" opacity="0.5" />
    <circle cx="18" cy="8" r="0.3" fill="currentColor" opacity="0.5" />
  </svg>
),

// Star Icon - Favorite/Premium with radiant energy
StarIcon : ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <defs>
      <linearGradient id="starGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
      </linearGradient>
    </defs>
    
    {/* Main star shape */}
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="url(#starGrad)" opacity="0.1" />
    
    {/* Inner star glow */}
    <path d="M12 5l2 4 4.5 0.5-3.5 3 1 4.5-4-2.5-4 2.5 1-4.5-3.5-3L10 9z" fill="currentColor" opacity="0.15" />
    
    {/* Radiant beams */}
    <path d="M12 2v-2" strokeWidth="0.8" opacity="0.6" />
    <path d="M12 22v2" strokeWidth="0.8" opacity="0.6" />
    <path d="M2 12h-2" strokeWidth="0.8" opacity="0.6" />
    <path d="M22 12h2" strokeWidth="0.8" opacity="0.6" />
    
    {/* Diagonal rays */}
    <path d="M5 5l-1.5-1.5" strokeWidth="0.6" opacity="0.5" />
    <path d="M19 5l1.5-1.5" strokeWidth="0.6" opacity="0.5" />
    <path d="M5 19l-1.5 1.5" strokeWidth="0.6" opacity="0.5" />
    <path d="M19 19l1.5 1.5" strokeWidth="0.6" opacity="0.5" />
    
    {/* Star point accents */}
    <circle cx="12" cy="2" r="0.5" fill="currentColor" opacity="0.9" />
    <circle cx="15.09" cy="8.26" r="0.4" fill="currentColor" opacity="0.8" />
    <circle cx="22" cy="9.27" r="0.4" fill="currentColor" opacity="0.8" />
    <circle cx="17" cy="14.14" r="0.4" fill="currentColor" opacity="0.7" />
    <circle cx="18.18" cy="21.02" r="0.4" fill="currentColor" opacity="0.7" />
    <circle cx="12" cy="17.77" r="0.4" fill="currentColor" opacity="0.7" />
    <circle cx="5.82" cy="21.02" r="0.4" fill="currentColor" opacity="0.7" />
    <circle cx="7" cy="14.14" r="0.4" fill="currentColor" opacity="0.7" />
    <circle cx="2" cy="9.27" r="0.4" fill="currentColor" opacity="0.8" />
    <circle cx="8.91" cy="8.26" r="0.4" fill="currentColor" opacity="0.8" />
    
    {/* Center sparkle */}
    <circle cx="12" cy="12" r="0.8" fill="currentColor" opacity="0.2" />
    <circle cx="12" cy="12" r="0.4" fill="currentColor" opacity="0.8" />
    
    {/* Energy sparkles around */}
    <circle cx="9" cy="6" r="0.3" fill="currentColor" opacity="0.6" />
    <circle cx="15" cy="6" r="0.3" fill="currentColor" opacity="0.6" />
    <circle cx="6" cy="12" r="0.3" fill="currentColor" opacity="0.5" />
    <circle cx="18" cy="12" r="0.3" fill="currentColor" opacity="0.5" />
    <circle cx="9" cy="18" r="0.3" fill="currentColor" opacity="0.6" />
    <circle cx="15" cy="18" r="0.3" fill="currentColor" opacity="0.6" />
  </svg>
),

// Pin Icon - Push pin with anchoring energy
PinIcon : ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <defs>
      <linearGradient id="pinGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
      </linearGradient>
    </defs>
    
    {/* Pin head */}
    <path d="M12 2l-2 4v5c0 .55-.45 1-1 1H8c-.55 0-1 .45-1 1s.45 1 1 1h8c.55 0 1-.45 1-1s-.45-1-1-1h-1c-.55 0-1-.45-1-1V6l-2-4z" />
    <path d="M12 2l-2 4v5c0 .55-.45 1-1 1H8c-.55 0-1 .45-1 1s.45 1 1 1h8c.55 0 1-.45 1-1s-.45-1-1-1h-1c-.55 0-1-.45-1-1V6l-2-4z" fill="url(#pinGrad)" opacity="0.1" />
    
    {/* Pin needle */}
    <line x1="12" y1="14" x2="12" y2="22" strokeWidth="2" />
    
    {/* Pin point */}
    <circle cx="12" cy="22" r="0.5" fill="currentColor" />
    
    {/* Anchoring waves */}
    <circle cx="12" cy="22" r="2" stroke="currentColor" strokeWidth="0.5" opacity="0.4">
      <animate attributeName="r" values="2;4;2" dur="2s" repeatCount="indefinite" />
      <animate attributeName="opacity" values="0.4;0.1;0.4" dur="2s" repeatCount="indefinite" />
    </circle>
    
    {/* Energy emanating from pin head */}
    <path d="M12 2l-1-1" strokeWidth="0.6" opacity="0.6" />
    <path d="M12 2l1-1" strokeWidth="0.6" opacity="0.6" />
    <path d="M10 4l-2-1" strokeWidth="0.6" opacity="0.5" />
    <path d="M14 4l2-1" strokeWidth="0.6" opacity="0.5" />
    
    {/* Pin head shine */}
    <circle cx="12" cy="4" r="0.8" fill="currentColor" opacity="0.2" />
    <circle cx="12" cy="4" r="0.4" fill="currentColor" opacity="0.8" />
    
    {/* Side energy dots */}
    <circle cx="8" cy="12" r="0.3" fill="currentColor" opacity="0.6" />
    <circle cx="16" cy="12" r="0.3" fill="currentColor" opacity="0.6" />
    
    {/* Grip texture on pin */}
    <circle cx="10" cy="8" r="0.3" fill="currentColor" opacity="0.4" />
    <circle cx="14" cy="8" r="0.3" fill="currentColor" opacity="0.4" />
    <circle cx="10" cy="10" r="0.3" fill="currentColor" opacity="0.3" />
    <circle cx="14" cy="10" r="0.3" fill="currentColor" opacity="0.3" />
    
    {/* Corner accents */}
    <circle cx="7" cy="13" r="0.5" fill="currentColor" opacity="0.7" />
    <circle cx="17" cy="13" r="0.5" fill="currentColor" opacity="0.7" />
  </svg>
),

// Alert Icon - Exclamation with warning energy
AlertIcon : ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <defs>
      <linearGradient id="alertGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
      </linearGradient>
    </defs>
    
    {/* Triangle shape */}
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" fill="url(#alertGrad)" opacity="0.1" />
    
    {/* Exclamation mark */}
    <line x1="12" y1="9" x2="12" y2="13" strokeWidth="2" />
    <circle cx="12" cy="17" r="0.5" fill="currentColor" />
    
    {/* Warning pulse waves */}
    <path d="M2 12c-1-1-1-3 0-4" strokeWidth="1" opacity="0.6" />
    <path d="M22 12c1-1 1-3 0-4" strokeWidth="1" opacity="0.6" />
    <path d="M0 12c-2-2-2-6 0-8" strokeWidth="0.8" opacity="0.4" />
    <path d="M24 12c2-2 2-6 0-8" strokeWidth="0.8" opacity="0.4" />
    
    {/* Energy accents */}
    <circle cx="12" cy="3.86" r="0.5" fill="currentColor" opacity="0.8" />
    <circle cx="1.82" cy="18" r="0.5" fill="currentColor" opacity="0.6" />
    <circle cx="22.18" cy="18" r="0.5" fill="currentColor" opacity="0.6" />
    
    {/* Corner sparkles */}
    <circle cx="8" cy="12" r="0.3" fill="currentColor" opacity="0.5" />
    <circle cx="16" cy="12" r="0.3" fill="currentColor" opacity="0.5" />
  </svg>
),

// Move Icon - Dynamic movement arrows with energy trails
MoveIcon : ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <defs>
      <linearGradient id="moveGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
      </linearGradient>
    </defs>
    
    {/* Main cross arrows */}
    <path d="M5 9l-3 3 3 3" />
    <path d="M9 5l3-3 3 3" />
    <path d="M15 19l3 3 3-3" />
    <path d="M19 9l3 3-3 3" />
    
    {/* Connecting lines */}
    <path d="M2 12h20" />
    <path d="M12 2v20" />
    <path d="M2 12h20M12 2v20" fill="url(#moveGrad)" opacity="0.1" />
    
    {/* Center energy point */}
    <circle cx="12" cy="12" r="2" fill="currentColor" opacity="0.15" />
    <circle cx="12" cy="12" r="1" fill="currentColor" opacity="0.8" />
    
    {/* Motion trails */}
    <path d="M6 12h2" strokeWidth="1" opacity="0.4" />
    <path d="M16 12h2" strokeWidth="1" opacity="0.4" />
    <path d="M12 6v2" strokeWidth="1" opacity="0.4" />
    <path d="M12 16v2" strokeWidth="1" opacity="0.4" />
    
    {/* Energy dots at arrow tips */}
    <circle cx="2" cy="12" r="0.5" fill="currentColor" opacity="0.8" />
    <circle cx="22" cy="12" r="0.5" fill="currentColor" opacity="0.8" />
    <circle cx="12" cy="2" r="0.5" fill="currentColor" opacity="0.8" />
    <circle cx="12" cy="22" r="0.5" fill="currentColor" opacity="0.8" />
    
    {/* Diagonal movement indicators */}
    <circle cx="6" cy="6" r="0.3" fill="currentColor" opacity="0.6" />
    <circle cx="18" cy="6" r="0.3" fill="currentColor" opacity="0.6" />
    <circle cx="6" cy="18" r="0.3" fill="currentColor" opacity="0.6" />
    <circle cx="18" cy="18" r="0.3" fill="currentColor" opacity="0.6" />
    
    {/* Motion blur effects */}
    <path d="M3 11h1" strokeWidth="0.5" opacity="0.3" />
    <path d="M20 13h1" strokeWidth="0.5" opacity="0.3" />
    <path d="M11 3v1" strokeWidth="0.5" opacity="0.3" />
    <path d="M13 20v1" strokeWidth="0.5" opacity="0.3" />
  </svg>
),

  // Milestone Journey Icons
// MilestoneRunner Icon - Illustrated Rocket
MilestoneRunner: ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    className={className}
    {...props}
  >
    {/* Rocket body */}
    <path 
      d="M13 3c-1.5 2-2.5 4.5-2.5 7.5 0 2 0.5 3.5 1.5 5l3-1.5 2 2c1-1.5 1.5-3.5 1.5-5.5 0-3.5-2-6-5.5-7.5z" 
      fill="#f8fafc"
      stroke="#1e293b"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
    
    {/* Top fin */}
    <path 
      d="M15 5.5l4-1.5-1 4-2-1.5z" 
      fill="#f43f5e"
      stroke="#1e293b"
      strokeWidth="1"
      strokeLinejoin="round"
    />
    
    {/* Bottom fin */}
    <path 
      d="M9 14l-2 4 4-1-1-2z" 
      fill="#f43f5e"
      stroke="#1e293b"
      strokeWidth="1"
      strokeLinejoin="round"
    />
    
    {/* Window */}
    <circle 
      cx="14" 
      cy="10" 
      r="2" 
      fill="#0ea5e9"
      stroke="#1e293b"
      strokeWidth="1"
    />
    <circle cx="13.5" cy="9.5" r="0.5" fill="white" opacity="0.8"/>
    
    {/* Flame */}
    <path 
      d="M7 17c-0.5 1-1.5 2-3 3 0.5-1.5 1-2.5 1-3.5 0-0.5 0.5-1 1-1 1 0 1.5 0.5 1 1.5z" 
      fill="#f97316"
      stroke="#1e293b"
      strokeWidth="0.8"
      strokeLinejoin="round"
    />
    
    {/* Decorative plus signs */}
    <g stroke="#1e293b" strokeWidth="1" opacity="0.6">
      <path d="M20 4v2M19 5h2" />
      <path d="M6 6v1.5M5.25 6.75h1.5" />
      <path d="M21 14v1.5M20.25 14.75h1.5" />
    </g>
    
    {/* Decorative dots */}
    <circle cx="4" cy="20" r="0.6" fill="#1e293b" opacity="0.4"/>
    <circle cx="18" cy="18" r="0.5" fill="#1e293b" opacity="0.4"/>
    <circle cx="21" cy="11" r="0.4" fill="#1e293b" opacity="0.3"/>
    <circle cx="8" cy="4" r="0.4" fill="#1e293b" opacity="0.3"/>
  </svg>
),

  // Lightning - Energy and momentum
  MilestoneLightning: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <defs>
        <linearGradient id="lightningGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.9" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.5" />
        </linearGradient>
      </defs>
      
      {/* Main lightning bolt */}
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" fill="url(#lightningGrad)" opacity="0.8" />
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" strokeWidth="1.5" />
      
      {/* Energy field */}
      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="0.5" opacity="0.3">
        <animate attributeName="r" values="8;10;8" dur="2s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.3;0.1;0.3" dur="2s" repeatCount="indefinite" />
      </circle>
      
      {/* Electric sparks */}
      <path d="M4 8l1-1" strokeWidth="1" opacity="0.6" />
      <path d="M18 4l1 1" strokeWidth="1" opacity="0.6" />
      <path d="M20 16l-1 1" strokeWidth="1" opacity="0.6" />
      <path d="M6 20l-1-1" strokeWidth="1" opacity="0.6" />
      
      {/* Dynamic energy points */}
      <circle cx="5" cy="7" r="0.4" fill="currentColor" opacity="0.7">
        <animate attributeName="opacity" values="0.7;0.2;0.7" dur="1.5s" repeatCount="indefinite" />
      </circle>
      <circle cx="19" cy="5" r="0.4" fill="currentColor" opacity="0.7">
        <animate attributeName="opacity" values="0.7;0.2;0.7" dur="1.8s" repeatCount="indefinite" />
      </circle>
      <circle cx="19" cy="17" r="0.4" fill="currentColor" opacity="0.7">
        <animate attributeName="opacity" values="0.7;0.2;0.7" dur="1.3s" repeatCount="indefinite" />
      </circle>
    </svg>
  ),

  // Fire - Building heat and progress
  MilestoneFire: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <defs>
        <linearGradient id="fireGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.9" />
          <stop offset="50%" stopColor="currentColor" stopOpacity="0.6" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.3" />
        </linearGradient>
      </defs>
      
      {/* Main flame */}
      <path d="M12 2C10 4 8 8 8 12c0 4 2 8 4 8s4-4 4-8c0-4-2-8-4-8z" fill="url(#fireGrad)" opacity="0.7" />
      <path d="M12 2C10 4 8 8 8 12c0 4 2 8 4 8s4-4 4-8c0-4-2-8-4-8z" />
      
      {/* Inner flame core */}
      <path d="M12 6c-1 1-2 3-2 5s1 4 2 4 2-2 2-4-1-4-2-5z" fill="currentColor" opacity="0.5" />
      
      {/* Flame flickers */}
      <path d="M10 4c0 2-1 3-1 5" strokeWidth="1" opacity="0.6">
        <animate attributeName="d" values="M10 4c0 2-1 3-1 5;M10 3c0 2-1 4-1 6;M10 4c0 2-1 3-1 5" dur="2s" repeatCount="indefinite" />
      </path>
      <path d="M14 4c0 2 1 3 1 5" strokeWidth="1" opacity="0.6">
        <animate attributeName="d" values="M14 4c0 2 1 3 1 5;M14 3c0 2 1 4 1 6;M14 4c0 2 1 3 1 5" dur="1.8s" repeatCount="indefinite" />
      </path>
      
      {/* Heat waves */}
      <path d="M6 18c2-1 4-1 6 0s4 1 6 0" strokeWidth="0.8" opacity="0.3">
        <animateTransform attributeName="transform" type="translate" values="0,0;0,1;0,0" dur="2s" repeatCount="indefinite" />
      </path>
      <path d="M7 21c2-1 3-1 5 0s3 1 5 0" strokeWidth="0.6" opacity="0.2">
        <animateTransform attributeName="transform" type="translate" values="0,0;0,-1;0,0" dur="2.5s" repeatCount="indefinite" />
      </path>
      
      {/* Sparks */}
      <circle cx="6" cy="14" r="0.3" fill="currentColor" opacity="0.6" />
      <circle cx="18" cy="16" r="0.4" fill="currentColor" opacity="0.5" />
      <circle cx="4" cy="11" r="0.2" fill="currentColor" opacity="0.7" />
    </svg>
  ),

  // Target - Precision and focus
  MilestoneTarget: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <defs>
        <linearGradient id="targetGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.3" />
        </linearGradient>
      </defs>
      
      {/* Target rings */}
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="10" fill="url(#targetGrad)" opacity="0.1" />
      <circle cx="12" cy="12" r="7" opacity="0.7" />
      <circle cx="12" cy="12" r="7" fill="currentColor" opacity="0.05" />
      <circle cx="12" cy="12" r="4" opacity="0.8" />
      <circle cx="12" cy="12" r="4" fill="currentColor" opacity="0.1" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" opacity="0.8" />
      
      {/* Crosshairs */}
      <line x1="12" y1="2" x2="12" y2="6" strokeWidth="1" opacity="0.6" />
      <line x1="12" y1="18" x2="12" y2="22" strokeWidth="1" opacity="0.6" />
      <line x1="2" y1="12" x2="6" y2="12" strokeWidth="1" opacity="0.6" />
      <line x1="18" y1="12" x2="22" y2="12" strokeWidth="1" opacity="0.6" />
      
      {/* Precision indicators */}
      <line x1="8.5" y1="8.5" x2="9.5" y2="9.5" strokeWidth="0.8" opacity="0.5" />
      <line x1="15.5" y1="8.5" x2="14.5" y2="9.5" strokeWidth="0.8" opacity="0.5" />
      <line x1="8.5" y1="15.5" x2="9.5" y2="14.5" strokeWidth="0.8" opacity="0.5" />
      <line x1="15.5" y1="15.5" x2="14.5" y2="14.5" strokeWidth="0.8" opacity="0.5" />
      
      {/* Focus pulse */}
      <circle cx="12" cy="12" r="6" stroke="currentColor" strokeWidth="0.3" opacity="0.4">
        <animate attributeName="r" values="6;8;6" dur="3s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.4;0.1;0.4" dur="3s" repeatCount="indefinite" />
      </circle>
      
      {/* Accuracy dots */}
      <circle cx="12" cy="4" r="0.4" fill="currentColor" opacity="0.6" />
      <circle cx="20" cy="12" r="0.4" fill="currentColor" opacity="0.6" />
      <circle cx="12" cy="20" r="0.4" fill="currentColor" opacity="0.6" />
      <circle cx="4" cy="12" r="0.4" fill="currentColor" opacity="0.6" />
    </svg>
  ),

  // Trophy - Achievement and victory
  MilestoneTrophy: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <defs>
        <linearGradient id="trophyGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.9" />
          <stop offset="50%" stopColor="currentColor" stopOpacity="0.7" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
        </linearGradient>
      </defs>
      
      {/* Trophy cup */}
      <path d="M8 6h8v7a4 4 0 0 1-8 0z" fill="url(#trophyGrad)" opacity="0.7" />
      <path d="M8 6h8v7a4 4 0 0 1-8 0z" />
      
      {/* Trophy handles */}
      <path d="M6 9a2 2 0 0 1-2-2V6a1 1 0 0 1 1-1h1" strokeWidth="1.5" />
      <path d="M18 9a2 2 0 0 0 2-2V6a1 1 0 0 0-1-1h-1" strokeWidth="1.5" />
      
      {/* Trophy base */}
      <rect x="7" y="18" width="10" height="3" rx="1" fill="currentColor" opacity="0.6" />
      <line x1="9" y1="17" x2="15" y2="17" strokeWidth="2" />
      
      {/* Trophy stem */}
      <line x1="12" y1="17" x2="12" y2="13" strokeWidth="2" />
      
      {/* Victory ornament */}
      <circle cx="12" cy="8" r="1" fill="currentColor" opacity="0.8" />
      <path d="M10 3l2 2 2-2" strokeWidth="1.2" opacity="0.7" />
      
      {/* Celebration sparkles */}
      <circle cx="6" cy="4" r="0.4" fill="currentColor" opacity="0.7">
        <animate attributeName="opacity" values="0.7;0.3;0.7" dur="2s" repeatCount="indefinite" />
      </circle>
      <circle cx="18" cy="3" r="0.3" fill="currentColor" opacity="0.6">
        <animate attributeName="opacity" values="0.6;0.2;0.6" dur="1.5s" repeatCount="indefinite" />
      </circle>
      <circle cx="20" cy="7" r="0.5" fill="currentColor" opacity="0.8">
        <animate attributeName="opacity" values="0.8;0.4;0.8" dur="2.3s" repeatCount="indefinite" />
      </circle>
      <circle cx="4" cy="8" r="0.3" fill="currentColor" opacity="0.5">
        <animate attributeName="opacity" values="0.5;0.1;0.5" dur="1.8s" repeatCount="indefinite" />
      </circle>
      
      {/* Victory rays */}
      <path d="M12 1v2" strokeWidth="0.8" opacity="0.5" />
      <path d="M20.5 3.5l-1.5 1.5" strokeWidth="0.8" opacity="0.4" />
      <path d="M3.5 3.5l1.5 1.5" strokeWidth="0.8" opacity="0.4" />
      <path d="M22 8h-2" strokeWidth="0.8" opacity="0.3" />
      <path d="M4 8H2" strokeWidth="0.8" opacity="0.3" />
    </svg>
  ),

  // Rocket - Dynamic progress and launch
  MilestoneRocket: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <defs>
        <linearGradient id="rocketGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.9" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.5" />
        </linearGradient>
      </defs>
      
      {/* Rocket body */}
      <path d="M10 2a2 2 0 0 1 4 0v16a2 2 0 0 1-4 0z" fill="url(#rocketGrad)" opacity="0.7" />
      <path d="M10 2a2 2 0 0 1 4 0v16a2 2 0 0 1-4 0z" />
      
      {/* Rocket nose */}
      <circle cx="12" cy="4" r="1.5" fill="currentColor" opacity="0.8" />
      
      {/* Rocket fins */}
      <path d="M10 14l-3 4 1 2 2-1z" strokeWidth="1.2" />
      <path d="M14 14l3 4-1 2-2-1z" strokeWidth="1.2" />
      
      {/* Rocket window */}
      <circle cx="12" cy="8" r="1" stroke="currentColor" strokeWidth="1" opacity="0.6" />
      <circle cx="12" cy="8" r="0.3" fill="currentColor" opacity="0.8" />
      
      {/* Exhaust flames */}
      <path d="M10 18c0 2 1 4 2 4s2-2 2-4" fill="currentColor" opacity="0.6">
        <animate attributeName="opacity" values="0.6;0.3;0.6" dur="1.5s" repeatCount="indefinite" />
      </path>
      <path d="M11 20c0 1 0.5 2 1 2s1-1 1-2" fill="currentColor" opacity="0.8">
        <animate attributeName="opacity" values="0.8;0.4;0.8" dur="1.2s" repeatCount="indefinite" />
      </path>
      
      {/* Thrust trails */}
      <circle cx="12" cy="19" r="0.5" fill="currentColor" opacity="0.5">
        <animate attributeName="r" values="0.5;1.5;0.5" dur="2s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.5;0.1;0.5" dur="2s" repeatCount="indefinite" />
      </circle>
      
      {/* Launch sparkles */}
      <circle cx="6" cy="6" r="0.3" fill="currentColor" opacity="0.7">
        <animate attributeName="opacity" values="0.7;0.2;0.7" dur="1.8s" repeatCount="indefinite" />
      </circle>
      <circle cx="18" cy="4" r="0.4" fill="currentColor" opacity="0.6">
        <animate attributeName="opacity" values="0.6;0.1;0.6" dur="2.2s" repeatCount="indefinite" />
      </circle>
      <circle cx="20" cy="10" r="0.2" fill="currentColor" opacity="0.8">
        <animate attributeName="opacity" values="0.8;0.3;0.8" dur="1.5s" repeatCount="indefinite" />
      </circle>
      <circle cx="4" cy="12" r="0.3" fill="currentColor" opacity="0.5">
        <animate attributeName="opacity" values="0.5;0.1;0.5" dur="2s" repeatCount="indefinite" />
      </circle>
      
      {/* Speed lines */}
      <path d="M2 8h3" strokeWidth="1" opacity="0.4" />
      <path d="M1 12h4" strokeWidth="0.8" opacity="0.3" />
      <path d="M3 16h2" strokeWidth="0.6" opacity="0.2" />
    </svg>
  ),

  // Draft Icon - Document with pencil and edit energy
DraftIcon: ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <defs>
      <linearGradient id="draftGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
      </linearGradient>
    </defs>
    
    {/* Document */}
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" fill="url(#draftGrad)" opacity="0.1" />
    
    {/* Document folded corner */}
    <path d="M14 2v6h6" />
    
    {/* Pencil editing */}
    <path d="M11 17l-4 1 1-4 7-7 3 3-7 7z" />
    <path d="M15 10l3 3" />
    
    {/* Draft lines (incomplete) */}
    <line x1="8" y1="7" x2="12" y2="7" strokeWidth="1" opacity="0.5" strokeDasharray="2 1" />
    <line x1="8" y1="10" x2="11" y2="10" strokeWidth="1" opacity="0.4" strokeDasharray="2 1" />
    
    {/* Edit energy */}
    <path d="M18 7l1-1" strokeWidth="0.5" opacity="0.6" />
    <path d="M19 8l1 0" strokeWidth="0.5" opacity="0.5" />
    <path d="M17 6l0-1" strokeWidth="0.5" opacity="0.5" />
    
    {/* Corner accent */}
    <circle cx="14" cy="2" r="0.5" fill="currentColor" opacity="0.8" />
    
    {/* Pencil tip detail */}
    <circle cx="8" cy="18" r="0.5" fill="currentColor" opacity="0.8" />
    
    {/* Work in progress dots */}
    <circle cx="6" cy="13" r="0.3" fill="currentColor" opacity="0.6" />
    <circle cx="8" cy="13" r="0.3" fill="currentColor" opacity="0.5" />
    <circle cx="10" cy="13" r="0.3" fill="currentColor" opacity="0.4" />
    
    {/* Edit sparkles */}
    <circle cx="16" cy="11" r="0.3" fill="currentColor" opacity="0.7" />
    <circle cx="13" cy="14" r="0.3" fill="currentColor" opacity="0.6" />
  </svg>
),

HelpSupport: ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    {...props}
  >
    {/* Background glow */}
    <circle cx="12" cy="12" r="10" opacity="0.15"/>
    
    {/* Circle border */}
    <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 2a8 8 0 100 16 8 8 0 000-16z" opacity="0.3"/>
    
    {/* Question mark */}
    <path d="M12 6a3.5 3.5 0 0 0-3.5 3.5 1 1 0 1 0 2 0A1.5 1.5 0 0 1 12 8a1.5 1.5 0 0 1 1.5 1.5c0 .494-.243.934-.617 1.216l-.03.023c-.395.297-.853.635-1.853 1.761a1 1 0 0 0 1.5 1.32c.853-.96 1.147-1.168 1.447-1.392l.036-.027c.747-.564 1.517-1.414 1.517-2.901A3.5 3.5 0 0 0 12 6z"/>
    
    {/* Dot below question mark */}
    <circle cx="12" cy="17" r="1.25"/>
    
    {/* Energy particles around */}
    <circle cx="7" cy="7" r="1" opacity="0.6"/>
    <circle cx="12" cy="4.5" r="0.8" opacity="0.5"/>
    <circle cx="17" cy="7" r="1" opacity="0.6"/>
    <circle cx="19.5" cy="12" r="0.8" opacity="0.5"/>
    <circle cx="17" cy="17" r="1" opacity="0.6"/>
    <circle cx="12" cy="19.5" r="0.8" opacity="0.5"/>
    <circle cx="7" cy="17" r="1" opacity="0.6"/>
    <circle cx="4.5" cy="12" r="0.8" opacity="0.5"/>
    
    {/* Flow lines */}
    <path d="M8 6l-0.5-0.5" strokeWidth="0.5" stroke="currentColor" opacity="0.3" fill="none"/>
    <path d="M16 6l0.5-0.5" strokeWidth="0.5" stroke="currentColor" opacity="0.3" fill="none"/>
    <path d="M18 10l0.8-0.5" strokeWidth="0.5" stroke="currentColor" opacity="0.3" fill="none"/>
    <path d="M18 14l0.8 0.5" strokeWidth="0.5" stroke="currentColor" opacity="0.3" fill="none"/>
    <path d="M16 18l0.5 0.5" strokeWidth="0.5" stroke="currentColor" opacity="0.3" fill="none"/>
    <path d="M8 18l-0.5 0.5" strokeWidth="0.5" stroke="currentColor" opacity="0.3" fill="none"/>
    <path d="M6 14l-0.8 0.5" strokeWidth="0.5" stroke="currentColor" opacity="0.3" fill="none"/>
    <path d="M6 10l-0.8-0.5" strokeWidth="0.5" stroke="currentColor" opacity="0.3" fill="none"/>
    
    {/* Sparkles for helpfulness */}
    <path d="M9 4l0.2-0.6 0.2 0.6 0.6 0.2-0.6 0.2-0.2 0.6-0.2-0.6-0.6-0.2z" opacity="0.4"/>
    <path d="M15 4l0.2-0.6 0.2 0.6 0.6 0.2-0.6 0.2-0.2 0.6-0.2-0.6-0.6-0.2z" opacity="0.4"/>
    <path d="M20 9l0.3-0.8 0.3 0.8 0.8 0.3-0.8 0.3-0.3 0.8-0.3-0.8-0.8-0.3z" opacity="0.35"/>
    <path d="M20 15l0.3-0.8 0.3 0.8 0.8 0.3-0.8 0.3-0.3 0.8-0.3-0.8-0.8-0.3z" opacity="0.35"/>
    <path d="M4 9l0.3-0.8 0.3 0.8 0.8 0.3-0.8 0.3-0.3 0.8-0.3-0.8-0.8-0.3z" opacity="0.35"/>
    <path d="M4 15l0.3-0.8 0.3 0.8 0.8 0.3-0.8 0.3-0.3 0.8-0.3-0.8-0.8-0.3z" opacity="0.35"/>
  </svg>
),

  // Social Media Icons (from Simple Icons - auto-updated)
  Instagram: ({ size = 24, color, className = "", ...props }) => (
    <SiInstagram size={size} color={color || 'currentColor'} className={className} {...props} />
  ),
  X: ({ size = 24, color, className = "", ...props }) => (
    <SiX size={size} color={color || 'currentColor'} className={className} {...props} />
  ),
  TikTok: ({ size = 24, color, className = "", ...props }) => (
    <SiTiktok size={size} color={color || 'currentColor'} className={className} {...props} />
  ),
  YouTube: ({ size = 24, color, className = "", ...props }) => (
    <SiYoutube size={size} color={color || 'currentColor'} className={className} {...props} />
  ),
  Facebook: ({ size = 24, color, className = "", ...props }) => (
    <SiFacebook size={size} color={color || 'currentColor'} className={className} {...props} />
  ),
  Snapchat: ({ size = 24, color, className = "", ...props }) => (
    <SiSnapchat size={size} color={color || 'currentColor'} className={className} {...props} />
  ),
  Twitch: ({ size = 24, color, className = "", ...props }) => (
    <SiTwitch size={size} color={color || 'currentColor'} className={className} {...props} />
  ),
  Threads: ({ size = 24, color, className = "", ...props }) => (
    <SiThreads size={size} color={color || 'currentColor'} className={className} {...props} />
  ),
  Pinterest: ({ size = 24, color, className = "", ...props }) => (
    <SiPinterest size={size} color={color || 'currentColor'} className={className} {...props} />
  ),
  Spotify: ({ size = 24, color, className = "", ...props }) => (
    <SiSpotify size={size} color={color || 'currentColor'} className={className} {...props} />
  ),
  Discord: ({ size = 24, color, className = "", ...props }) => (
    <SiDiscord size={size} color={color || 'currentColor'} className={className} {...props} />
  ),

// Clock / Schedule icon - Clean precision style
Clock: ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <defs>
      <linearGradient id="clockFaceGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0.12" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0.04" />
      </linearGradient>
    </defs>

    {/* Outer dashed orbit ring */}
    <circle cx="12" cy="12" r="11" strokeWidth="0.6" strokeDasharray="1.8 2.8" opacity="0.22" />

    {/* Face fill */}
    <circle cx="12" cy="12" r="8.5" fill="url(#clockFaceGrad)" />

    {/* Face outline */}
    <circle cx="12" cy="12" r="8.5" strokeWidth="1.5" />

    {/* 12 tick marks — 4 bold cardinal, 8 minor */}
    <path d="M12 4v1.4" strokeWidth="1.4" opacity="0.8" />
    <path d="M20 12h-1.4" strokeWidth="1.4" opacity="0.8" />
    <path d="M12 20v-1.4" strokeWidth="1.4" opacity="0.8" />
    <path d="M4 12h1.4" strokeWidth="1.4" opacity="0.8" />
    <path d="M17.66 6.34l-1 1" strokeWidth="0.9" opacity="0.35" />
    <path d="M19.5 8.8l-1.2.5" strokeWidth="0.9" opacity="0.35" />
    <path d="M19.5 15.2l-1.2-.5" strokeWidth="0.9" opacity="0.35" />
    <path d="M17.66 17.66l-1-1" strokeWidth="0.9" opacity="0.35" />
    <path d="M6.34 17.66l1-1" strokeWidth="0.9" opacity="0.35" />
    <path d="M4.5 15.2l1.2-.5" strokeWidth="0.9" opacity="0.35" />
    <path d="M4.5 8.8l1.2.5" strokeWidth="0.9" opacity="0.35" />
    <path d="M6.34 6.34l1 1" strokeWidth="0.9" opacity="0.35" />

    {/* Hour hand — pointing to ~10 o'clock */}
    <line x1="12" y1="12" x2="7.8" y2="9.6" strokeWidth="2.2" strokeLinecap="round" />

    {/* Minute hand — pointing to ~2 o'clock */}
    <line x1="12" y1="12" x2="16" y2="7.5" strokeWidth="1.6" strokeLinecap="round" />

    {/* Subtle sweep arc behind minute hand */}
    <path d="M12 5.2 A6.8 6.8 0 0 1 15.5 7" strokeWidth="0.8" opacity="0.3" />

    {/* Center cap */}
    <circle cx="12" cy="12" r="1.3" fill="currentColor" stroke="none" />

    {/* Two accent particles — asymmetric, not a ring */}
    <circle cx="21" cy="7.5" r="0.65" fill="currentColor" stroke="none" opacity="0.45" />
    <circle cx="3.5" cy="16" r="0.5" fill="currentColor" stroke="none" opacity="0.35" />

    {/* Single sparkle top-right */}
    <path d="M20.5 3.8l0.28-.65.28.65.65.28-.65.28-.28.65-.28-.65-.65-.28z" fill="currentColor" stroke="none" opacity="0.5" />
  </svg>
),

// Trending Icon - Flame with energy gradient
TrendingIcon: ({ size = 24, className = "", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <defs>
      <linearGradient id="flameGrad" x1="0%" y1="100%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#f97316" stopOpacity="0.9" />
        <stop offset="100%" stopColor="#ef4444" stopOpacity="0.9" />
      </linearGradient>
      <linearGradient id="flameInnerGrad" x1="0%" y1="100%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#fbbf24" stopOpacity="0.9" />
        <stop offset="100%" stopColor="#f97316" stopOpacity="0.9" />
      </linearGradient>
    </defs>
    {/* Outer flame */}
    <path d="M12 2c0 4-4 6-4 10a6 6 0 0 0 12 0c0-4-4-6-4-10-1 2-3 3-4 0z" fill="url(#flameGrad)" stroke="none" />
    {/* Inner flame */}
    <path d="M12 9c0 2-2 3-2 5a3 3 0 0 0 6 0c0-2-2-3-2-5-.5 1-1.5 1.5-2 0z" fill="url(#flameInnerGrad)" stroke="none" />
    {/* Energy sparks */}
    <circle cx="9" cy="7" r="0.4" fill="currentColor" opacity="0.4" />
    <circle cx="15.5" cy="8" r="0.3" fill="currentColor" opacity="0.3" />
  </svg>
)
,

  GoogleBrand: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {/* Monochrome "G" mark — uses currentColor so it picks up the theme primary */}
      <path fill="currentColor" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.954 4 4 12.954 4 24s8.954 20 20 20 20-8.954 20-20c0-1.341-.138-2.65-.389-3.917z" />
    </svg>
  ),

  XBrand: ({ size = 24, className = "", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {/* Official X glyph; uses currentColor so it adapts to light/dark themes */}
      <path
        fill="currentColor"
        d="M18.244 2H21.5l-7.5 8.572L22.5 22h-6.844l-5.36-7.013L4.16 22H.9l8.06-9.21L.5 2h6.998l4.844 6.41L18.244 2zm-2.4 18h1.82L7.244 4H5.32l10.524 16z"
      />
    </svg>
  )

};

// Styled wrapper with background support
const IconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  /* Background Shapes */
  ${props => props.$bgShape === 'circle' && `border-radius: 50%;`}
  ${props => props.$bgShape === 'rounded' && `border-radius: 12px;`}
  ${props => props.$bgShape === 'square' && `border-radius: 6px;`}
  
  /* Background Sizes (padding) */
  ${props => props.$bgSize === 'xs' && `padding: 4px;`}
  ${props => props.$bgSize === 'sm' && `padding: 6px;`}
  ${props => props.$bgSize === 'md' && `padding: 10px;`}
  ${props => props.$bgSize === 'lg' && `padding: 14px;`}
  ${props => props.$bgSize === 'xl' && `padding: 18px;`}
  
  /* Background Colors */
  ${props => {
    if (!props.$bgColor || props.$bgColor === 'none') return '';
    
    const colors = {
      // Solid colors
      primary: { bg: props.theme?.colors?.primary || '#3B82F6', text: 'white' },
      secondary: { bg: props.theme?.colors?.secondary || '#8B5CF6', text: 'white' },
      success: { bg: props.theme?.colors?.success || '#10B981', text: 'white' },
      warning: { bg: props.theme?.colors?.warning || '#F59E0B', text: 'white' },
      danger: { bg: props.theme?.colors?.danger || '#EF4444', text: 'white' },
      info: { bg: props.theme?.colors?.info || '#0EA5E9', text: 'white' },
      dark: { bg: '#1F2937', text: props.theme?.colors?.primary },
      light: { bg: '#fff', text: props.theme?.colors?.primary },
      
      // Light variants
      'primary-light': { bg: '#DBEAFE', text: '#1D4ED8' },
      'secondary-light': { bg: '#EDE9FE', text: '#7C3AED' },
      'success-light': { bg: '#D1FAE5', text: '#047857' },
      'warning-light': { bg: '#FEF3C7', text: '#92400E' },
      'danger-light': { bg: '#FEE2E2', text: '#B91C1C' },
      'info-light': { bg: '#E0F2FE', text: '#0369A1' },
      
      // Gradients
      'gradient-blue': { bg: 'linear-gradient(135deg, #3B82F6, #1D4ED8)', text: 'white' },
      'gradient-purple': { bg: 'linear-gradient(135deg, #8B5CF6, #7C3AED)', text: 'white' },
      'gradient-pink': { bg: 'linear-gradient(135deg, #EC4899, #BE185D)', text: 'white' },
      'gradient-sunset': { bg: 'linear-gradient(135deg, #F59E0B, #EF4444)', text: 'white' },
      'gradient-ocean': { bg: 'linear-gradient(135deg, #0EA5E9, #10B981)', text: 'white' },
      'gradient-night': { bg: 'linear-gradient(135deg, #1F2937, #374151)', text: 'white' },
      
      // Glass effects
      'glass': { 
        bg: 'rgba(255, 255, 255, 0.2)', 
        text: 'currentColor', 
        extra: 'backdrop-filter: blur(10px); border: 1px solid rgba(255, 255, 255, 0.3);' 
      },
      'glass-dark': { 
        bg: 'rgba(0, 0, 0, 0.2)', 
        text: 'white', 
        extra: 'backdrop-filter: blur(10px); border: 1px solid rgba(255, 255, 255, 0.1);' 
      }
    };
    
    const colorConfig = colors[props.$bgColor];
    if (colorConfig) {
      return `
        background: ${colorConfig.bg};
        color: ${colorConfig.text};
        ${colorConfig.extra || ''}
      `;
    }
    return '';
  }}
  
  /* Shadows */
  ${props => props.$shadow === 'sm' && `box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);`}
  ${props => props.$shadow === 'md' && `box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);`}
  ${props => props.$shadow === 'lg' && `box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1);`}
  ${props => props.$shadow === 'xl' && `box-shadow: 0 20px 25px rgba(0, 0, 0, 0.1);`}
  ${props => props.$shadow === 'colored' && `box-shadow: 0 4px 14px rgba(59, 130, 246, 0.4);`}
  ${props => props.$shadow === true && `box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);`}
  
  /* Hover Effects */
  ${props => props.$hover !== false && `
    &:hover {
      transform: translateY(-1px) scale(1.05);
      filter: brightness(1.1);
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
    }
  `}
  
  /* Animation Classes */
  &.animate-pulse {
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }
  
  &.animate-bounce {
    animation: bounce 1s ease-in-out infinite;
  }
  
  /* Keyframes */
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
  
  @keyframes bounce {
    0%, 20%, 53%, 80%, 100% { transform: translate3d(0, 0, 0); }
    40%, 43% { transform: translate3d(0, -8px, 0); }
    70% { transform: translate3d(0, -4px, 0); }
    90% { transform: translate3d(0, -2px, 0); }
  }
`;

// Enhanced VipIcon with background support
const VipIcon = ({ 
  name, 
  size = 24, 
  className = "",
  variant = "default",
  animated = false,
  
  // Background options
  bgColor = 'none',        // 'primary', 'secondary', 'success', 'gradient-blue', etc.
  bgShape = 'none',        // 'circle', 'rounded', 'square'
  bgSize = 'md',           // 'xs', 'sm', 'md', 'lg', 'xl'
  shadow = false,          // true, 'sm', 'md', 'lg', 'xl', 'colored'
  hover = true,            // enable/disable hover effects
  
  // Theme
  theme,
  
  ...props 
}) => {
  const IconComponent = AppIcons[name];
  
  if (!IconComponent) {
    console.warn(`VIP icon "${name}" not found`);
    return null;
  }

  const baseClassName = `vip-icon vip-icon-${name}`;
  const variantClassName = variant !== "default" ? `variant-${variant}` : "";
  const animatedClassName = animated ? "animate-pulse" : "";
  
  const combinedClassName = [
    baseClassName,
    variantClassName,
    animatedClassName,
    className
  ].filter(Boolean).join(" ");

  // If no background is needed, return simple icon
  if (bgColor === 'none' && bgShape === 'none') {
    return (
      <IconComponent
        size={size}
        className={combinedClassName}
        {...props}
      />
    );
  }

  // Return icon with background wrapper
  return (
    <IconWrapper
      $bgColor={bgColor}
      $bgShape={bgShape}
      $bgSize={bgSize}
      $shadow={shadow}
      $hover={hover}
      theme={theme}
      className={combinedClassName}
      {...props}
    >
      <IconComponent size={size} />
    </IconWrapper>
  );
};

// Attach individual icons to VipIcon for dot notation access
Object.keys(AppIcons).forEach(iconName => {
  VipIcon[iconName] = AppIcons[iconName];
});

export default VipIcon;